import { z } from 'zod';
import { archiveFilterSchema, baseListQuery, dateOnlySchema, uuidSchema } from './common';
import { contactNameSchema, contactPhoneSchema } from './common';
import {
  actsForCounterparty,
  can,
  hasGrantCode,
  type AccessSubject,
  type Permission,
} from './permissions';
import {
  OFFICE_EQUIPMENT_EXECUTOR_GRANT,
  OFFICE_EQUIPMENT_IT_GRANT,
  OFFICE_EQUIPMENT_OPERATOR_GRANT,
  OFFICE_EQUIPMENT_REQUESTER_GRANT,
} from './office-equipment-profiles';
import { roleScopeAxis } from './grant-scope';
// Только тип: список ролей-заявителей (ADR 0181) обязан состоять из ЗНАЧЕНИЙ словаря ролей, и
// `satisfies readonly Role[]` роняет сборку в тот день, когда роль-заплатку `manager` уберут.
import type { Role } from './enums';
// Значением, а не типом: сквозную область модуля читает предикат правки заявки (Н8) — тем же
// `hasModuleWideScope`, каким её читают область видимости на сервере и подбор заказчика на портале.
// Обратной зависимости у наборов на заявки нет, поэтому цикла модулей не заводится.
import { hasModuleWideScope } from './grants';
// Третий способ назвать предмет заявки (план кандидатов, Р2): схема сообщения об отсутствующем
// аппарате встраивается в заведение заявки полем `equipmentCandidate`. Отдельной ручки у кандидата
// нет вовсе, поэтому и схема у него одна — общая с заведением, а не своя копия шести реквизитов.
import { equipmentCandidateInputSchema } from './office-equipment-candidates';
// Только тип: перечень состояний кандидата объявлен там же, где сам кандидат, и переписанный сюда
// союз из четырёх слов разошёлся бы с ним молча — ровно как разошлась бы третья копия `CHECK`.
import type { OfficeEquipmentCandidateStatus } from './office-equipment-candidates';
// Type-only: `office-equipment` imports this module at runtime, so a value import would be a cycle.
import type { OfficeEquipmentState } from './office-equipment';
import type { ModuleMailOutcome } from './module-mail';
import type { RequestChangeDto } from './request-history';

// ── Заявки на обслуживание оргтехники (ADR 0085) ──
// Цикл длиннее, чем у вывоза мусора и заказа техники: между «приняли» и «сделали» стоит смета,
// которую подписывают ИТ-служба и «Ведение», а после работ — приёмка. Здесь живут вид заявки,
// статусы, коридоры переходов и схемы всех ручек модуля; кто и что может — в permissions.ts,
// область — в lib/access.ts на сервере.
//
// Выпуск 1 плана `office-equipment-requests-rework-plan.md` свёл оба вида заявок к одному
// словарю статусов (Н2) и увёл визу ИТ со входа на смету (Н3). Два значения статуса —
// `it_approved` и `diagnostics` — МЁРТВЫЕ значения: заявок в этих статусах больше не существует,
// их запретил `CHECK` выпуска 2 (`0197`), а остаток перевела та же миграция. В типе они остались по
// двум причинам, и обе внешние: `ALTER TYPE … DROP VALUE` в Postgres нет вовсе, а история переходов
// их ХРАНИТ — строка «Новая → Согласована ИТ» от 12.08 правдива, и портал обязан её подписать.
// Поэтому подписи и цвета у них есть, а ходов нет: в каждом коридоре ниже стоит пустой список, и
// это не пропуск, а утверждение «из этого статуса не ходят, потому что в нём не бывают».
//
// Выпуск ADR 0145 (`office-equipment-cycle-simplification-plan.md`) отправил тем же путём ещё два
// значения — `assigned` и `estimate_review`. Оба означали не состояние заявки, а ожидание нажатия
// кнопки: «Назначена» — что распределивший уже нажал, а исполнитель ещё нет; «Смета на
// согласовании» — что счёт предъявлен, а ответа на него нет. Оба ожидания видны по самой строке
// заявки, и вместо статусов их называют два признака (Р2): `serviceHasExecutors` и
// `serviceEstimatePending`. Назначение, отказ, предъявление, возврат в правку и согласование
// перестали быть переходами вовсе — доступность каждого спрашивают предикаты Р11, а не коридор.

// ── Вид заявки ──

/**
 * Ремонт и расходники — один модуль и один цикл (Н1): та же техника, та же область видимости,
 * та же история, те же письма. Вид отвечает за две вещи — какие дуги доступны (заход в смету
 * только у ремонта) и что показывает форма. Вторым модулем это стоило бы второго списка,
 * второй истории и второго набора прав ради одной таблицы строк.
 *
 * Схема базы разрешает `consumable` с выпуска 1, а API — нет (план §7.3): до выпуска 3 у
 * такой заявки нет ни строк номенклатуры, ни формы, ни списания, и заведённая раньше времени
 * она была бы заявкой без предмета.
 */
export const SERVICE_REQUEST_KINDS = ['repair', 'consumable'] as const;
export const serviceRequestKindSchema = z.enum(SERVICE_REQUEST_KINDS);
export type ServiceRequestKind = (typeof SERVICE_REQUEST_KINDS)[number];

/**
 * Kind labels for people. `repair` reads «Обслуживание» (R1, request 7): the customer asked for a
 * wider word, since a request of this kind is not always about a fault — it also asks to set up,
 * connect or refill, and a narrow «Ремонт» discouraged filing it at all.
 *
 * The value `repair` itself changes nowhere — not in the database, the contract, the indexes or a
 * `CHECK`: renaming the value would take a data migration for a word on screen, and only two
 * places read the word: the «Чем помочь» switch in the form and the kind tag on the card.
 *
 * The word «ремонт» in warranties (`warrantyClaimSourceLabels.item`, `warrantyBearerLabels.repair`)
 * is kept on purpose: there it names work already done, not the request kind.
 */
export const serviceRequestKindLabels: Record<ServiceRequestKind, string> = {
  repair: 'Обслуживание',
  consumable: 'Расходники',
};

// ── Статусы ──

export const SERVICE_REQUEST_STATUSES = [
  'new',
  'it_approved',
  'assigned',
  'diagnostics',
  'estimate_review',
  'in_work',
  // Заморозка стоит среди рабочих статусов, а не последней: список сортируется порядком значений
  // enum в БД (миграция добавляет значение `AFTER 'in_work'`), и порядок этого массива обязан ему
  // совпадать — иначе сортировка по статусу в портале и на сервере разъедутся.
  'on_hold',
  'done',
  'accepted',
  'cancelled',
] as const;
export const serviceRequestStatusSchema = z.enum(SERVICE_REQUEST_STATUSES);
export type ServiceRequestStatus = (typeof SERVICE_REQUEST_STATUSES)[number];

export const serviceRequestStatusLabels: Record<ServiceRequestStatus, string> = {
  new: 'Новая',
  // Мёртвый статус (`0197`): заявок в нём нет, но подпись нужна ИСТОРИИ — строка «Новая →
  // Согласована ИТ» от 12.08 правдива, и портал обязан её назвать. Прежней она оставлена по той же
  // причине: переименуй мы её в «Новая», два разных состояния слились бы в ленте истории.
  it_approved: 'Согласована ИТ',
  // Мёртвый статус (`0224`, Р1): назначение перестало быть переходом, и заявок в нём больше не
  // бывает — а подпись нужна ИСТОРИИ, где строка «Новая → Назначена» от 20.08 правдива.
  // Переименуй мы её в «Новая» — два разных состояния слились бы в ленте. Имя прежнее и не «Назначен
  // сервис» (Н2): исполнителем бывает и свой сотрудник.
  assigned: 'Назначена',
  diagnostics: 'Диагностика', // мёртвый статус (0197): подпись нужна истории
  // Мёртвый статус (`0224`, Р1): ожидание подписи держит теперь признак `serviceEstimatePending`, а
  // не статус. Подпись оставлена по той же причине — истории.
  estimate_review: 'Смета на согласовании',
  in_work: 'В работе',
  // Заморозка — состояние, а не флаг рядом со статусом (Р103): «В работе, но не в работе»
  // пришлось бы читать вместе с каждым коридором.
  on_hold: 'Отложена',
  // «Решена», а не «Ожидает приёмки» (Н2, постановка заказчика): словарь один на оба вида заявок, и
  // подпись обязана одинаково читаться у ремонта и у привезённого картриджа.
  done: 'Решена',
  accepted: 'Закрыта',
  cancelled: 'Отменена',
};

export const serviceRequestStatusColors: Record<ServiceRequestStatus, string> = {
  new: 'blue',
  it_approved: 'purple',
  assigned: 'cyan', // мёртвый статус (0224): цвет нужен ленте истории
  diagnostics: 'geekblue',
  estimate_review: 'gold', // мёртвый статус (0224): цвет нужен ленте истории
  in_work: 'orange',
  // Серый: заявка не движется, и в списке она не должна спорить цветом с теми, за которые взялись.
  on_hold: 'default',
  done: 'lime',
  accepted: 'green',
  cancelled: 'red',
};

/**
 * Что за шаг ждут в этом статусе (Р101). Строка нижним регистром и без лица: портал приписывает
 * лицо сам — «Вам: согласовать смету» тому, за кем ход, и «ждёт оператора» остальным. Словарь один
 * на все стороны, потому что два («что делать сервису» и «кого ждут») разъезжаются на первом же
 * новом статусе: так и вышло с заморозкой — прежний `serviceTodoLabel` знал три статуса из девяти.
 *
 * У статусов без хода — отложенной, закрытой и отменённой — подписи нет: ждать в них нечего.
 *
 * После слияния статусов (Р2) словарь отвечает не на всё, и это осознанная граница: в «Новой» ждут
 * то распределения, то «примите в работу», а в «В работе» — то подписи под объёмом работ, то самих
 * работ. Развилку держит `serviceStepLabelFor` стороной ожидания; здесь остаётся ответ по статусу —
 * тот, которым подписывают ЧУЖОЙ ход, где сторона уже названа словом.
 */
export const serviceStepLabels: Record<ServiceRequestStatus, string> = {
  new: 'назначить исполнителей',
  it_approved: 'назначить исполнителей', // мёртвый статус (0197)
  assigned: 'принять в работу', // мёртвый статус (0224): подпись нужна истории
  diagnostics: 'выполнить и закрыть работы', // мёртвый статус (0197)
  // Мёртвый статус (0224). Слово переименовано вместе с разделом (Р17): «Смета» стала «Объёмом
  // работ», и подпись шага, оставленная прежней, звала бы искать между ними разницу, которой нет.
  estimate_review: 'согласовать объём работ',
  in_work: 'выполнить и закрыть работы',
  on_hold: '',
  done: 'принять работу',
  accepted: '',
  cancelled: '',
};

/**
 * Шаг, которого ждут **от конкретной стороны** в этом статусе. Нужен там, где одного статуса
 * недостаточно, — а после слияния (Р2) таких статусов ровно два: «Новая» без исполнителей ждёт
 * распределения, а с исполнителями — что за неё возьмутся; «В работе» с непогашенным предъявлением
 * ждёт подписи под объёмом работ, без него — самих работ.
 *
 * Признаки Р2 сюда не передаются, и это не срезание угла: сторону ожидания считает по ним же
 * `serviceRequestWaitingOn`, а второй разбор тех же двух колонок здесь разошёлся бы с очередью
 * МОЛЧА — подпись звала бы к одному шагу, а бейдж «ждут меня» отбирал бы заявки по другому
 * правилу. Поэтому аргумента два, и второй — уже посчитанный ответ очереди.
 *
 * Словарь `serviceStepLabels` остаётся: он отвечает на вопрос «что за шаг ждут в этом статусе»
 * вообще — им подписывают чужой ход («Ждёт исполнителя»), где сторона уже названа. Эта функция
 * отвечает на «что делать **мне**».
 */
export function serviceStepLabelFor(
  status: ServiceRequestStatus,
  waiting: ServiceWaitingOn,
): string {
  // «Новая», за которую уже кто-то отвечает: распределять нечего — ход за исполнителем (Р6).
  if (status === 'new' && waiting === 'service') return 'принять в работу';
  // «В работе» с висящим предъявлением: ждут подписи, а не работ (Р3).
  if (status === 'in_work' && waiting === 'approval') return 'согласовать объём работ';
  return serviceStepLabels[status];
}

/**
 * Закрытая заявка: ни ход, ни правка ей больше не положены. Отложенная закрытой не считается
 * (Р109): движение остановлено, но заявка жива — техника ждёт этого же ремонта, и вторую на ту же
 * единицу заводить нельзя.
 */
export function isServiceRequestClosed(status: ServiceRequestStatus): boolean {
  return status === 'accepted' || status === 'cancelled';
}

// ── Коридоры переходов ──
// Их шесть, а не один общий, и разведены они не по ролям, а по трём основаниям матрицы (план §6):
// что субъекту позволено правом, назначен ли он на эту заявку и из какого статуса ход. Общая
// таблица склеила бы первые два: право `serviceRequests.status` есть и у «Ведения», и у оператора
// сервисной компании — по ней подрядчик принимал бы собственную работу, а «Ведение» закрывало бы
// работы, которых не делало. Портал такие кнопки не рисует, но портал и не защита: отказывает
// сервер, и обе стороны спрашивают одну функцию.

/**
 * Ход исполнителя: то, что делает сам взявшийся за заявку. Открывает его **факт назначения**, а не
 * право (план §6, п. 2), — поэтому свой сисадмин и подрядчик ходят одними и теми же ручками.
 *
 * Ходов осталось два. Отказ, предъявление объёма работ и возврат его в правку переходами быть
 * перестали (Р7, Р8, Р9): они меняют состав исполнителей и колонку предъявления, а статуса не
 * трогают. Доступность каждого спрашивают предикаты Р11 — коридор о них не знает вовсе, и искать
 * их здесь бесполезно.
 */
export const SERVICE_EXECUTOR_TRANSITIONS: Record<ServiceRequestStatus, ServiceRequestStatus[]> = {
  // «Принять в работу» из «Новой» (Р6): промежуточной «Назначенной» между заведением и работой
  // больше нет. Открывает ход всё тот же факт назначения, и он же закрывает его у нераспределённой
  // заявки: у «Новой» без исполнителей назначенных нет, и `isServiceExecutor` ложен при любом праве.
  new: ['in_work'], // принять в работу
  it_approved: [], // мёртвый статус (0197): заявок в нём не бывает
  assigned: [], // мёртвый статус (0224): назначение больше не переход (Р5)
  diagnostics: [], // мёртвый статус (0197): заявок в нём не бывает
  estimate_review: [], // мёртвый статус (0224): предъявление больше не переход (Р8)
  // Предъявление объёма работ ушло отсюда вместе со статусом (Р8): оно поднимает ревизию и ставит
  // `estimate_pending_revision`, оставляя заявку в «В работе», — ровно как просил заказчик. Подъём
  // ревизии при этом никуда не делся: на нём по-прежнему держится обесценивание подписи.
  in_work: ['done'], // закрыть работы
  // Заморозку исполнитель не двигает (Р105): о задержке он сообщает примечанием, а откладывает и
  // возобновляет тот, кто ведёт заявку, — иначе «ждём запчасть» становилось бы решением подрядчика.
  on_hold: [],
  done: [],
  accepted: [],
  cancelled: [],
};

/*
 * КОРИДОРА ВИЗЫ ИТ ЗДЕСЬ БОЛЬШЕ НЕТ. Таблица `SERVICE_IT_TRANSITIONS` стояла пустой ЦЕЛИКОМ с
 * упразднения визы (Р10, ответ В2): ручку `PATCH /:id/it-approval` сняли, вопрос «чинить за эти
 * деньги или менять аппарат» задаёт себе тот же человек, что смотрит на объём работ, — и держали
 * её только ради права `serviceRequests.approveIt`, которое лежало в наборе ИТ-службы и говорило
 * «право есть, ходов по нему нет». Право из набора убрано (план профилей оргтехники, Э9, миграция
 * E), и пустая таблица вместе с её чтением ниже ушла тем же выпуском: перечень, ни разу не
 * отвечающий «да», читается как заготовка, которой не существует.
 *
 * Из словаря прав `approveIt` не снято — там оно живёт своей жизнью (матрица администратора,
 * `PERMISSION_REQUIRES`), — но коридора у него нет ни одного, и вернуть его можно только новой
 * таблицей и новой веткой, то есть осознанно.
 */

/**
 * Распределение (право `serviceRequests.assign`): кто делает заявку. Своей таблицей, а не строкой в
 * операторской, потому что назначают двое — «Ведение» и ИТ-служба, — а права хода
 * (`serviceRequests.status`) у второй нет и быть не должно (§7.2): слитые в одно, назначение
 * пришлось бы выдавать вместе с приёмкой работы, отменой и согласованием сметы.
 */
export const SERVICE_ASSIGNER_TRANSITIONS: Record<ServiceRequestStatus, ServiceRequestStatus[]> = {
  // Назначение статуса не меняет (Р5): состав исполнителей пишет `PUT /:id/executors`, строка
  // истории кладётся `from = to`, а видно ли действие — отвечает `canAssignServiceExecutors`.
  new: [],
  it_approved: [], // мёртвый статус (0197)
  assigned: [], // мёртвый статус (0224)
  diagnostics: [], // мёртвый статус (0197)
  estimate_review: [], // мёртвый статус (0224)
  /*
   * РУЧНОЙ ВОЗВРАТ РАБОТАЮЩЕЙ ЗАЯВКИ В «НОВУЮ» — и больше ничего (ADR 0187).
   *
   * Дуга стояла здесь ради ПЕРЕназначения: оно откатывало заявку из «В работе», чтобы новый
   * исполнитель нажал «Принять в работу» сам. Отката больше нет — назначение статуса не меняет ни
   * при каком исходе, — но дугу это не освобождает: её единственным ходом остаётся пункт «Вернуть
   * в «Новую»» (`PATCH /:id/status`, откат своего же «принял в работу», Р13).
   *
   * УБРАТЬ ЕЁ ЗАОДНО С ОТКАТОМ БЫЛО НЕЛЬЗЯ, и это проверено по составу наборов. У «Ведения»
   * оргтехники (`office_equipment_operator`) есть `serviceRequests.assign` и
   * `serviceRequests.status`, а `requests.rollbackStatus` нет — через `SERVICE_ADMIN_ROLLBACKS`
   * дуга к нему не придёт вовсе. Сними мы строку, ручной возврат пропал бы у тех, кто заявки
   * ведёт, — молча и без единой просьбы об этом.
   *
   * Запрет переназначения под висящим предъявлением живёт в предикате `canAssignServiceExecutors`
   * (Р11), а не здесь: назначение таблицу переходов больше не спрашивает вовсе.
   */
  in_work: ['new'],
  on_hold: [],
  done: [],
  accepted: [],
  cancelled: [],
};

/**
 * Заморозка (§6.2): из любого рабочего статуса и ничего из закрытых. «Решена» в перечне нет
 * намеренно — работа предъявлена, ход за приёмкой, и откладывать там нечего: заявка и так стоит,
 * пока её не примут.
 *
 * Отдельной таблицей, потому что держателей двое — «Ведение» и ИТ-служба, — и приходит заморозка
 * своим правом `serviceRequests.hold`: дай мы её через `serviceRequests.status`, ИТ-служба
 * получила бы вместе с ней весь операторский коридор (§7.1).
 */
export const SERVICE_HOLD_TRANSITIONS: Record<ServiceRequestStatus, ServiceRequestStatus[]> = {
  new: ['on_hold'],
  it_approved: [], // мёртвый статус (0197)
  assigned: [], // мёртвый статус (0224): откладывать нечего — заявок в нём не бывает
  diagnostics: [], // мёртвый статус (0197)
  estimate_review: [], // мёртвый статус (0224)
  // Отложить заявку, ждущую подписи, по-прежнему можно: это та же «В работе» с непогашенным
  // предъявлением, и заморозка её не различает — ход останавливается целиком.
  in_work: ['on_hold'],
  // В себя заморозка не вкладывается: вторая причина поверх первой потеряла бы `held_from_status`,
  // и заявке некуда стало бы возвращаться.
  on_hold: [],
  /*
   * «Решена» откладывается, и это не недосмотр (правка после В3). Р106 ADR 0125 разрешил заморозку
   * этого статуса прямо — «ждём акт от сервиса», — и закрыть её значило бы сузить сегодняшнее
   * поведение без просьбы заказчика.
   *
   * Второй довод появился вместе с автозакрытием: отложенная заявка стоит в `on_hold`, а отбор
   * берёт только `done`, — заморозка и есть единственный ручной способ снять заявку с очереди на
   * автоматическое закрытие, пока идёт разбирательство.
   */
  done: ['on_hold'],
  accepted: [],
  cancelled: [],
};

/**
 * Cancel (the right `serviceRequests.cancel`, workflow-fixes plan Р2): exactly the CANCELLING arcs of
 * the operator corridor and nothing beyond them. A table of its own and not a row of the operator
 * one, for the reason the hold has: the right is held by the IT service, which has no
 * `serviceRequests.status` and must not (§7.2) — merged, they would hand out the acceptance of the
 * work, the return for rework and (paired with `requests.rollbackStatus`) the administrative
 * rollbacks together with the cancel.
 *
 * There is no arc from «Решена», «Принята» and «Отменена» on purpose: acceptance, return and
 * «вернуть в работу» belong to «Ведение», and cancelling accepted work is not a cancel but a
 * rollback.
 */
export const SERVICE_CANCEL_TRANSITIONS: Record<ServiceRequestStatus, ServiceRequestStatus[]> = {
  new: ['cancelled'],
  it_approved: [], // мёртвый статус (0197)
  assigned: [], // мёртвый статус (0224)
  diagnostics: [], // мёртвый статус (0197)
  estimate_review: [], // мёртвый статус (0224)
  in_work: ['cancelled'],
  on_hold: ['cancelled'],
  done: [],
  accepted: [],
  cancelled: [],
};

/**
 * «Ведение» (право `serviceRequests.status`): решения заказчика — приёмка, возврат на доработку и
 * отмена. Ни одного шага исполнителя здесь нет и быть не должно.
 *
 * Согласования объёма работ в таблице нет вовсе (Р8), и это не пропуск: «согласовано» статуса не
 * меняет — заявка остаётся в «В работе», ровно как просил заказчик. А «не согласовано» — это отмена
 * с причиной и решением (В1, Р12), то есть та же дуга `in_work → cancelled`, которой отменяют по
 * любому другому основанию. Различает исходы тело ручки (`approveServiceEstimateSchema`), а
 * доступность самого действия — `canApproveServiceEstimate` (Р11).
 */
export const SERVICE_OPERATOR_TRANSITIONS: Record<ServiceRequestStatus, ServiceRequestStatus[]> = {
  // Отменить «Новую» можно: заявку, которую отзывает сам заказчик, незачем гонять по циклу. Сам
  // заказчик статусов не двигает (§6.3) — он просит отменить, а ход делает «Ведение».
  new: ['cancelled'],
  it_approved: [], // мёртвый статус (0197)
  assigned: [], // мёртвый статус (0224)
  diagnostics: [], // мёртвый статус (0197)
  estimate_review: [], // мёртвый статус (0224)
  in_work: ['cancelled'], // отмена — она же исход «не согласовано» (Р8)
  on_hold: ['cancelled'],
  done: ['accepted', 'in_work'], // принять · вернуть на доработку
  accepted: [],
  cancelled: [],
};

/**
 * Откаты администратора. Дуги `in_work → estimate_review` здесь намеренно нет: объём работ меняют
 * повторным предъявлением из «В работе» с подъёмом ревизии, и второй путь назад сделал бы этот
 * подъём необязательным — а на нём держится обесценивание подписи (Н3, Р9).
 *
 * Откат назначения снят вместе со статусом (Р13): назначение больше не переход, и отматывать
 * ему нечего — состав исполнителей меняют самим назначением.
 */
export const SERVICE_ADMIN_ROLLBACKS: Record<ServiceRequestStatus, ServiceRequestStatus[]> = {
  new: [],
  it_approved: [], // мёртвый статус (0197)
  assigned: [], // мёртвый статус (0224): откат назначения снят вместе с ним (Р13)
  diagnostics: [], // мёртвый статус (0197)
  // Предъявление объёма работ отматывает не откат, а возврат в правку (`estimate/reopen`, Р9): два
  // способа снять одно предъявление разошлись бы на первой же правке.
  estimate_review: [], // мёртвый статус (0224)
  // Откат «принял в работу» ведёт в «Новую»: промежуточной «Назначенной» больше нет, и заявка
  // возвращается к назначенным, не теряя их, — исполнителя снимает не этот откат, а назначение.
  in_work: ['new'],
  // Из заморозки не откатывают (Р110): цель пришлось бы считать от `held_from_status` вторым путём
  // рядом с возвратом, и два способа выйти в тот же статус разошлись бы на первой же правке. И
  // откатов **в** `on_hold` нет ни у одного статуса: заморозка — решение оператора, а не ошибка
  // хода, которую администратор отматывает.
  on_hold: [],
  done: ['in_work'],
  accepted: ['done'],
  cancelled: ['new'],
};

// ── Ход исполнителя: назначение вместо права ──

/**
 * Признаки назначения — то, чего матрица прав не знает и знать не может: они читаются из самой
 * заявки. Предикат принимает их **готовыми**, а не ходит за ними сам, потому что спрашивают его
 * двое — сервер по строке заявки и портал по карточке, — и «сходить в базу» умеет только первый.
 */
export interface ServiceExecutorAssignment {
  /** Заявка назначена контрагенту, оператором которого работает субъект (Н5). */
  actsForAssignedCounterparty: boolean;
  /** Субъект значится в `service_request_executors` этой заявки (Н5). */
  isNamedExecutor: boolean;
}

/** Заявка, про которую признаков не спрашивали: ни компании субъекта, ни его строки в ней нет. */
const NOT_ASSIGNED: ServiceExecutorAssignment = {
  actsForAssignedCounterparty: false,
  isNamedExecutor: false,
};

/**
 * Is the subject the executor on this request — a disjunction of two facts (plan §7.1), written
 * once for every executor action: status moves, the estimate, closing the work and editing what was
 * issued.
 *
 * A service company is assigned as a whole, so it needs no permission: it has no named rows, and
 * demanding `serviceRequests.execute` from a contractor's account would either take today's moves
 * away from him or add a permission for a check that never holds for him under any assignment. For
 * an own employee the assignment works **in pair** with the permission — revoking the grant of a
 * transferred system administrator closes his moves without touching the assignment history.
 *
 * `has` answers the permission. The server leaves the default, the matrix over its own principal;
 * the portal passes its `can` over the list the server computed (ADR 0223).
 */
export function isServiceExecutor(
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  if (assignment.actsForAssignedCounterparty && actsForCounterparty(subject, 'service')) {
    return true;
  }
  return assignment.isNamedExecutor && has('serviceRequests.execute');
}

/**
 * Что субъекту доступно из текущего статуса. Одна функция на сервер и портал: разойдись они —
 * кнопка вела бы в 403 либо действие оставалось бы недоступным при разрешающем сервере.
 *
 * Признаки назначения необязательны, и это не послабление: до волн В3/В6 их не передаёт ни один
 * зовущий, а ответ «ходов нет» отобрал бы у подрядчика весь цикл в тот же день, когда контракты
 * уехали, а маршруты — ещё нет. Поэтому без признаков сторона исполнителя считается по
 * сегодняшнему правилу: оператор контрагента-сервиса. Заявок чужого контрагента он не видит вовсе,
 * и на вопрос «назначена ли она моей компании» за него уже ответила область видимости.
 *
 * Администратор получает **объединение** коридоров: он разбирает ошибки и доводит заявку за любую
 * сторону. Ход исполнителя открывает ему право сметы, а не назначение, — вне контрагента-сервиса
 * оно есть только у него, и без этой ветки зависшую заявку было бы нечем довести.
 */
export function allowedServiceStatusTransitions(
  from: ServiceRequestStatus,
  subject: AccessSubject | null | undefined,
  assignment?: ServiceExecutorAssignment | null,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): ServiceRequestStatus[] {
  if (!subject) return [];
  // Сторона подрядчика отвечает **вместо** остальных, а не вместе с ними: право
  // `serviceRequests.status` у оператора сервисной компании есть, и по сумме коридоров он принял
  // бы собственную работу и отменил бы заявку заказчика.
  if (actsForCounterparty(subject, 'service')) {
    const assigned = assignment?.actsForAssignedCounterparty ?? true;
    return assigned ? [...SERVICE_EXECUTOR_TRANSITIONS[from]] : [];
  }
  const allowed = new Set<ServiceRequestStatus>();
  if (
    isServiceExecutor(subject, assignment ?? NOT_ASSIGNED, has) ||
    has('serviceRequests.estimate')
  ) {
    for (const to of SERVICE_EXECUTOR_TRANSITIONS[from]) allowed.add(to);
  }
  if (has('serviceRequests.assign')) {
    for (const to of SERVICE_ASSIGNER_TRANSITIONS[from]) allowed.add(to);
  }
  if (canHoldService(subject, has)) {
    for (const to of SERVICE_HOLD_TRANSITIONS[from]) allowed.add(to);
  }
  // Cancel is a right of its own (workflow-fixes plan Р2). It stands after the hold and before the
  // operator corridor because it is the same «narrow door»: the holder of `cancel` alone gets the
  // cancelling arcs only, the holder of `status` gets the same and the acceptance besides (the
  // `Set` makes the overlap harmless). The contractor never reaches the branch: his side answers
  // above, instead of the others.
  if (has('serviceRequests.cancel')) {
    for (const to of SERVICE_CANCEL_TRANSITIONS[from]) allowed.add(to);
  }
  if (has('serviceRequests.status')) {
    for (const to of SERVICE_OPERATOR_TRANSITIONS[from]) allowed.add(to);
  }
  // Откаты спрашиваются парой прав, а не одним `requests.rollbackStatus`: оно есть и у диспетчера,
  // который заявок оргтехники не ведёт вовсе, — а «отмотать статус назад» это всё-таки ход по
  // модулю, и без права хода в нём он означал бы стороннее вмешательство в чужой цикл.
  if (has('requests.rollbackStatus') && has('serviceRequests.status')) {
    for (const to of SERVICE_ADMIN_ROLLBACKS[from]) allowed.add(to);
  }
  return [...allowed];
}

export function canTransitionServiceStatus(
  from: ServiceRequestStatus,
  to: ServiceRequestStatus,
  subject: AccessSubject | null | undefined,
  assignment?: ServiceExecutorAssignment | null,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  return allowedServiceStatusTransitions(from, subject, assignment, has).includes(to);
}

/**
 * The right the status door (`PATCH /:id/status`) asks for a move INTO the given status
 * (workflow-fixes plan Р2): `serviceRequests.status` for any target, and for «Отменена» the cancel
 * right of its own as well.
 *
 * WHY THIS IS NOT LEFT TO THE CORRIDOR. The door used to be guarded by `status` alone, and that
 * guard was part of the rule, not decoration: the corridor gives the IT service one more arc through
 * this door besides the cancel — `in_work → new` ("Вернуть в «Новую»") comes to it from the right to
 * assign (`SERVICE_ASSIGNER_TRANSITIONS`), and the owner asked in the same walkthrough (item 6) to
 * take that item away from the IT account. Letting the cancel right through the guard without this
 * question would have given the arc back by the side door. One function, asked by the route; the
 * corridor still decides WHICH arcs exist — there is no second map of rules.
 */
export function holdsServiceStatusRight(
  subject: AccessSubject | null | undefined,
  to: ServiceRequestStatus,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  if (has('serviceRequests.status')) return true;
  return to === 'cancelled' && has('serviceRequests.cancel');
}

/**
 * Кто держит и отпускает заявку. Право `serviceRequests.hold` — своё (§7.1), но `status` остаётся
 * вторым источником: заморозка сегодня приходит вместе с ним, и учётка, у которой набор ещё не
 * выдан, а надстройка ADR 0086 жива, иначе потеряла бы её в день выката. Правило выпуска 1 ничего
 * не запрещает из того, что делает старый код (план §3, п. 1).
 *
 * Исполнителю заморозка закрыта при любом праве (Р105): о задержке он сообщает примечанием, а
 * держит и отпускает заявку тот, кто её ведёт, — иначе «ждём запчасть» становилось бы решением
 * подрядчика.
 */
export function canHoldService(
  subject: AccessSubject | null | undefined,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  if (actsForCounterparty(subject, 'service')) return false;
  return has('serviceRequests.hold') || has('serviceRequests.status');
}

/**
 * Вправе ли субъект вернуть отложенную заявку в работу. Отдельным именем, а не строкой в коридоре:
 * цель возврата динамическая (`held_from_status`), и таблицей `Record<status, status[]>` она не
 * выражается — а спрашивают это и портал (показать пункт меню), и сервер (ручка `/resume`).
 *
 * Условие то же, что у заморозки, и считается той же функцией: заморозка, из которой возвращает не
 * тот, кто её ставил, означала бы заявку, отпущенную мимо человека, знающего причину.
 */
export function canResumeService(
  subject: AccessSubject | null | undefined,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  return canHoldService(subject, has);
}

/**
 * Куда вернётся отложенная заявка; `null` — она не отложена, и возвращать нечего. Дуга назад одна
 * (Р104): разреши мы выбирать целевой статус, «Отложена» стала бы вторым входом в цикл — в обход
 * виз, сметы и назначения.
 */
export function serviceResumeTarget(row: {
  status: ServiceRequestStatus;
  heldFromStatus: ServiceRequestStatus | null;
}): ServiceRequestStatus | null {
  return row.status === 'on_hold' ? row.heldFromStatus : null;
}

/**
 * Переход, отменяющий чужую работу, требует объяснения: без него в истории останется пара строк, по
 * которой не понять, ошиблись исполнителем, отказался он сам или смета оказалась вдвое дороже.
 *
 * Отказа по объёму работ в перечне отдельной строкой нет, и это не пропуск: он уводит заявку в
 * «Отменена» (Р8), а её причину требует самая первая ветка — вместе со всякой другой отменой. Пара
 * «откуда → куда» исход согласования не различает вовсе, поэтому и причину, и решение спрашивает
 * тело ручки (`approveServiceEstimateSchema`) — там, где известен сам исход.
 */
export function serviceStatusChangeRequiresReason(
  from: ServiceRequestStatus,
  to: ServiceRequestStatus,
): boolean {
  if (to === 'cancelled') return true;
  // Заморозка (Р107): даты «отложена до» у неё нет, и когда ждать — говорит только причина. Без
  // неё в списке стоит «Отложена · 12 дней», по которым не понять, ждут запчасть или решение.
  if (to === 'on_hold') return true;
  /*
   * Пары `assigned → new` здесь больше нет, и заменять её на `in_work → new` НЕ НАДО.
   *
   * Прежняя пара требовала объяснения потому, что снимала исполнителя: без причины в истории
   * оставались две строки, по которым не понять, ошиблись исполнителем или он отказался сам. Оба
   * её звавших ушли — отказ переходом быть перестал (Р7) и спрашивает причину телом ручки
   * `decline`, а откат назначения снят вместе со статусом (Р13).
   *
   * По дуге `in_work → new` ходят двое, и требовать причину не за что ни у одного: откат «принял в
   * работу» исполнителя не трогает (`baseServiceReset`), а прежде — как `in_work → assigned` —
   * причины не требовал вовсе; переназначение же спрашивает её само, у себя в ручке, и там она
   * обязательна ровно тогда, когда исполнители у заявки уже были. Требуй мы её и здесь, откат
   * молча стал бы строже, чем был, без единой просьбы об этом.
   */
  if (from === 'done' && to === 'in_work') return true; // возврат на доработку
  return false;
}

/**
 * Что заявка теряет, возвращаясь назад. Заявка после возврата не должна выглядеть так, будто её и
 * не двигали: снимок согласования под чужой ревизией сметы или факт закрытия у заявки «в работе»
 * означали бы решение, которого никто не принимал.
 *
 * Гарантия при возврате снимается целиком — и посчитанная, и введённая руками. Причина в том, что
 * возврат отменяет само выполнение: гарантия не начиналась, отсчитывать её не от чего, а строка без
 * выполнения гарантии и не держит (CHECK в БД). Талон при этом никуда не делся — дату введут заново
 * при повторном закрытии, той же ручкой; `warranty_until_manual` отличает её от посчитанной, чтобы
 * повторное закрытие не перетёрло дату из талона своим расчётом.
 */
export interface ServiceTransitionReset {
  /** Снять визу отдела ИТ (кто и когда). */
  itApproval: boolean;
  /** Снять назначенного исполнителя — и контрагента, и поимённые строки. */
  executor: boolean;
  /**
   * Стереть смету целиком: строки, ревизию, дату и снимок суммы предъявления. Непогашенное
   * предъявление (`estimate_pending_revision`) гасится вместе с ними, и не только ради очереди
   * (Р2): ревизия уходит в `0`, а оставленная pending-ревизия уронила бы саму запись — `CHECK`
   * требует их равенства.
   */
  estimate: boolean;
  /** Стереть снимок согласования (кто, когда, какая ревизия). */
  approval: boolean;
  /** Стереть факт закрытия: дату, итог, корректировку и отметки выполнения по строкам. */
  completion: boolean;
  /** Стереть снимок приёмки. */
  acceptance: boolean;
  /**
   * Снять обе пометки отказа: рекомендацию замены аппарата (М5 плана) и решение, принятое вместо
   * ремонта (`rejection_resolution`, Р12). Одним флагом, а не двумя, потому что живут и умирают они
   * всегда вместе: обе объясняют, почему заявку закрыли без ремонта, обе относятся к отмене, — и
   * возврат отменённой в «Новую» обязан снять обе, иначе откат упрётся в
   * `service_requests_replacement_check` или в `service_requests_rejection_resolution_check`
   * ошибкой БД. Прежнее имя `replacement` называло половину и после Р12 стало бы врать.
   */
  rejection: boolean;
  /**
   * Очистить поля заморозки: `held_from_status` и `hold_reason` (Р118). Поднимается на **любом**
   * выходе из `on_hold` — и при возобновлении, и при отмене отложенной, и на пути, заведённом
   * позже. Иначе `service_requests_hold_check` поймает отмену отложенной ошибкой БД: остальные
   * поля этой структуры про исполнителя и согласование, про заморозку они не знают.
   */
  hold: boolean;
}

const NO_RESET: ServiceTransitionReset = {
  itApproval: false,
  executor: false,
  estimate: false,
  approval: false,
  completion: false,
  acceptance: false,
  rejection: false,
  hold: false,
};

/**
 * Р118. Флаг заморозки **дополняет** обычный сброс, а не подменяет его: напрашивающаяся ветка
 * `if (from === 'on_hold') return { ...NO_RESET, hold: true }` в цепочке ниже была бы ошибкой —
 * отмена отложенной заявки обязана снять исполнителя и снимок согласования ровно так же, как
 * отмена из любого другого статуса, иначе у отменённой останутся и сервис, и согласие со сметой.
 * Поэтому базовый сброс считается по паре `(from → to)` отдельной функцией, а `hold` приписывается
 * сверху, каким бы этот сброс ни оказался.
 *
 * Вход в заморозку не сбрасывает ничего: она ничего не отменяет — ни одна ветка `to` про `on_hold`
 * не знает, и это верно.
 */
export function serviceResetOnTransition(
  from: ServiceRequestStatus,
  to: ServiceRequestStatus,
): ServiceTransitionReset {
  return { ...baseServiceReset(from, to), hold: from === 'on_hold' };
}

/**
 * Сброс по паре статусов, без заморозки. Возврат из `on_hold` попадает в ту же ветку, что и
 * обычный переход в тот же статус: два правила на один переход разошлись бы на первой же правке.
 */
function baseServiceReset(
  from: ServiceRequestStatus,
  to: ServiceRequestStatus,
): ServiceTransitionReset {
  /*
   * `in_work → new` НЕ СНИМАЕТ ИСПОЛНИТЕЛЯ, и это не пропуск, а условие работоспособности хода,
   * который по этой дуге идёт.
   *
   * Прежняя ветка `assigned → new` снимала — но её звал отказ, а отказ переходом быть перестал
   * (Р7): состав он правит сам, статуса не трогает. Ходов по дуге было двое, остался один, и обоим
   * сброс здесь во вред:
   *
   * - **откат «принял в работу»** (Р13) обязан вернуть заявку назначенным, НЕ ТЕРЯЯ их: прежде эта
   *   дуга называлась `in_work → assigned` и не сбрасывала ничего. Сними мы исполнителя — откат
   *   «взялся» превратился бы в откат «назначил», то есть в другое действие, которого больше нет;
   * - **переназначение из «В работе»** по этой дуге больше не ходит вовсе (ADR 0187): назначение
   *   статуса не меняет. Довод, впрочем, остаётся верным и для него — состав пишется ДО
   *   `applyTransition`, потому что письмо собирается в той же транзакции и читает исполнителей из
   *   таблицы, а сброс, идущий следом, удалил бы строки по всей заявке (`reset.executor` — это
   *   `DELETE ... WHERE request_id = …`). Появись у назначения новая дуга — она наступит на те же
   *   грабли, и абзац здесь ради этого.
   *
   * Снять исполнителя по-прежнему есть чему: отмена (ветка ниже) и возврат отменённой в «Новую».
   */
  // Отмена возвращает заявку в состояние «ничего не делали»: она остаётся историей того, что
  // собирались чинить, но ни исполнителя, ни согласованной сметы у неё больше нет.
  if (to === 'cancelled') return { ...NO_RESET, executor: true, approval: true };
  if (from === 'cancelled' && to === 'new') {
    // Возвращённая отменённая заявка проходит цикл заново — в том числе визу ИТ: отклонить её мог
    // как раз он, и сохранённая подпись означала бы согласие, которого не было. Пометка «менять
    // аппарат» снимается тем же ходом: она относилась к отмене, которой больше нет.
    return {
      ...NO_RESET,
      itApproval: true,
      executor: true,
      estimate: true,
      approval: true,
      rejection: true,
    };
  }
  // Возврат на доработку и одноимённый откат: факт закрытия предъявлен заново.
  if (from === 'done' && to === 'in_work') return { ...NO_RESET, completion: true };
  if (from === 'accepted' && to === 'done') return { ...NO_RESET, acceptance: true };
  // Остальные переходы не стирают ничего. Согласования объёма работ среди них больше нет вовсе
  // (Р8): «согласовано» статуса не меняет и до этой функции не доходит, а «не согласовано» —
  // обычная отмена, которую разобрала ветка `to === 'cancelled'` выше. Погашение самого
  // предъявления (`estimate_pending_revision`) делает ручка, а не сброс по паре статусов: оно
  // случается и без перехода (Р2).
  return NO_RESET;
}

// ── Признаки вместо снятых статусов (Р2) ──
//
// Сняв «Назначенную» и «Смету на согласовании», мы обязаны назвать то, что они означали. Признаков
// ровно два, и оба живут здесь предикатами — одна функция на сервер и портал, как и всё прочее в
// этом модуле: разойдись они, портал рисовал бы кнопку, которую сервер не пускает, либо прятал бы
// разрешённое действие.

/**
 * Состав исполнителей заявки. Пара «контрагент + число поимённых строк», а не готовый признак,
 * потому что собирают её из разного: сервер — из колонки и строк `service_request_executors`,
 * портал — из `service` и `executors.length` карточки. Общего типа у строки БД и DTO нет, и это тот
 * же приём, каким живёт `serviceRequestNeedsClosingDocument`.
 */
export interface ServiceExecutorsRow {
  /** Исполнитель-контрагент; `null` — заявку ведут только свои. */
  serviceCounterpartyId: string | null;
  /** Сколько поимённых строк `service_request_executors` у заявки. */
  executorCount: number;
}

/**
 * У заявки есть исполнители: поимённые строки ЛИБО назначенная сервисная компания. Это ровно то,
 * что означала «Назначена», — и то, чем «Новая» после слияния отличается от «Новой»: без
 * исполнителей заявка ждёт распределения, с ними — что за неё возьмутся (Р2).
 *
 * Дизъюнкция, а не «строки есть»: у сервисной компании поимённых строк не бывает вовсе —
 * назначается компания целиком (Н5), — и заявка, отданная подрядчику, числилась бы вечно
 * нераспределённой: и в очереди оператора, и в письмах, и в правке заказчиком (Р14).
 */
export function serviceHasExecutors(row: ServiceExecutorsRow): boolean {
  return row.serviceCounterpartyId !== null || row.executorCount > 0;
}

/**
 * Первое ли это назначение — то есть отбираем ли мы работу у прежнего исполнителя. От ответа
 * зависит обязательность причины, и прежде он читался из статуса (`row.status === 'new'`) только
 * потому, что статус с ним СОВПАДАЛ. Совпадать больше нечему, и признак становится тем, чем был по
 * смыслу: «исполнителей у заявки ещё не было» — ни строк, ни контрагента (Р11).
 *
 * Одной функцией на оба места, где вопрос задавался порознь: окно назначения решает по ней,
 * спрашивать ли причину, а ручка — принимать ли тело без неё. Разойдись они, окно требовало бы
 * причину там, где она не нужна, либо отправляло запрос, на который придёт 422.
 */
export function serviceIsFirstAssignment(row: ServiceExecutorsRow): boolean {
  return !serviceHasExecutors(row);
}

/**
 * Объём работ предъявлен, и ответа на него ещё нет, — то, что означала «Смета на согласовании».
 * `NULL` в колонке значит «ответ получен» (согласовали, отклонили либо исполнитель отозвал
 * предъявление), число — «висит предъявление этой ревизии». Инвариант «предъявлена именно текущая
 * ревизия» держит `CHECK` в БД, а не эта функция.
 *
 * **Почему своя колонка, а не вывод из существующих.** Две первые редакции плана выводили признак
 * из того, что уже лежит в строке, и обе ошиблись в одном месте. Ревизия без снимка предъявления не
 * отличает «вернули в правку» от «ждёт подписи»: возврат в правку чистит снимок согласования и
 * ОСТАВЛЯЕТ `estimate_submitted_at`. Добавленный к ней снимок предъявления не отличает того же
 * самого у старых данных: отказ по смете эту дату тоже не чистил, а нормализовать её миграцией
 * нельзя — тот же запрос через релиз накрыл бы настоящие предъявления, созданные уже по новой
 * модели.
 *
 * Ошибка обоих подходов одна: признак выводился из полей, которые пишет и СТАРЫЙ код, и отличить
 * его запись от нашей было нечем. Своя колонка даёт это отличие по построению — старый код о ней не
 * знает и оставляет `NULL`, — а `estimate_submitted_at` остаётся тем, чем был: датой последнего
 * предъявления для показа. Активное состояние по ней не определяется вовсе: у отозванного
 * предъявления дата непуста (Р9).
 */
export function serviceEstimatePending(row: { estimatePendingRevision: number | null }): boolean {
  return row.estimatePendingRevision !== null;
}

// ── Доступность действий, переставших быть переходами (Р11) ──
//
// Портал строит меню из коридора: назначение он искал по дуге в «Назначенную», отказ — по дуге в
// «Новую», предъявление — по дуге в «Смету на согласовании». Это работало ровно пока у каждого
// действия была своя дуга; после правки дуг нет у четырёх действий из пяти, и, не заведи мы им
// замену, пункты меню просто исчезли бы с экрана — молча: сервер разрешает, портал не рисует.
//
// **Перечень статусов в каждом предикате — не украшение, а половина условия.** Признаки Р2 сами по
// себе шире допустимых состояний: и назначенные исполнители, и висящее предъявление, и снимок
// подписи переживают «Решена», «Закрыта», «Отменена» и заморозку. Без явного перечня переназначение
// попало бы в «Решена» (работа предъявлена, ждут приёмки — менять исполнителя там нечего), а
// согласовать объём работ можно было бы по отменённой заявке: `serviceEstimatePending` переход в
// «Отменена» переживает, и удерживает её вне очереди подписи ровно перечень.

/**
 * Строка заявки, по которой считается доступность действий. Одна на все пять предикатов, потому что
 * собирает её один раз карточка (портал) либо чтение заявки (сервер), — а не каждый предикат сам:
 * ходить за строкой умеет только один из двух зовущих. Каждый предикат при этом объявляет `Pick`
 * ровно из того, что читает: перечень полей в сигнатуре и есть перечисление условий из таблицы Р11.
 *
 * Состав исполнителей входит в неё наследованием, потому что рядом с назначением всегда спрашивают
 * и `serviceIsFirstAssignment` — обязательность причины окно решает той же строкой.
 */
export interface ServiceActionRequest extends ServiceExecutorsRow {
  kind: ServiceRequestKind;
  status: ServiceRequestStatus;
  /** Непогашенное предъявление объёма работ (Р2). */
  estimatePendingRevision: number | null;
  /** Ревизия, под которой стоит подпись; `null` — согласования нет (Р9, «есть что снимать»). */
  approvedEstimateRevision: number | null;
  /*
   * The two fields of the post-dispute signature (Р9 of
   * `docs/office-equipment-on-site-and-invoice-estimate-plan.md`) stay OPTIONAL on purpose, as a
   * fail-closed default rather than a leniency.
   *
   * Every server since release A sends both on every request row, and the DTO declares the origin
   * required, but this shape is assembled by hand (the portal's `serviceActionRow`, test fixtures),
   * and a builder that forgets a field must fail closed, not open. A missing value reads as "the
   * wait was not opened by a dispute", which keeps the approval in «Решена» closed: exactly the
   * behaviour without the feature.
   *
   * The dangerous half is one — opening the approval in «Решена» to a wait that should not have it
   * — and it needs BOTH fields at once: empty, they refuse rather than admit.
   */
  /** Номер действующей ревизии объёма работ: с ним сверяется происхождение ожидания (Р9). */
  estimateRevision?: number;
  /** Чем открыто непогашенное ожидание подписи: предъявлением или исходом спора (Р9). */
  estimatePendingSource?: ServiceEstimatePendingSource | null;
}

/**
 * The executor side as a whole — the same rule the corridor reads it by, in the same order: the
 * operator of the assigned contractor answers INSTEAD of the others (by permissions a service
 * company's operator resembles «Ведение», and by the sum of conditions he would approve his own
 * invoice); an own employee — by the named assignment in pair with `execute`; the administrator —
 * by the estimate permission. That last branch is no widening: outside the service counterparty
 * only he holds `serviceRequests.estimate`, and without it a stuck request could not be finished.
 *
 * EXPORTED, although the R11 predicates call it themselves. The portal asks the same question where
 * no predicate expresses the side's action — editing the consumables list: its permission is the
 * pair `estimate` + `execute`, and «assigned to THIS request» is checked by the handler's body.
 * Without the export the portal would keep a copy of the rule, and it would drift silently — as a
 * button leading to a 403.
 *
 * `has` answers both permissions and is passed on to `isServiceExecutor`. The server leaves the
 * default, the matrix over its own principal; the portal passes its `can` over the list the server
 * computed (ADR 0223).
 */
export function actsAsServiceExecutor(
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  if (actsForCounterparty(subject, 'service')) return assignment.actsForAssignedCounterparty;
  return isServiceExecutor(subject, assignment, has) || has('serviceRequests.estimate');
}

/**
 * Whose estimate this is (plan `docs/office-equipment-workflow-fixes-plan.md`, Р6): the operator of
 * the assigned contractor, or the holder of `serviceRequests.estimate` — outside the service
 * counterparty that is only the administrator, who finishes a stuck request for anyone.
 *
 * NARROWER THAN `actsAsServiceExecutor` BY EXACTLY ONE BRANCH — the named in-house executor. The
 * owner's answer of 08.09.2026: the estimate is dropped for internal requests only and stays with
 * the contractor's people. On a mixed assignment (an own system administrator plus a service
 * company) `serviceRequestNeedsEstimate` is true, and the wide side would let the own employee
 * write, submit, withdraw and document the contractor's invoice — through the API, without a
 * single button. The estimate is the contractor's commercial offer; the own employee is paid by
 * nobody and has nothing to offer.
 *
 * Every estimate door asks this one function — `PUT /:id/estimate`, `canSubmitServiceEstimate`,
 * `canReopenServiceEstimate`, the side of the `estimate` file kind and, through those predicates,
 * the portal's menu and tab. A copy of the rule in any of them would reopen the door it closes.
 * Approval is NOT here: the approving side is counted differently and on purpose
 * (`canApproveServiceEstimate`).
 *
 * `has` answers the permission, as in `actsAsServiceExecutor`: the server leaves the matrix
 * default, the portal may pass its `can` over the server's list (ADR 0223).
 */
export function actsAsEstimateAuthor(
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  if (actsForCounterparty(subject, 'service')) return assignment.actsForAssignedCounterparty;
  return has('serviceRequests.estimate');
}

/**
 * Сторона «Ведения» — тот, кто ведёт заявку по циклу: администратор либо держатель набора
 * `office_equipment_operator`, и ни при каких условиях не подрядчик.
 *
 * ПАРА К `actsAsServiceExecutor`, и заведена по той же причине: сторону спрашивают в двух местах —
 * подсветка адресата «Оргтехнике (ведение)» в ленте и дверь повтора служебного письма
 * (`POST /:id/notify`, план аудита исполнителей, Р9), — а правило обязано быть одно. Формула стояла
 * внутри `matchesServiceChatSide`, оттуда сюда и вынесена: скопируй её вторая дверь, и первая же
 * правка модели профилей развела бы «кому реплика подсвечена» и «кому открыт повтор» молча.
 *
 * ПОЧЕМУ КОД НАБОРА, А НЕ СУММА ПРАВ. Пара `status` + `assign` давала ровно «Ведение», пока такой
 * набор был один; с назначаемыми полномочиями (ADR 0106) его собирают в проде руками, и профиль
 * модуля выводился бы из состава чужого набора. Профиль — это код, а не сумма прав.
 *
 * `admin` отдельной строкой: кодов у него нет вовсе, а разбирать застрявшее — его работа.
 * Исключение подрядчика — второй рубеж на случай, если набор «Ведения» выдадут ему руками: право
 * `serviceRequests.status` у типа контрагента `service` есть, и без этой половины исполнитель стал
 * бы заказчиком собственной работы.
 */
export function actsAsServiceOperator(subject: AccessSubject | null | undefined): boolean {
  return (
    (subject?.role === 'admin' || hasGrantCode(subject, OFFICE_EQUIPMENT_OPERATOR_GRANT)) &&
    !actsForCounterparty(subject, 'service')
  );
}

/**
 * Признаки заявки, которыми решается сторона ЗАКАЗЧИКА, — и ровно два, а не пять.
 *
 * Отдельным типом от `ServiceChatFacts`, который его расширяет (см. ниже): стороне заказчика
 * назначение и контрагент не нужны ни одним слагаемым, а требуй предикат полных фактов — каждая
 * зовущая ручка ходила бы за строкой исполнителей ради вопроса, который на неё не смотрит. Общая
 * половина при этом остаётся ОДНОЙ: наследование здесь и есть обещание, что «автор» и «область
 * заказчика» у обсуждения и у стража правки значат одно и то же.
 */
export interface RequestCustomerFacts {
  /** Он завёл эту заявку (`created_by`). */
  isAuthor: boolean;
  /**
   * Заявка видна ему СО СТОРОНЫ ЗАКАЗЧИКА — ролью площадки или отдела
   * (`inServiceRequestCustomerScope`). Сквозная область набора сюда не входит намеренно: ИТ-служба
   * видит заявку не потому, что заявка её.
   */
  inCustomerScope: boolean;
}

/**
 * Коды наборов модуля, которыми доступ приходит НЕ «заявителем». Держатель любого из них ведёт
 * модуль по-своему — координацией, работой руками или циклом целиком, — и правило автора ниже про
 * него не написано.
 *
 * Перечислены поимённо, а не «всё, что начинается на `office_equipment`»: коды приходят из базы
 * строками, и префикс поймал бы заодно набор номенклатуры расходников, к стороне заказчика
 * отношения не имеющий вовсе.
 */
const OFFICE_EQUIPMENT_NON_REQUESTER_GRANTS = [
  OFFICE_EQUIPMENT_OPERATOR_GRANT,
  OFFICE_EQUIPMENT_IT_GRANT,
  OFFICE_EQUIPMENT_EXECUTOR_GRANT,
] as const;

/**
 * Роли, которым круг заявителя приходит САМОЙ РОЛЬЮ, а не набором (ADR 0181, решение заказчика
 * 10.09.2026): `manager` — временная роль-заплатка, и всё, что она раздаёт, уходит вместе с её
 * строкой в матрице, а не строкой в `grant_roles` и выдачами в `user_grants`.
 *
 * ЗАЧЕМ СПИСОК ЗДЕСЬ. Страж ниже опознаёт заявителя КОДОМ набора (Р9), и права, пришедшие ролью,
 * прошли бы мимо него целиком: менеджер видит заявки всей компании и правил бы, удалял и подшивал
 * бы файлы к любой чужой. Второй источник профиля требует второго признака — вот он.
 *
 * ИМЕНЕМ РОЛИ, А НЕ СОСТАВОМ ПРАВ, и это не отступление от Р9, а его прямое следствие. «Профиль —
 * это код, а не сумма прав» ровно потому, что собранный руками набор с тем же составом не должен
 * получать чужое ограничение; у роли же кода нет вовсе, и назвать её нечем, кроме имени. Обратное
 * правило — «сузить всякого, у кого есть круг заявителя и нет прав ведения» — это отрицательное
 * основание, которое ломается молча: первое же новое право модуля вывело бы роль из-под стража, и
 * ошибка была бы в неперечисленном.
 *
 * `satisfies readonly Role[]` — сторож удаления роли: в тот день, когда `manager` уйдёт из `ROLES`,
 * эта строка не скомпилируется, и заплатку не придётся искать по дереву.
 */
const OFFICE_EQUIPMENT_ROLE_REQUESTERS = ['manager'] as const satisfies readonly Role[];

/**
 * Тот единственный субъект, которого правило сужает: **роль без своей оси, чей доступ к модулю
 * пришёл ТОЛЬКО набором «Заявитель»** (план профилей оргтехники, Р6).
 *
 * Три сомножителя, и каждый отсекает свой случай:
 *
 *   1. `roleScopeAxis(...) === 'none'` — у роли нет оси области, то есть предикат видимости ей
 *      ничего не сужает и заявки компании видны ей целиком. У роли с осью сужение уже работает, и
 *      второе правило поверх него меняло бы поведение действующих учёток;
 *   2. доступ пришёл заявительским слоем — выданным кодом «Заявителя» ЛИБО самой ролью, если она
 *      названа в `OFFICE_EQUIPMENT_ROLE_REQUESTERS` (ADR 0181: у `manager` круг заказчика стоит
 *      прямо в матрице). Профиль по-прежнему опознаётся кодом, а не суммой прав (Р9): собранный
 *      руками набор с теми же шестью правами сюда не попадает — сузить его значило бы вывести
 *      профиль модуля из чужого набора, ровно то, от чего Р9 уводит;
 *   3. ни одного другого кода модуля нет. «Ведение» открывает операторские действия ОТДЕЛЬНЫМ
 *      слоем, и смешать их с авторским правилом нельзя: централизованное ведение перестало бы
 *      работать в тот же день, когда его разрешат (Р6 прямым текстом).
 *
 * `admin` проходит первый сомножитель (его ось — `none`) и не проходит второй: кодов наборов у него
 * нет вовсе, а роли его в списке ролей-заявителей нет и быть не может — права у него от полного
 * перечня, и разбор застрявшего за любую сторону его работа. Это не случайное везение, а то же основание, по которому он проходит в
 * `actsAsServiceOperator` отдельной строкой, — права у него от роли, а не от выдачи.
 */
function isGlobalRequesterOnly(subject: AccessSubject | null | undefined): boolean {
  if (!subject) return false;
  if (roleScopeAxis(subject.role) !== 'none') return false;
  const byRole =
    subject.role !== null &&
    subject.role !== undefined &&
    (OFFICE_EQUIPMENT_ROLE_REQUESTERS as readonly Role[]).includes(subject.role);
  if (!byRole && !hasGrantCode(subject, OFFICE_EQUIPMENT_REQUESTER_GRANT)) return false;
  return !OFFICE_EQUIPMENT_NON_REQUESTER_GRANTS.some((code) => hasGrantCode(subject, code));
}

/**
 * **Разрешает ли сторона заказчика этому субъекту менять ЭТУ строку** (план профилей оргтехники,
 * Р6; план карточки заявителя, §13 — «добавить авторскую проверку в подшивку и правку»).
 *
 * ДЫРА, КОТОРУЮ ЗАКРЫВАЕТ. У `manager` и `dispatcher` оси области нет, и предикат видимости им
 * ничего не сужает — так устроена роль без оси, и это правильно. Но набор «Заявитель» заводится
 * ради них же (Р6: диспетчер сидит в офисе рядом с принтером, а завести заявку не может), и без
 * этого стража «видит все заявки компании» превращалось бы в «правит и удаляет любую чужую
 * „Новую“, подшивает к ней файлы и пишет от лица заявителя». Набор выдаётся ради своей заявки, а не
 * ради чужой.
 *
 * ПОЧЕМУ ФУНКЦИЯ ОТВЕЧАЕТ «ДА» ВСЕМ ОСТАЛЬНЫМ. Это страж, а не описание стороны: вопрос у него —
 * «сужает ли ЭТОТ слой этого субъекта», и «нет» тут значит «правило не про него», а не «он
 * заказчик». Иначе её нельзя было бы позвать одним вызовом: администратор, «Ведение» и назначенный
 * исполнитель заказчиками не являются ни на одной заявке, и честный предикат стороны пришлось бы
 * сопровождать на каждой двери вторым условием «а применимо ли правило» — то есть завести второе
 * место, где его забывают. Свои действия этих сторон стерегут свои предикаты
 * (`actsAsServiceExecutor`, `actsAsServiceOperator`, коридор переходов), и отбирать их этот слой не
 * вправе.
 *
 * ДИЗЪЮНКЦИЯ ЗАПИСАНА ЦЕЛИКОМ, ХОТЯ ВТОРАЯ ЕЁ ПОЛОВИНА СЕГОДНЯ ВСЕГДА ЛОЖНА. У роли без оси
 * `inCustomerScope` не бывает истинным ни при какой заявке (`inServiceRequestCustomerScope`
 * отвечает по площадке и отделу, а их у такой роли нет). Написана она потому, что правило Р6
 * звучит «существующая объектная/отдельская сторона ЛИБО автор строки»: получи такая роль однажды
 * ось, сторона обязана работать сама, а не через правку этого файла. Стоит она первой и по второй
 * причине — так читается порядок «сначала прежнее правило, потом добавка».
 *
 * ХОДИТЬ В БАЗУ ЕЙ НЕЧЕМ И НЕЗАЧЕМ: оба признака приходят посчитанными, как у всех предикатов
 * действий модуля. Сервер считает их по строке заявки под теми же условиями, что и видимость
 * (`assertActsAsRequestCustomer` в `lib/access.ts`), портал — из готового ответа: `isAuthor` ему
 * приходит фактом в сводке обсуждения (`chat.participantSides` содержит `customer` ровно у автора),
 * потому что `ServiceRequestDto` не раскрывает `createdBy` и вывести авторство сам портал не может.
 * Второй копии правила на клиенте от этого не заводится — обе стороны зовут эту функцию.
 *
 * СТОРОНУ ОБСУЖДЕНИЯ ЭТА ФУНКЦИЯ НЕ ЗАМЕНЯЕТ И В `matchesServiceChatSide` НЕ ПЕРЕЕЗЖАЕТ. Участие в
 * разговоре со стороны `customer` там считается строже — только автором, — и подставь мы сюда
 * страж, участниками «Заявителя» стали бы разом все, кого правило не сужает: администратор,
 * подрядчик, наблюдатель. Письмо от лица заказчика закрыто поэтому тем же автором, а не этим
 * предикатом (Т17 проверяет 403 на чужой заявке именно там).
 */
export function actsAsRequestCustomer(
  subject: AccessSubject | null | undefined,
  facts: RequestCustomerFacts,
): boolean {
  if (!isGlobalRequesterOnly(subject)) return true;
  return facts.inCustomerScope || facts.isAuthor;
}

/**
 * **В модуле он исполнитель и никто больше** — тот единственный профиль, которому назначение
 * область СУЖАЕТ, а не расширяет (план
 * `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р3; ответы В3 и В6 заказчика от
 * 09.09.2026: «исполнитель видит только свои заявки», плюс свои заведённые и снятые с него).
 *
 * Три сомножителя, и каждый отсекает своё: доступ к модулю пришёл ему набором ИТ-службы либо
 * набором исполнителя, то есть работой руками, а не должностью заказчика; роль не `admin`; и у него
 * нет «Ведения».
 *
 * ЗАМЕНИЛА СОБОЙ `isModuleWideReaderOnly`, СТОЯВШУЮ РОВНО ЗДЕСЬ. Форма у них общая — те же два
 * исключения и по тем же причинам: «Ведение» (`office_equipment_operator`) ведёт модуль и правит
 * чужие заявки по должности, операторский слой с авторским не смешивается, а пара «Ведение + ИТ» у
 * одного человека — рабочий случай, он и координирует, и чинит; у `admin` же права от роли, кодов
 * наборов у него не бывает вовсе, и разбор застрявшего за любую сторону — его работа (по тому же
 * основанию он исключён в `actsAsServiceOperator` и в стороне обсуждения `it`). Разошлись они в
 * первом сомножителе, и это и есть решение Р10: прежняя спрашивала КАРТУ области
 * (`hasModuleWideScope(…, 'serviceRequests')`), а карта из модуля уезжает следующим выпуском — вся
 * логика, на неё опёртая, после уборки молча поменяла бы ответ.
 *
 * СПРАШИВАЮТСЯ КОДЫ НАБОРОВ, А НЕ ПРАВО `serviceRequests.execute`, и это не вкусовщина: «по праву»
 * ответило бы неверно с обеих сторон сразу.
 *
 *   1. У роли `admin` право есть по построению (`admin: [...PERMISSIONS]`), и признак отобрал бы у
 *      администратора все заявки портала — сузив ровно того, кто разбирает застрявшее;
 *   2. у держателя ОДНОГО ИТ-набора его нет вовсе: миграция 0262 отдала `execute` отделённому
 *      набору исполнителя. «По праву» оставило бы такому держателю сквозную видимость целиком, то
 *      есть не сузило бы ничего у того, ради кого решение и принято.
 *
 * ПОДРЯДЧИКА ЭТА ФУНКЦИЯ НЕ КАСАЕТСЯ И КАСАТЬСЯ НЕ ДОЛЖНА: у оператора контрагента `service` кодов
 * наборов оргтехники не бывает ни одного (`OFFICE_EQUIPMENT_PROFILE_REGISTRY`, профиль `service` —
 * пустой список), а сужает его своя ось «заявка назначена моей компании». Ответ В7 — «у сервисного
 * центра всё верно уже сегодня», — и трогать его волна не бралась.
 *
 * СЛЕДА СНЯТИЯ ЗДЕСЬ НЕТ: это свойство ЗАЯВКИ, а не субъекта, и живёт оно в области чтения. Функция
 * отвечает только на вопрос «кто он в модуле», и все три сомножителя читаются по самому субъекту —
 * поэтому её одинаково зовут и предикат выборки списка, и проверка по одной строке.
 */
export function actsAsServiceExecutorOnly(subject: AccessSubject | null | undefined): boolean {
  if (!subject) return false;
  if (subject.role === 'admin') return false;
  if (hasGrantCode(subject, OFFICE_EQUIPMENT_OPERATOR_GRANT)) return false;
  return (
    hasGrantCode(subject, OFFICE_EQUIPMENT_IT_GRANT) ||
    hasGrantCode(subject, OFFICE_EQUIPMENT_EXECUTOR_GRANT)
  );
}

/**
 * **Выбирает ли субъект предмет заявки по всей компании** — площадку аппарата, отдел-заказчика,
 * площадку сообщения об отсутствующей технике (план
 * `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р9).
 *
 * ОСНОВАНИЕ ПЕРЕЕЗЖАЕТ С КАРТЫ ЗАЯВОК НА КАРТУ ПАРКА, и в этом всё решение. Сегодня четыре разбора
 * предмета спрашивают сквозную область МОДУЛЯ ЗАЯВОК (`hasModuleWideScope(…, 'serviceRequests')`),
 * хотя ни один из них не про ленту заявок: площадка аппарата, отдел-заказчик и площадка кандидата —
 * это СПРАВОЧНИК, из которого заявку собирают, и вопрос у них один и тот же — «весь ли парк ему
 * показывать». Пока оба ключа стоят у одного набора (ИТ-служба), подмена не меняет ни одного
 * ответа; ровно это и обязан доказать parity-тест, а не рассуждение.
 *
 * ЗАЧЕМ ТОГДА МЕНЯТЬ, ЕСЛИ ОТВЕТ ТОТ ЖЕ. Затем, что `serviceRequests` из карты области уезжает
 * следующим выпуском, и без этой правки уборка строки молча сузила бы ЗАВЕДЕНИЕ заявки: «аппарат
 * вижу, а завести по нему заявку не могу» — не сужение видимости заявок, а поломка сценария, ради
 * которого парк и оставлен видимым целиком (ответ В5). То есть выпуск, названный технической
 * уборкой, поменял бы продуктовые правила заведения. С этим предикатом он остаётся тем, чем назван,
 * — уборкой строки, которую больше никто не читает.
 *
 * `admin` отдельной веткой по тому же основанию, что у соседей: карту читают по кодам наборов, а у
 * него их нет вовсе.
 */
export function canPickAnyServiceSubject(subject: AccessSubject | null | undefined): boolean {
  if (!subject) return false;
  if (subject.role === 'admin') return true;
  return hasModuleWideScope(subject.grantCodes, 'officeEquipment');
}

/**
 * **Распоряжается ли субъект самой записью со стороны заказчика** — то есть открыты ли ему ПРАВКА и
 * УДАЛЕНИЕ этой заявки (план профилей оргтехники, находка Н8, решение заказчика 04.09.2026).
 *
 * ПРАВИЛО ОДНОЙ СТРОКОЙ: правку и удаление открывает сторона заказчика, посчитанная по
 * СОБСТВЕННОЙ оси субъекта (его площадка, его отдел) либо по авторству. Сквозная область стороной
 * заказчика не делает — она отвечает «видно», а не «моё».
 *
 * ЧТО ЭТО ЗАКРЫВАЕТ. Обе половины профиля «Системный администратор» выдаются поверх ролей
 * `shtab`/`site`/`department`, у каждой из которых `serviceRequests.update` и `.delete` есть по
 * матрице как у заказчика, а сквозная область ИТ-набора снимает у них ось. Итог до правила:
 * держатель профиля правил и уводил в архив ЛЮБУЮ «Новую» заявку компании, тогда как матрица §5.1
 * обещает обратное двумя клетками. Правило и есть то место, которое эти клетки теперь держит.
 *
 * СУЖАЕТ ОНО РОВНО ДВЕ ДВЕРИ, А НЕ ЧЕТЫРЕ, И ЭТО ГЛАВНОЕ В НЁМ. Страж авторства
 * (`actsAsRequestCustomer`) стоит на общем входе всех изменяющих ручек, и под ним, кроме правки и
 * удаления, живёт ПОДШИВКА И СНЯТИЕ ДОКУМЕНТОВ. Сисадмина штатно назначают исполнителем на заявки
 * чужих площадок — ради этого у профиля второй набор, — и подшить акт по такой заявке он обязан.
 * Расширь мы существующий страж на сквозную область «в лоб», назначенный исполнитель перестал бы
 * прикладывать документы к заявке, которую сам же чинит. Поэтому правило отдельной функцией и
 * зовётся только с двух дверей; на остальных двух прежнее правило остаётся ровно таким, каким было.
 *
 * ПРЕЖНЕЕ ПРАВИЛО СНИМАЕТСЯ ПЕРВЫМ, а не заменяется этим: субъекты у них разные — там роль без оси
 * с набором «Заявитель», здесь исполнительский профиль модуля, — и оба обязаны отвечать на своей
 * двери. Конъюнкция читается как «сначала прежний вопрос, потом добавка», и порядок этот
 * единственно верный: глобальному заявителю сквозной области не выдают, а сисадмину «Заявитель»
 * не нужен, но собрать одному человеку оба набора администратор вправе.
 *
 * ОСНОВАНИЕ ОТВЯЗАНО ОТ КАРТЫ ОБЛАСТИ (план
 * `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р10). Прежде тот же субъект
 * опознавался здесь через сквозную область модуля заявок, то есть через КАРТУ. Правило от этого
 * работало — назначенный на чужую площадку сисадмин правки и удаления не получал, — но держалось
 * на строке, которую волна как раз и убирает: сними карту, признак стал бы ложным, страж ответил бы
 * `true`, и правка с удалением ЧУЖОЙ заявки открылись бы через назначение. Правило «правка и
 * удаление — своя площадка либо своё авторство» обязано пережить уборку карты без единой правки
 * поведения, поэтому субъект опознаётся кодами наборов (`actsAsServiceExecutorOnly`), а не
 * областью. Правится это ВЫПУСКОМ A, а не B: область действий уже пропускает назначенного по своей,
 * исполнительской причине, и до замены между ней и этим стражем осталась бы щель.
 *
 * НАЗНАЧЕНИЕ ОСТАЁТСЯ ОСНОВАНИЕМ — но своим. Действия исполнителя и подшивка документов открыты
 * назначенному ровно как сегодня (см. абзац про две двери выше); разведены два разных основания:
 * «я это чиню» и «я этим распоряжаюсь». Первое даёт назначение, второе — сторона заказчика, и
 * смешивать их нельзя ни в одну сторону: слитые, они либо отобрали бы у исполнителя акт, либо
 * отдали бы ему чужую заявку целиком.
 *
 * ИЗМЕНЕНИЕ НАЗВАНО ВСЛУХ, А НЕ СПРЯТАНО ЗА «ТОТ ЖЕ СУБЪЕКТ». Держатель ОДНОГО исполнительского
 * набора (без ИТ) сквозной области не имеет и прежним признаком не ловился вовсе — правку и
 * удаление он получал по обычной оси своей роли. Теперь ловится: править и удалять он может свою
 * площадку и свои заявки, чужие — нет. Это правильная строгость, но это ИЗМЕНЕНИЕ поведения, а не
 * его сохранение, и доказывать его обязан отдельный тест.
 *
 * В БАЗУ НЕ ХОДИТ И ХОДИТЬ НЕ МОЖЕТ: признаки приходят посчитанными, как у соседей. Сервер считает
 * их по строке заявки тем же `inServiceRequestCustomerScope`, каким считается подсветка адресата
 * «Заявителю», портал — из готового ответа (`ServiceRequestDto.inCustomerScope` и сторона
 * `customer` в сводке обсуждения). Второй копии правила на клиенте от этого не заводится — обе
 * стороны зовут ЭТУ функцию.
 */
export function canChangeRequestAsCustomer(
  subject: AccessSubject | null | undefined,
  facts: RequestCustomerFacts,
): boolean {
  if (!actsAsRequestCustomer(subject, facts)) return false;
  if (!actsAsServiceExecutorOnly(subject)) return true;
  return facts.inCustomerScope || facts.isAuthor;
}

/**
 * Факты заявки, которыми решается вопрос «можно ли этому субъекту вообще её трогать» (план
 * `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р11).
 *
 * Три поля, и ни одного лишнего. СЛЕДА СНЯТИЯ ЗДЕСЬ НЕТ НАМЕРЕННО: он расширяет область ЧТЕНИЯ
 * (ответ В6 — «заявки, с которых его сняли»), а не действий. Снятый исполнитель заявку видит и в
 * обсуждении по-прежнему участвует, но чинить, назначать, замораживать и подшивать больше не может;
 * приедь след сюда четвёртым полем, «смотреть» и «распоряжаться» снова слились бы в один вопрос —
 * ровно то разделение, ради которого две области и разведены.
 */
export interface ServiceRequestActionFacts {
  /**
   * Рубильник `service_request_executor_scope` включён. Полем факта, а не вопросом внутри
   * предиката: предикаты контрактов чистые и в базу не ходят, на сервере признак приезжает полем
   * принципала тем же запросом, что и остальной субъект, а на портал — списком `features` в ответе
   * сессии. Протаскивай мы флаг параметром через каждого зовущего, о нём было бы где забыть.
   */
  executorScopeEnabled: boolean;
  /**
   * Он завёл эту заявку (`service_requests.created_by`) — то же самое поле, каким сторона
   * заказчика считается в `RequestCustomerFacts`.
   */
  isAuthor: boolean;
  /**
   * Он стоит в ДЕЙСТВУЮЩЕМ поимённом составе исполнителей. Именно действующем: строка назначения
   * при снятии удаляется физически, и «был назначен» — это уже след, то есть другой вопрос и другая
   * область (см. абзац выше).
   */
  isNamedExecutor: boolean;
}

/**
 * **Можно ли этому субъекту вообще трогать эту заявку** — общий первый сомножитель ВСЕХ действий
 * над ней (план `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р11).
 *
 * ПРАВИЛО ТРЕМЯ ВЕТКАМИ:
 *
 *   1. рубильник выключен — `true`, сегодняшнее поведение до единого ответа. Так выглядит выпуск A
 *      до включения и любой откат: строку ключа возвращает `UPDATE`, а не обратный релиз;
 *   2. рубильник включён и субъект — исполнительский профиль (`actsAsServiceExecutorOnly`):
 *      действие открывает действующее назначение либо своё авторство, и больше ничего;
 *   3. все остальные — `true`, без единого изменения. Ветка обязательна, и первые редакции плана её
 *      теряли: общий вход в изменяющие ручки стоит у ВСЕХ субъектов, и правило «назначен ∨ автор»,
 *      поставленное туда без развилки, отобрало бы правку у обычного коллеги с ролью площадки,
 *      который правит заявку своей площадки, автором не будучи. Это была бы переделка всей модели
 *      действий под видом сужения одного профиля.
 *
 * ЗОВУТ ЕГО ДВОЕ, И В ЭТОМ ВЕСЬ СМЫСЛ ОДНОГО ПРЕДИКАТА. Сервер зовёт его из
 * `assertServiceRequestActionable` — общего входа изменяющих ручек. Портал — первым сомножителем во
 * всех своих мутациях: набор пунктов меню, правка и удаление в списке, подшивка и снятие
 * документов, объём работ и расходники. Доступность действия портал считает сам — по праву и
 * статусу, — а про отношение к заявке не знает вовсе: без общего сомножителя бывшему исполнителю
 * рисовалась бы кнопка «Изменить исполнителей», а сервер отвечал бы на неё 403. Обещаний, которых
 * сервер не выполняет, модуль избегает предикатами с ADR 0162, и этот — из того же ряда.
 *
 * СЛУЧАЙ, КОТОРЫЙ ЛОВИТ ТОЛЬКО ОН: бывший исполнитель на СВОЕЙ площадке. Страж стороны заказчика
 * выше отвечает ему `true` по `inCustomerScope` — площадка действительно его, — а область действий
 * отвечает `false`: он не назначен и не автор. Считай портал доступность одним стражем заказчика,
 * он показал бы правку, удаление и загрузку документов там, где сервер отвечает 403 на каждую.
 *
 * ДВА ПРЕДИКАТА НЕ ЗАМЕНЯЮТ ДРУГ ДРУГА, А ПЕРЕМНОЖАЮТСЯ: `canChangeRequestAsCustomer` отвечает «моя
 * ли это запись», этот — «в моей ли она области действий». Вопросы разные, и ни один не выводится
 * из другого.
 */
export function canActOnServiceRequest(
  subject: AccessSubject | null | undefined,
  facts: ServiceRequestActionFacts,
): boolean {
  if (!facts.executorScopeEnabled) return true;
  if (!actsAsServiceExecutorOnly(subject)) return true;
  return facts.isNamedExecutor || facts.isAuthor;
}

/**
 * Назначить или переназначить исполнителей (`PUT /:id/executors`).
 *
 * Условие «предъявление не висит» — то же сегодняшнее правило, а не новое: переназначение из «Сметы
 * на согласовании» запрещено и сейчас (пустая строка в `SERVICE_ASSIGNER_TRANSITIONS`), потому что
 * цифры предъявленного объёма принадлежат прежнему исполнителю и переданная заявка оставила бы
 * новому чужой счёт. После слияния это же состояние зовётся «В работе» + `serviceEstimatePending`,
 * поэтому перечня статусов мало: не войди условие в сам предикат, запрет тихо исчез бы вместе со
 * статусом. Сперва решают по объёму работ — из «В работе» без предъявления переназначение снова
 * открыто, ровно как сегодня.
 *
 * Ожидание берётся ДЕЙСТВУЮЩЕЕ, а не сырой колонкой (Н11): у внутренней заявки предъявление могло
 * сохраниться с тех пор, когда объём работ был обязателен любому ремонту, а ответить на него после
 * Р5 некому. Спроси предикат колонку — такая заявка навсегда осталась бы с прежним исполнителем, и
 * единственным выходом был бы ручной возврат сметы в правку, которого волна не заводит.
 */
export function canAssignServiceExecutors(
  row: Pick<
    ServiceActionRequest,
    'kind' | 'status' | 'serviceCounterpartyId' | 'estimatePendingRevision'
  >,
  subject: AccessSubject | null | undefined,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  if (row.status !== 'new' && row.status !== 'in_work') return false;
  if (serviceRequestHasEffectivePendingEstimate(row)) return false;
  return has('serviceRequests.assign');
}

/**
 * Отказаться от заявки (`PATCH /:id/decline`). Статус один — «Новая»: отказ ВЗЯВШЕГОСЯ (из «В
 * работе») мы не открываем (Р7) — сегодня его нет, и заводить его заодно значило бы расширение, о
 * котором не просили; такую заявку возвращает переназначение или откат «Ведения».
 *
 * Право здесь ни при чём: отказывается назначенный, и сторону считает тот же
 * `actsAsServiceExecutor`, каким прежняя дуга `assigned → new` открывалась в коридоре. Частичный
 * отказ от полного предикат не отличает и отличать не должен: кого снимать — свою строку или всю
 * компанию — решает ручка, а «остался ли кто-то ещё» отвечает уже `serviceHasExecutors` (Р7).
 */
export function canDeclineServiceRequest(
  row: Pick<ServiceActionRequest, 'status' | 'serviceCounterpartyId' | 'executorCount'>,
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (row.status !== 'new') return false;
  /*
   * «Есть от чего отказываться» — второй потерянный запрет статуса, найден db-тестами при
   * реализации (первый — третий замок предъявления, Р9).
   *
   * Прежде это держала сама дуга: отказ ходил `assigned → new`, и нераспределённая заявка стояла в
   * «Новой», то есть до ручки не доходила вовсе. После слияния статусов (Р1) перечень стал `new`, и
   * нераспределённая проходит его наравне с назначенной. Предикат стороны её не отбивает: держатель
   * `serviceRequests.estimate` — вне контрагента-сервиса это администратор — проходит
   * `actsAsServiceExecutor` **по праву**, а не по назначению.
   *
   * Что получалось без этой строки: отказ по заявке, которой никто не брал, отвечает 200 и пишет
   * след — возраст ожидания обнуляется (заявка молодеет в очереди распределения, хотя её никто не
   * трогал), в ленту ложатся переход `new → new` и событие «исполнителей стало меньше» там, где их
   * не было ни одного, а в аудит — «отказ за всех» при нуле отказавшихся.
   *
   * Условие стоит в предикате, а не в теле ручки, намеренно: так его спрашивают обе стороны, и
   * пункт меню пропадает у той же заявки, у которой сервер отвечает отказом.
   */
  if (!serviceHasExecutors(row)) return false;
  return actsAsServiceExecutor(subject, assignment, has);
}

/**
 * Принять заявку в работу (`PATCH /:id/start`, Р6).
 *
 * Шестой предикат Р11, и заведён он позже пяти остальных — по находке db-тестов. План (Р6)
 * утверждал, что перечня статусов здесь не нужно: «ход открывает факт назначения, а не право, и он
 * же сам собой закрывает ход у нераспределённой заявки — `isServiceExecutor` ложен при любом
 * праве». Половина этого верна: `isServiceExecutor` действительно ложен. Но коридор исполнителя
 * открывает **дизъюнкция**, и вторая её половина — `can(subject, 'serviceRequests.estimate')` — от
 * назначения не зависит вовсе.
 *
 * Пока коридор был `assigned → in_work`, это ничего не значило: у «Новой» дуг не было, и держатель
 * права получал честный отказ. После Р6 коридор стал `new → in_work`, и запрет исчез вместе со
 * статусом — как исчезли два других (третий замок предъявления Р9 и «есть от чего отказываться»
 * у `decline`). Заявку без исполнителей администратор переводил в «В работе», и ловил это уже
 * отложенный `service_requests_executor_present` (0178) — на `COMMIT`, ошибкой БД: данные целы, но
 * наружу уходило 500 вместо отказа, а человеку вместо «сначала распределите заявку» показывали
 * «что-то пошло не так».
 *
 * Поэтому `start` перестаёт спрашивать коридор в одиночку и получает предикат, как остальные пять
 * действий: статус, наличие исполнителей и сторона. Портал спрашивает его же — иначе он рисовал бы
 * «Принять в работу» у нераспределённой заявки держателю права на объём работ.
 */
export function canStartServiceWork(
  row: Pick<ServiceActionRequest, 'status' | 'serviceCounterpartyId' | 'executorCount'>,
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (row.status !== 'new') return false;
  if (!serviceHasExecutors(row)) return false;
  return actsAsServiceExecutor(subject, assignment, has);
}

/**
 * Предъявить объём работ (`PATCH /:id/estimate/submit`).
 *
 * «Предъявление не висит» — первый из двух замков Р9, и без него правка была бы дырявой: прежде
 * повторное предъявление запирал сам статус (из «Сметы на согласовании» ручка была недоступна), а
 * сняв его, мы позволили бы исполнителю поднять ревизию и подменить снимок суммы под уже открытым
 * окном согласования. Согласующий нажал бы «Согласовать» по цифрам, которых больше нет, и сверка
 * ревизий на закрытии этого не поймала бы: ревизия-то согласована свежая.
 *
 * Вид — только ремонт: у расходников объёма работ нет вовсе, там предмет заявки — номенклатура.
 * Один вид спрашивать перестало быть достаточно (Р5): за работу своего сотрудника не платят, и
 * предъявлять по внутреннему ремонту нечего — на оба условия отвечает `serviceRequestNeedsEstimate`
 * первой строкой.
 *
 * The side is the estimate's author (`actsAsEstimateAuthor`, workflow-fixes plan Р6), not every
 * assigned person: on a mixed assignment the own employee does not submit the contractor's offer.
 */
export function canSubmitServiceEstimate(
  row: Pick<
    ServiceActionRequest,
    'kind' | 'status' | 'serviceCounterpartyId' | 'estimatePendingRevision'
  >,
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!serviceRequestNeedsEstimate(row)) return false;
  if (row.status !== 'in_work') return false;
  if (serviceEstimatePending(row)) return false;
  return actsAsEstimateAuthor(subject, assignment, has);
}

/**
 * ВПРАВЕ ЛИ СУБЪЕКТ ЗАЯВИТЬ ОСВОБОЖДЕНИЕ ОТ ПОДПИСИ — галочка «Согласование не требуется» (Р1, Р3
 * плана `docs/office-equipment-on-site-and-invoice-estimate-plan.md`).
 *
 * ТОЛЬКО ОПЕРАТОР КОНТРАГЕНТА-СЕРВИСА, одной веткой вместо трёх (ответ В1 заказчика от
 * 11.09.2026). Поимённый свой исполнитель такого заявления не делает, и администратор тоже: за
 * работу своего сотрудника не платят (первая строка, `serviceRequestNeedsEstimate`), а «Ведение»
 * подпись ставит, а не отменяет. Поэтому здесь НЕ зовётся `actsAsServiceExecutor`: он шире ровно на
 * эти две ветки, и заявление стало бы доступно обеим сторонам денежного решения сразу.
 *
 * Назначение спрашивается тем же признаком, что и всюду: заявление по заявке ЧУЖОГО подрядчика —
 * не освобождение, а подпись за другого.
 *
 * ЭТО ЖЕ УСЛОВИЕ ПОКАЗА ЧЕКБОКСА НА ПОРТАЛЕ, и другого у него нет. Нарисованный шире чекбокс обещал
 * бы человеку денежное решение, за которым стоит 403; собранная порталом своя копия правила
 * разошлась бы с этой молча (правило одного места). Нарушение этого предиката — единственное, что
 * отвечает `403`/`422`: исход заявления отказом не бывает (`evaluateExemption` ниже).
 *
 * «Висящего предъявления нет» — тот же замок, что у самого предъявления: заявление едет ВМЕСТЕ с
 * командой предъявления, одной транзакцией (Р7), и при открытом ожидании предъявлять нечего.
 */
export function canDeclareExemption(
  row: Pick<
    ServiceActionRequest,
    'kind' | 'status' | 'serviceCounterpartyId' | 'estimatePendingRevision'
  >,
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
): boolean {
  if (!subject) return false;
  if (!serviceRequestNeedsEstimate(row)) return false;
  if (row.status !== 'in_work') return false;
  if (serviceEstimatePending(row)) return false;
  if (!actsForCounterparty(subject, 'service')) return false;
  return assignment.actsForAssignedCounterparty;
}

/**
 * ЧТО ВЫШЛО ИЗ ЗАЯВЛЕНИЯ (Р3). Отдельной функцией от «кому дано заявить» намеренно: то вопрос
 * доступа, и его нарушение даёт отказ; этот — вопрос ИСХОДА, и отказом он не отвечает никогда.
 *
 * ВХОДА ДВА, И ВТОРОЙ — ПАМЯТЬ СПОРА (Р9). Предварительных барьеров у денег заказчик не захотел ни
 * одного (ответы В2, В12 и В13 от 11.09.2026): остался рубильник `service_estimate_exemption`. Но
 * барьер ПОСЛЕ спора — не политика, а исполнение уже принятого решения: если «Ведение» разобрало
 * спор исходом «нужна подпись», подпись по этой заявке собирают, и заявление второй раз освобождать
 * от неё не может.
 *
 * ЧТО БЫЛО БЕЗ ВТОРОГО ВХОДА. Исход спора снимал сам подрядчик и без всякого разбора: возврат в
 * правку (`/estimate/reopen`) гасит ожидание, то же заявление по новой ревизии снова отвечало
 * `applied`, автоподпись вставала на место, работы закрывались, и автоприёмка через сутки принимала
 * заявку. Ни одной человеческой подписи, и требование спора не помнил никто. Функция обязана его
 * спросить — иначе весь разбор спора стоит один возврат в правку.
 *
 * Исходов по-прежнему ровно два, и отказом ни один из них не бывает: заявление со снятым
 * освобождением записывается как `observed` — след остаётся, подпись собирается обычным порядком.
 * Третий исход заводить нельзя: см. `SERVICE_ESTIMATE_EXEMPTION_OUTCOMES`.
 *
 * ОБА ПРИЗНАКА ПРИХОДЯТ ЗНАЧЕНИЯМИ, А НЕ ЧИТАЮТСЯ ЗДЕСЬ. Рубильник сервер знает по строке
 * `feature_flags`, портал — по ответу сессии (`hasFeature`); память спора сервер читает по таблице
 * споров, портал — по полю карточки `dispute`. Общего источника у них нет, и спроси функция базу
 * сама — портал не смог бы позвать её вовсе. От этого она остаётся чистой и проверяемой всеми
 * сочетаниями в четыре строки теста.
 *
 * ОБА ПОЛЯ ОБЯЗАТЕЛЬНЫ. Необязательное со значением по умолчанию `false` означало бы «о споре не
 * спросили» и «спора не было» одним и тем же ответом — то есть новый вызов, забывший про память
 * спора, молча вернул бы дыру, которую этот вход и закрывает.
 */
export function evaluateExemption(input: {
  flagEnabled: boolean;
  /** По заявке есть разрешённый спор с исходом «нужна подпись» (Р9): освобождение больше не даётся. */
  disputeRequiresSignature: boolean;
}): ServiceEstimateExemptionOutcome {
  if (input.disputeRequiresSignature) return 'observed';
  return input.flagEnabled ? 'applied' : 'observed';
}

/**
 * ПРИЗНАКИ ЗАЯВКИ, КОТОРЫМИ РЕШАЕТСЯ СПОР (Р9), — то, чего ни матрица прав, ни строка заявки не
 * знают: оба лежат в своих таблицах волны (`service_request_estimate_exemptions` и
 * `service_request_estimate_disputes`).
 *
 * Готовыми, а не чтением внутри предиката — по той же причине, что у `ServiceExecutorAssignment`:
 * спрашивают предикат двое, и «сходить в базу» умеет только сервер; портал читает их из карточки.
 */
export interface ServiceEstimateDisputeFacts {
  /**
   * Освобождение по действующей ревизии ПРИМЕНЕНО — исход `applied`, а не `observed` (Р3). Спорить о
   * наблюдённом заявлении не о чем: подпись по такой заявке и так собирают обычным порядком, и
   * заморозка лишь отняла бы у «Ведения» возможность её поставить.
   */
  exemptionApplied: boolean;
  /** Спор уже ведётся: второго по той же заявке не бывает — заявка и так остановлена. */
  disputeOpen: boolean;
}

/**
 * СТАТУСЫ, ИЗ КОТОРЫХ ОТКРЫВАЮТ СПОР, — «до приёмки» словами ответа В3 заказчика от 11.09.2026.
 *
 * Перечнем в контрактах, а не условием по месту: его спрашивают предикат ниже, ручка открытия спора
 * и матрица разрешения — ей важно, ОТКУДА спор открыт, потому что возврат в «В работе» из «Решена»
 * стёр бы факт закрытия, суммы и гарантии (`serviceResetOnTransition`, находка Н12).
 *
 * Принятой заявки здесь нет намеренно: приёмка — конец разбирательства, и спорить после неё значило
 * бы отменять чужое решение задним числом. Заморозки здесь нет по другой причине: остановленная
 * заявка уже стоит, и второй спор по ней не открывают.
 */
export const SERVICE_ESTIMATE_DISPUTE_OPEN_STATUSES = [
  'in_work',
  'done',
] as const satisfies readonly ServiceRequestStatus[];

/**
 * КТО ВПРАВЕ ОСПОРИТЬ ОСВОБОЖДЕНИЕ (Р9, ответ В3): «Ведение» — тем же правом `serviceRequests.assign`,
 * каким оно распределяет заявки и каким считается «ведёт ли субъект заявку»
 * (`canCoordinateServiceRequests`).
 *
 * ПОЧЕМУ НЕ ПРАВО СОГЛАСОВАНИЯ. Спор — не подпись и даже не её отсутствие: он останавливает заявку
 * и может увести её в «Отменена». Это ход того, кто ведёт заявку по циклу, а `approveEstimate`
 * бывает и у поимённого исполнителя (ответ В2 прошлой волны) — дай мы спор ему, исполнитель
 * оспаривал бы решение, принятое по его же заявке.
 *
 * Оператор контрагента-сервиса исключён явно и раньше права: освобождение заявил он, и спор с самим
 * собой — не контроль, а его имитация. Без этой строки право, попади оно в набор подрядчика, отдало
 * бы ему обе стороны сразу — ровно как в `isWaitingOn`.
 */
export function canOpenServiceEstimateDispute(
  row: Pick<ServiceActionRequest, 'kind' | 'status' | 'serviceCounterpartyId'>,
  subject: AccessSubject | null | undefined,
  facts: ServiceEstimateDisputeFacts,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  if (!serviceRequestNeedsEstimate(row)) return false;
  if (!SERVICE_ESTIMATE_DISPUTE_OPEN_STATUSES.some((status) => status === row.status)) return false;
  if (!facts.exemptionApplied) return false;
  if (facts.disputeOpen) return false;
  if (actsForCounterparty(subject, 'service')) return false;
  return has('serviceRequests.assign');
}

/**
 * КТО ВПРАВЕ РАЗРЕШИТЬ СПОР (Р9). Право то же, что у открытия, и это не совпадение: спор ведёт тот,
 * кто его начал, — иначе заявку отпускал бы мимо человека, знающего, о чём спорили (тот же довод,
 * что у пары «заморозка — возврат», `canResumeService`).
 *
 * Статус — только «Отложена», и спрашивается он, а не один признак `disputeOpen`: спор останавливает
 * заявку заморозкой с видом `estimate_exemption_dispute`, и открытый спор по заявке в рабочем
 * статусе означал бы рассогласование, которое надо увидеть отказом, а не разрешить вторым решением.
 */
export function canResolveServiceEstimateDispute(
  row: Pick<ServiceActionRequest, 'kind' | 'status' | 'serviceCounterpartyId'>,
  subject: AccessSubject | null | undefined,
  facts: Pick<ServiceEstimateDisputeFacts, 'disputeOpen'>,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  if (!serviceRequestNeedsEstimate(row)) return false;
  if (row.status !== 'on_hold') return false;
  if (!facts.disputeOpen) return false;
  if (actsForCounterparty(subject, 'service')) return false;
  return has('serviceRequests.assign');
}

/**
 * Согласовать объём работ либо не согласовать (`PATCH /:id/estimate/approval`). Спрашивают предикат
 * двое: кнопки под таблицей объёма работ и пункт меню «Действия», — и одно действие двумя входами
 * не дублирование ровно до тех пор, пока оба спрашивают это правило.
 *
 * Сторона согласующего — две ветки (Р3, ответ В2). Право `approveEstimate` видно по субъекту и даёт
 * «Ведение»; поимённый исполнитель по субъекту не виден и виден быть не может — «я в списке
 * назначенных» это свойство заявки, — и добирается он признаками назначения через
 * `isServiceExecutor`.
 *
 * Оператор контрагента-сервиса исключён явно и раньше обеих веток: объём работ предъявил он, и
 * подпись под собственным счётом — не согласование, а его копия.
 *
 * Признак Р4 стоит первым и у согласования — иначе историческое предъявление внутренней заявки
 * по-прежнему звало бы «Ведение» подписаться под ценами, которых по такой заявке не бывает.
 * Погасить его — работа закрытия (Р6), а не подписи.
 *
 * СТАТУС БОЛЬШЕ НЕ ОДИН, НО РАСШИРЕН НЕ ПО СТАТУСУ (Р9 плана
 * `docs/office-equipment-on-site-and-invoice-estimate-plan.md`): условие вынесено в
 * `allowsEstimateApprovalInStatus` — см. там, почему «Решена» пускает не всякое ожидание.
 */
export function canApproveServiceEstimate(
  row: Pick<
    ServiceActionRequest,
    | 'kind'
    | 'status'
    | 'serviceCounterpartyId'
    | 'estimatePendingRevision'
    | 'estimateRevision'
    | 'estimatePendingSource'
  >,
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  if (!serviceRequestNeedsEstimate(row)) return false;
  if (!allowsEstimateApprovalInStatus(row)) return false;
  if (!serviceEstimatePending(row)) return false;
  if (actsForCounterparty(subject, 'service')) return false;
  return isWaitingOn(subject, 'approval', has) || isServiceExecutor(subject, assignment, has);
}

/**
 * ГДЕ ПОДПИСЬ ВООБЩЕ БЫВАЕТ — половина условия согласования, вынесенная отдельно потому, что она
 * перестала быть одним сравнением статуса (Р9).
 *
 * «В работе» — как всегда. «Решена» — ТОЛЬКО постспорному ожиданию ЭТОЙ ревизии, и это прямое
 * требование плана: расширь мы предикат по статусу безусловно — он открыл бы согласование ЛЮБОМУ
 * ожиданию, случайно оказавшемуся в «Решена», включая наследственное у старых внутренних заявок.
 * Происхождение `dispute` ставит ровно одна дорога — исход «нужна подпись» у разрешённого спора.
 *
 * ПОЧЕМУ ПОДПИСЬ В «РЕШЕНА», А НЕ ВОЗВРАТ В «В РАБОТЕ». Возврат стирает факт закрытия, суммы и
 * гарантии матрицей сбросов (`serviceResetOnTransition`, находка Н12) — то есть отменяет
 * выполнение, которого никто не отменял. Из двух неприятностей выбрана та, что не теряет данные.
 *
 * СВЕРКА РЕВИЗИИ СТОИТ ЗДЕСЬ ВТОРЫМ ЗАМКОМ, хотя «предъявлена именно текущая ревизия» держит и
 * `CHECK` в базе: предикат спрашивают с карточки, которая могла быть открыта до переиздания
 * ревизии, — а подпись под ожиданием ПРОШЛОЙ ревизии и есть та самая подпись под цифрами, которых
 * больше нет. Пустой номер ревизии отвечает отказом (см. необязательные поля `ServiceActionRequest`).
 *
 * ВЫВЕДЕНА НАРУЖУ ПОТОМУ, ЧТО СПРАШИВАЮТ ЕЁ ДВОЕ: `canApproveServiceEstimate` (а через него портал)
 * и сама ручка согласования, которой нужен ТЕКСТ отказа раньше предиката. Оставь ручка своё
 * сравнение со «В работе» — коридор статусов получил бы второго носителя, и постспорная подпись
 * отбивалась бы 422 при предикате, отвечающем «да» (правило одного места: такие копии расходятся
 * молча, компилятор их не ловит).
 */
export function allowsEstimateApprovalInStatus(
  row: Pick<
    ServiceActionRequest,
    'status' | 'estimatePendingRevision' | 'estimateRevision' | 'estimatePendingSource'
  >,
): boolean {
  if (row.status === 'in_work') return true;
  if (row.status !== 'done') return false;
  if (row.estimatePendingSource !== 'dispute') return false;
  return row.estimateRevision !== undefined && row.estimatePendingRevision === row.estimateRevision;
}

/**
 * Вернуть объём работ в правку (`PATCH /:id/estimate/reopen`) — ключ от обоих замков Р9. Ручка
 * снимает ОБЕ отметки: снимок согласования (как и прежде) и само предъявление. Отсюда и
 * предусловие «есть что снимать» — подпись ЛИБО непогашенное предъявление: прежнего «согласование
 * есть» после Р9 мало, иначе отозвать собственное предъявление было бы нечем.
 *
 * Вид в условии больше не выводится из дизъюнкции (прежде он не назывался вовсе: у расходников не
 * бывает ни предъявления, ни подписи). После Р5 этого мало — у внутреннего ремонта обе отметки
 * встречаются исторически, — и признак Р4 стоит явной первой строкой: возвращать в правку объём
 * работ, которого по заявке не составляют, некому и незачем. Историческая ревизия остаётся видна
 * только на чтение (Р7).
 *
 * Withdrawn by the same side that submits it — the estimate's author (`actsAsEstimateAuthor`,
 * workflow-fixes plan Р6): a door the in-house executor of a mixed assignment may not open on the
 * way in must not open for him on the way back either.
 */
export function canReopenServiceEstimate(
  row: Pick<
    ServiceActionRequest,
    | 'kind'
    | 'status'
    | 'serviceCounterpartyId'
    | 'estimatePendingRevision'
    | 'approvedEstimateRevision'
  >,
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!serviceRequestNeedsEstimate(row)) return false;
  if (row.status !== 'in_work') return false;
  if (!serviceEstimatePending(row) && row.approvedEstimateRevision === null) return false;
  return actsAsEstimateAuthor(subject, assignment, has);
}

/**
 * ВЕДЁТ ЛИ СУБЪЕКТ ЗАЯВКИ — то есть положены ли ему пояснения в интерфейсе (Р11 плана
 * `office-equipment-card-and-list-cleanup-plan.md`).
 *
 * Право то же самое, что у распределения, и это не совпадение: заявку ведёт тот, кто её раздаёт, —
 * «Ведение» и ИТ-служба. Заявитель, внутренний исполнитель и оператор подрядчика видят одну
 * заявку и своё действие в ней, и предупреждать их о состоянии чужой очереди не о чем: заказчик
 * просил убрать именно эти плашки (просьба 08.09.2026, п. 3).
 *
 * Своей функцией, а не строкой `can(subject, 'serviceRequests.assign')` по компонентам: мест,
 * которые её спрашивают, одиннадцать (§2.3 плана), и правило «кому положены пояснения» обязано
 * меняться в одном месте. Разложенное по вызовам, оно переехало бы наполовину — и половина
 * подсказок вернулась бы тем, у кого их убирали.
 *
 * Действия эта функция не открывает НИ ОДНОГО и открывать не должна: она отвечает про подачу
 * текста, а не про права. Всё, что решает доступ, спрашивает свой предикат выше.
 */
export function canCoordinateServiceRequests(
  subject: AccessSubject | null | undefined,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  return has('serviceRequests.assign');
}

// ── Кого ждут ──

/**
 * От кого сейчас ждут шага. Значения только те, у кого шаг в цикле есть: заказчик решений не
 * принимает — приёмку делает «Ведение», — поэтому `customer` здесь не заводится. Появится шаг
 * заказчика (например, подтверждение стоимости площадкой) — появится и значение вместе с веткой
 * предиката.
 */
export const SERVICE_WAITING_ON = [
  // Виза упразднена (Р10), и очередь эту сторону больше не возвращает ни при каком статусе:
  // `serviceRequestWaitingOn` не отвечает `it` ни в одном статусе при любых признаках заявки.
  // Уборка права `serviceRequests.approveIt` из наборов (Э9 плана профилей, миграция E) значение
  // не тронула, и обещать иное здесь больше нечем: перечень — форма ответа сервера, а не состав
  // полномочий, и снимать из него значение нужно вместе с подписью, ветвью `isWaitingOn` и
  // разбором ответа на портале — отдельной правкой контракта, а не довеском к каталогу.
  'it',
  'operator',
  // Согласование объёма работ (Р3): своя сторона, потому что согласующий не совпадает ни с одной
  // старой. Подробности — в ветке `isWaitingOn` ниже.
  'approval',
  'service',
  'hold',
  'nobody',
] as const;
export type ServiceWaitingOn = (typeof SERVICE_WAITING_ON)[number];

export const serviceWaitingOnLabels: Record<ServiceWaitingOn, string> = {
  it: 'Ждёт ИТ',
  operator: 'Ждёт оператора',
  // Не «Ждёт оператора» и не «Ждёт Ведения»: подпись читают в списке, где важно не КТО подписывает,
  // а чего заявка ждёт, — работы по ней стоят до ответа на предъявленный объём.
  approval: 'Ждёт согласования',
  service: 'Ждёт исполнителя',
  // Не «Никого»: отложенную заявку ждёт решение оператора, но не шаг цикла — очередь и бейдж её не
  // считают (Р111), а человеку в списке нужна причина остановки, а не пустая клетка.
  hold: 'Отложена',
  nobody: 'Закрыта',
};

/**
 * Строка заявки, по которой считается очередь. Три поля, а не один статус: после слияния (Р2) оба
 * рабочих статуса отвечают на «кого ждут» по-разному — «Новая» по составу исполнителей, «В работе»
 * по непогашенному предъявлению.
 *
 * `hasExecutors` приходит ГОТОВЫМ, а не парой «контрагент + число строк», как в
 * `ServiceExecutorsRow`: очередь считается не только по карточке, но и запросом по всему списку, где
 * этот признак — `EXISTS` по `service_request_executors`, а не поле строки. Тот же приём, что у
 * `ServiceExecutorAssignment`: чего в самой строке нет, предикат принимает посчитанным.
 *
 * Ревизии сметы и визы ИТ отсюда ушли: третья ось очереди упразднена вместе с самой визой (Р10), а
 * предъявление называет своя колонка.
 */
export interface ServiceWaitingRequest {
  status: ServiceRequestStatus;
  /** У заявки есть исполнители: `serviceHasExecutors` по карточке либо `EXISTS` в запросе списка. */
  hasExecutors: boolean;
  /**
   * ДЕЙСТВУЮЩЕЕ ожидание подписи под объёмом работ — уже посчитанное
   * `serviceRequestHasEffectivePendingEstimate`, а не колонка `estimate_pending_revision` (Р7, Н16).
   *
   * Ось у очереди осталась одна, и это её единственно возможная форма. SQL не зовёт
   * TypeScript-функцию на колонках, а третью ось перебора очередь получить не может: маски
   * выводятся сплошным перебором сочетаний, и каждая новая ось удваивает их число. Поэтому
   * вызывающий приводит обе половины к одному булеву — карточка признаком Р4, builder очередей
   * парой SQL-выражений, — и расхождение «в карточке ждут исполнителя, в списке согласования»
   * становится невозможным по построению.
   *
   * Тот же приём, что у `hasExecutors` рядом: чего в строке нет готовым, предикат принимает
   * посчитанным.
   */
  estimatePending: boolean;
}

/**
 * Стоит ли виза ИТ на **текущей** ревизии сметы. Функция ЧИТАЕТ ИСТОРИЮ и больше ничего не решает
 * (Р10): виза упразднена, ручка `it-approval` снята, очередь про неё не спрашивает, коридора визы
 * нет вовсе (Э9). Осталась она потому, что поля `it_approved_*` никуда не делись —
 * подпись от 22.08 правдива, стирать её нечем, — и карточка обязана показать, к той же ревизии она
 * относится или к позапрошлой.
 *
 * Равенство, а не «не меньше»: подпись под позапрошлой ревизией означала бы согласие с ценами,
 * которых в смете уже нет, — а предъявление поднимает ревизию именно затем, чтобы подписи перестали
 * относиться к делу.
 */
export function hasCurrentItApproval(row: {
  estimateRevision: number;
  itApprovedEstimateRevision: number | null;
}): boolean {
  return (
    row.itApprovedEstimateRevision !== null &&
    row.itApprovedEstimateRevision === row.estimateRevision
  );
}

/**
 * Кого ждёт заявка — таблица Р2 целиком. Заморозка, приёмка и закрытые статусы отвечают по себе и в
 * таблицу не попадают: признаки спрашиваются только там, где заявка движется.
 */
export function serviceRequestWaitingOn(row: ServiceWaitingRequest): ServiceWaitingOn {
  switch (row.status) {
    // «Новая» отвечает составом исполнителей — ровно тем, что означала «Назначена»: никого не
    // назначили — ждут распределения; назначили — ждут, что за заявку возьмутся (Р6).
    case 'new':
      return row.hasExecutors ? 'service' : 'operator';
    // «В работе» отвечает предъявлением: висит — ждут подписи под объёмом работ (Р3); не висит —
    // ждут самих работ. Третьей оси — визы ИТ — здесь больше нет, она ушла вместе с визой (Р10).
    // Признак читается готовым: «действует ли предъявление» решил вызывающий (Р7), потому что
    // ответ зависит от вида и исполнителя заявки, которых у строки очереди нет.
    case 'in_work':
      return row.estimatePending ? 'approval' : 'service';
    // Приёмка — ход «Ведения»: работу предъявили, и ждут, когда её примут.
    case 'done':
      return 'operator';
    // Мёртвые статусы (0197, 0224) в `switch` остаются: заявок в них нет, но функцию зовут и на
    // строках истории, где они встречаются законно, — а `switch` обязан покрыть весь тип. Отвечают
    // они тем же, чем ответил бы их живой эквивалент, чтобы старая строка не выглядела ничьей.
    case 'it_approved':
      return 'operator';
    case 'estimate_review':
      return 'approval';
    case 'assigned':
    case 'diagnostics':
      return 'service';
    // Заморозка снимает заявку со всех очередей: ход не за исполнителем и не за оператором — она
    // просто стоит (Р111).
    case 'on_hold':
      return 'hold';
    case 'accepted':
    case 'cancelled':
      return 'nobody';
  }
}

/**
 * @deprecated Отвечает по одному статусу и потому не различает ни «Новую» с исполнителями, ни «В
 * работе», ждущую подписи (Р2): обеим она даёт ответ нетронутой заявки. Пользуйтесь
 * `serviceRequestWaitingOn`; здесь функция дожидается снятия последних вызовов.
 */
export function serviceWaitingOn(status: ServiceRequestStatus): ServiceWaitingOn {
  return serviceRequestWaitingOn({ status, hasExecutors: false, estimatePending: false });
}

/**
 * Ждут ли сейчас этого субъекта. Сравнивать `waitingOn` с ролью напрямую нельзя: у оператора
 * оргтехники роль — «Штаб» или «Отдел» (сторону задаёт надстройка), а у сервиса роль — «Оператор
 * (внешний исполнитель)» (сторону задаёт тип контрагента). Поэтому сторона определяется правами.
 */
export function isWaitingOn(
  subject: AccessSubject | null | undefined,
  waiting: ServiceWaitingOn,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  // Отложенная не в очереди «Ждут меня» и не в бейдже раздела (Р111): оператор вернётся к ней
  // сам, когда решится причина, а до тех пор она стояла бы первой строкой у всех, кого «ждёт».
  if (!subject || waiting === 'nobody' || waiting === 'hold') return false;
  // Поимённый исполнитель в эту ветку не попадает и попасть не может: «я в списке назначенных» —
  // свойство заявки, а не субъекта, и очередь отбирает такие заявки соединением с
  // `service_request_executors` (волна В3). Здесь остаётся сторона подрядчика, которую видно по
  // самому субъекту.
  if (waiting === 'service') return actsForCounterparty(subject, 'service');
  if (waiting === 'it') return has('serviceRequests.approveIt');
  /*
   * Согласование объёма работ (Р3). Своей веткой, а не строкой в `operator`, потому что сторона
   * `operator` определяется правом РАСПРЕДЕЛЕНИЯ (`assign` ниже), а согласует по ответу В2
   * назначенный сотрудник: оставь мы согласование там, ИТ-служба видела бы в «Ждут меня» чужие
   * подписи, а «Ведение» — свои, но по неверному основанию.
   *
   * Поимённый исполнитель в эту ветку не попадает и попасть не может по той же причине, что в
   * `service`: «я в списке назначенных» — свойство заявки, а не субъекта, и в очередь он приходит
   * соединением с `service_request_executors` на стороне сервера (`waitingOnMeWhere`). Здесь
   * остаётся сторона, видная по самому субъекту, — «Ведение».
   *
   * Оператор контрагента-сервиса исключён явно: объём работ предъявил он, и подпись под собственным
   * счётом — не согласование, а его копия. Без этой строки право `serviceRequests.approveEstimate`,
   * попади оно в набор подрядчика, отдало бы ему обе стороны сразу.
   */
  if (waiting === 'approval') {
    if (actsForCounterparty(subject, 'service')) return false;
    return has('serviceRequests.approveEstimate');
  }
  return has('serviceRequests.assign');
}

// ── Номер заявки ──

/** Префикс сквозного номера: «М-» у мусора, «ТС-» у техники, «СО-» у сервисного обслуживания. */
export const SERVICE_REQUEST_NUMBER_PREFIX = 'СО-';

export function formatServiceRequestNumber(num: number): string {
  return `${SERVICE_REQUEST_NUMBER_PREFIX}${num}`;
}

/**
 * Номер из строки поиска: человек ищет и «СО-14», и «со-14», и просто «14». Возвращает `null`,
 * если это не номер, — тогда поиск идёт обычным текстом по модели и номерам техники.
 */
export function parseServiceRequestNumberSearch(search: string): number | null {
  const trimmed = search.trim().replace(/^со-?/i, '');
  if (!/^\d{1,9}$/.test(trimmed)) return null;
  const num = Number(trimmed);
  return num > 0 ? num : null;
}

// ── Смета ──

// ── Словарь волны «освобождение от подписи и объём работ документом» ──
//
// План `docs/office-equipment-on-site-and-invoice-estimate-plan.md`, решения Р2–Р6, Р9 и Р11. Все
// перечни волны стоят здесь, а не каждый рядом со своей схемой, по правилу одного места: тот же
// состав значений обязан совпасть с `CHECK` трёх новых таблиц, с SQL-условием закрывающего
// документа в очереди и автозакрытии и с подписями портала. Разойдись копии — заявка, законная для
// сервера, оказалась бы незаконной для очереди, и заметил бы это человек, а не прогон.

/**
 * ФОРМАТ РЕВИЗИИ ОБЪЁМА РАБОТ (Р2, Р4): чем именно предъявлен объём работ.
 *
 * ХРАНИТСЯ, А НЕ ВЫВОДИТСЯ ИЗ ДАННЫХ. Угадать формат по строке заявки нельзя ни одним признаком:
 * «строк ноль» бывает и у документной ревизии, и у построчной, которую ещё не наполнили; «файл
 * подшит» — у любой заявки с вложением. И главное: раскладка «Ведения» переиздаёт документную
 * ревизию в построчную (ADR 0179), и признак обязан это пережить — выведенный из данных, он соврал
 * бы на первой же раскладке.
 *
 * `warranty` — не «пустая смета», а осознанное «чиним по гарантии, денег нет» (Р27 ADR 0085): у
 * такой ревизии одна служебная строка с нулевой суммой. `document` — счёт БЕЗ СТРОК И БЕЗ СУММЫ
 * (ответ В5 заказчика от 11.09.2026): сумма не опущена, а неизвестна до разбора документа, и ноль
 * вместо неё читался бы как «работы бесплатны».
 */
export const SERVICE_ESTIMATE_FORMATS = ['items', 'document', 'warranty'] as const;
export const serviceEstimateFormatSchema = z.enum(SERVICE_ESTIMATE_FORMATS);
export type ServiceEstimateFormat = (typeof SERVICE_ESTIMATE_FORMATS)[number];

/**
 * Подписи форматов — карточке, списку и истории. Словами, а не именами значений: формат читает
 * человек, и он же выбирает его переключателем окна объёма работ (Р10).
 */
export const serviceEstimateFormatLabels: Record<ServiceEstimateFormat, string> = {
  items: 'Строками',
  document: 'Документом (счёт)',
  warranty: 'Гарантийный ремонт',
};

/**
 * СОСТОЯНИЕ РЕВИЗИИ (Р6, п. 3): ошибочно поданный документ не снимается и не удаляется — прежняя
 * ревизия становится `superseded`, а действующей делается новая.
 *
 * ЗАЧЕМ СОСТОЯНИЕ, ЕСЛИ ЕСТЬ НОМЕР. Номер отвечает «какая по счёту», состояние — «на чём денежное
 * решение стоит СЕЙЧАС», и это разные вопросы: отозванная через `reopen` ревизия остаётся тем, на
 * чём решение стояло, и затирать её нельзя (Р6). На состоянии же держится частичный уникальный
 * индекс «одна активная ревизия на заявку» — без него две активные разошлись бы молча, и предикат
 * закрывающего документа читал бы формат по жребию.
 */
export const SERVICE_ESTIMATE_REVISION_STATES = ['active', 'superseded'] as const;
export const serviceEstimateRevisionStateSchema = z.enum(SERVICE_ESTIMATE_REVISION_STATES);
export type ServiceEstimateRevisionState = (typeof SERVICE_ESTIMATE_REVISION_STATES)[number];

/**
 * ИСХОД ЗАЯВЛЕНИЯ ОБ ОСВОБОЖДЕНИИ (Р3): что вышло из галочки «Согласование не требуется».
 *
 * ИСХОДОВ РОВНО ДВА, И ТРЕТЬЕГО ЗАВОДИТЬ НЕЛЬЗЯ. Прежняя редакция плана знала `fallback_to_human`
 * («сумма больше лимита — собираем подпись»), но заказчик отказался от лимита вовсе (ответ В2 от
 * 11.09.2026). Ветка, которую не включают никакие данные, — мёртвое плечо: его не проверяет ни один
 * тест, а первое же ревью принимает его за денежный контроль, которого у освобождения нет (Р13).
 *
 * `observed` — не отказ, а НАБЛЮДЕНИЕ: предъявление проходит, заявление остаётся в следе, ожидание
 * подписи открывается как обычно. Этим служба и узнаёт ДО включения рубильника, сколько заявлений
 * приходит и на какие суммы; отвечай выключенный рубильник отказом, такой картины не было бы вовсе.
 */
export const SERVICE_ESTIMATE_EXEMPTION_OUTCOMES = ['applied', 'observed'] as const;
export const serviceEstimateExemptionOutcomeSchema = z.enum(SERVICE_ESTIMATE_EXEMPTION_OUTCOMES);
export type ServiceEstimateExemptionOutcome = (typeof SERVICE_ESTIMATE_EXEMPTION_OUTCOMES)[number];

/**
 * Исход — словами человека, а не именем значения: по этой строке «Ведение» понимает, собирают по
 * заявке подпись или уже нет (Р13: тег в списке и строка в карточке — два из четырёх механизмов
 * контроля постфактум).
 */
export const serviceEstimateExemptionOutcomeLabels: Record<
  ServiceEstimateExemptionOutcome,
  string
> = {
  applied: 'Принято без согласования',
  observed: 'Заявлено, ждём подписи',
};

/**
 * SOURCE OF THE SIGNATURE under a revision (Р11): a person signed, or the automatic acceptance did.
 *
 * Present exactly when the request is signed (release B, ADR 0183): the database pairs the two
 * (`service_requests_estimate_approval_source_check`). Release A had to read an empty source as
 * `human` — code that knew nothing about the column signed in its rollout window — and that rule is
 * gone with the pair: a reader takes the value as the server sends it.
 */
export const SERVICE_ESTIMATE_APPROVAL_SOURCES = ['human', 'auto'] as const;
export type ServiceEstimateApprovalSource = (typeof SERVICE_ESTIMATE_APPROVAL_SOURCES)[number];

export const serviceEstimateApprovalSourceLabels: Record<ServiceEstimateApprovalSource, string> = {
  human: 'Согласовано человеком',
  auto: 'Принято без согласования: заявил оператор сервиса',
};

/**
 * ПРОИСХОЖДЕНИЕ ОЖИДАНИЯ ПОДПИСИ (Р9): ожидание открыло предъявление или исход разрешённого спора.
 *
 * ЗАЧЕМ ОНО ЗАВЕДЕНО. Подпись в «Решена» — новая дверь (сегодня `canApproveServiceEstimate` пускает
 * только «В работе»), и расширить предикат по одному статусу было бы дырой: в «Решена» законно
 * доживает НАСЛЕДСТВЕННОЕ ожидание старых внутренних заявок, и его открыли бы тоже. Происхождение
 * отвечает на единственный вопрос, который здесь важен: это ожидание создано разрешением спора —
 * или просто досталось заявке от прошлого.
 */
export const SERVICE_ESTIMATE_PENDING_SOURCES = ['submit', 'dispute'] as const;
export type ServiceEstimatePendingSource = (typeof SERVICE_ESTIMATE_PENDING_SOURCES)[number];

/** Состояние спора (Р9): спор либо ведётся, либо разрешён — и тогда у него есть исход. */
export const SERVICE_ESTIMATE_DISPUTE_STATES = ['open', 'resolved'] as const;
export type ServiceEstimateDisputeState = (typeof SERVICE_ESTIMATE_DISPUTE_STATES)[number];

/**
 * ИСХОД СПОРА (Р9) — три, и каждый означает своё для статуса заявки:
 *   · `keep` — освобождение остаётся, заявка возвращается туда, откуда её остановили;
 *   · `require_signature` — автоподпись снята, ожидание подписи открыто с происхождением `dispute`;
 *   · `cancel` — заявка в «Отменена», причина обязательна, а факт и документы остаются: отмена их
 *     не стирает, и переписывать историю мы не будем.
 * Сама матрица «откуда открыт × исход» живёт на сервере: она про статусы и сбросы, а не про словарь.
 */
export const SERVICE_ESTIMATE_DISPUTE_OUTCOMES = ['keep', 'require_signature', 'cancel'] as const;
export const serviceEstimateDisputeOutcomeSchema = z.enum(SERVICE_ESTIMATE_DISPUTE_OUTCOMES);
export type ServiceEstimateDisputeOutcome = (typeof SERVICE_ESTIMATE_DISPUTE_OUTCOMES)[number];

export const serviceEstimateDisputeOutcomeLabels: Record<ServiceEstimateDisputeOutcome, string> = {
  keep: 'Оставить освобождение',
  require_signature: 'Нужна подпись',
  cancel: 'Отменить заявку',
};

/**
 * ВИД ЗАМОРОЗКИ: сегодня заморозка не различает «ждём запчасть» и «спорим о счёте» (Н11), а
 * различать обязана — при споре заперты возврат из заморозки, закрытие работ, приёмка и
 * автозакрытие, и запирать их у заявки, ждущей запчасть, было бы неверно.
 *
 * ПЕРЕЧНЕМ, А НЕ ОДНОЙ КОНСТАНТОЙ: состав обязан совпасть с `CHECK` колонки
 * `service_requests.hold_kind`, а второй вид заморозки план называет возможным продолжением.
 * Пустой вид — обычная заморозка, какой она была всегда, и этим держится окно выката: старый код
 * колонки не знает и оставляет её пустой.
 */
export const SERVICE_HOLD_KINDS = ['estimate_exemption_dispute'] as const;
export type ServiceHoldKind = (typeof SERVICE_HOLD_KINDS)[number];

/** Вид заморозки, которой заявку останавливают на время спора об освобождении (Р9). */
export const SERVICE_ESTIMATE_DISPUTE_HOLD_KIND =
  'estimate_exemption_dispute' satisfies ServiceHoldKind;

export const SERVICE_ITEM_KINDS = ['part', 'service'] as const;
export const serviceItemKindSchema = z.enum(SERVICE_ITEM_KINDS);
export type ServiceItemKind = (typeof SERVICE_ITEM_KINDS)[number];

export const serviceItemKindLabels: Record<ServiceItemKind, string> = {
  part: 'Запчасть',
  service: 'Услуга',
};

/** Служебная строка гарантийного ремонта (Р27): её не набирают руками, но она должна быть узнаваема. */
export const WARRANTY_REPAIR_ITEM_NAME = 'Гарантийный ремонт';

/**
 * Наименование строки объёма работ: до 2000 символов, переводы строк внутри разрешены (`trim`
 * снимает пробельное только по краям).
 *
 * ПОЧЕМУ ПРЕДЕЛ ВЫРОС С 255 (план `docs/office-equipment-free-estimate-and-executor-scope-plan.md`,
 * Р1; ответ В1 заказчика от 09.09.2026). Подрядчик присылает объём работ письмом — одной записью,
 * перечнем позиций с количествами: «Ремонт МФУ (RICOH MP C 2011 …); Узел фотобарабана в сборе …,
 * 2 шт; Блок проявки в сборе …» и так далее, около 370 символов на пять строк, а цены по позициям
 * не разложены вовсе. Свободный режим ввода кладёт этот перечень в ОДНУ строку сметы
 * (`kind = service`, `quantity = 1`, `unitPrice` = общая стоимость): второго источника суммы в
 * модели не заводится — итог, согласование, акт, факт, реестр гарантий и разбор спора читают
 * строки, и вторая дорога заставила бы каждое из шести мест отвечать, какой источник главный.
 * При пределе 255 письмо в эту строку просто не влезало: человек резал бы перечень руками, то есть
 * терял бы предмет согласования — ровно то, под чем потом стоит подпись.
 *
 * ПЕРЕНОСЫ СТРОК — ЧАСТЬ ЗНАЧЕНИЯ, А НЕ МУСОР. Перечень читают глазами там же, где согласуют, и
 * склеенный в одну строку он становится нечитаемым в том самом месте, ради которого заведён.
 * Поэтому схема их не режет, а таблица показывает наименование с сохранением переносов
 * (`white-space: pre-line`).
 *
 * ОГРАНИЧЕНИЕ ЖИВЁТ ТОЛЬКО ЗДЕСЬ, И МИГРАЦИИ ПОД НЕГО НЕТ. Колонка `service_request_items.name` —
 * `text` без длины, а единственный `CHECK` на ней требует непустоты (`btrim(name) <> ''`), не
 * длины. То есть 255 никогда не было свойством хранилища — это была рамка формы, и поднимается она
 * правкой одной строки контрактов, без окна выката. Обратная сторона названа вслух: сузить предел
 * обратно тоже можно без миграции, но уже нельзя безопасно — в базе к тому времени лежат записи
 * длиннее, и схема начала бы отбивать правку строк, которые сама же и приняла.
 */
const itemNameSchema = z.string().trim().min(2, 'Укажите наименование').max(2000);
const quantitySchema = z.number().positive('Количество больше нуля').max(9999);
const priceSchema = z.number().min(0, 'Цена не может быть отрицательной').max(99_999_999);

export const serviceEstimateItemSchema = z.object({
  kind: serviceItemKindSchema,
  name: itemNameSchema,
  quantity: quantitySchema.default(1),
  unitPrice: priceSchema,
  /** Что обещали: срок гарантии в месяцах. Дата считается при закрытии от даты выполнения работ. */
  warrantyMonths: z.number().int().min(1).max(120).nullish(),
});
export type ServiceEstimateItemInput = z.infer<typeof serviceEstimateItemSchema>;

/**
 * Состав сметы передаётся целиком, а не построчно: смета — документ, и «добавить строку» без
 * остальных строк не имеет смысла. Пустой список разрешён (черновик), а вот предъявить пустую
 * смету нельзя — это проверяет сервер при переходе.
 */
export const putServiceEstimateSchema = z.object({
  items: z.array(serviceEstimateItemSchema).max(200),
  version: z.number().int().nonnegative(),
});
export type PutServiceEstimateInput = z.infer<typeof putServiceEstimateSchema>;

/**
 * Раскладка свободной записи по графам (`PUT /:id/estimate/breakdown`; план
 * `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р2; ответ В9 заказчика от
 * 09.09.2026). По составу тело то же, что у `putServiceEstimateSchema`, — и это не повод
 * переиспользовать её схему.
 *
 * ЧЕМ ОТЛИЧАЕТСЯ ОТ СОСЕДКИ. Разные держатели, разная дверь и разные последствия. Ту открывает
 * `serviceRequests.estimate` — АВТОР объёма работ, исполнитель, — и правит он черновик: состав
 * заменяется, номер ревизии и подписи не трогаются. Эту открывает
 * `serviceRequests.estimateRewrite` — «Ведение», которое переносит присланный подрядчиком перечень
 * в графы, — и по СОГЛАСОВАННОЙ ревизии она делает другое: поднимает номер, снимает подпись,
 * пересчитывает итог и ставит новое предъявление, потому что подписанное содержимое под прежней
 * ревизией не меняется (ADR 0133). Одна схема на две двери означала бы, что тело запроса перестало
 * отличать правку черновика от переиздания документа.
 *
 * `.strict()` — НЕ УКРАШЕНИЕ, А ПЛАТА ЗА УЖЕ СЛУЧИВШИЙСЯ ДЕФЕКТ. Молчаливое отбрасывание
 * неизвестных полей приёмом модуля стоило ровно одного разбора: портал шлёт поле, сервер про него
 * не знает, ответ 200 — и человек уверен, что сохранил то, чего не сохранилось. У ручки, которая
 * снимает согласование и требует новой подписи, такая тишина дороже всего: опечатка в имени поля
 * прошла бы как успешная раскладка, а заявка ушла бы на второй круг подписей ни за чем.
 *
 * Предел строк тот же (200) — это тот же документ, и разложенная смета не бывает длиннее набранной
 * руками. Пустой список схемой разрешён, как и у соседки: что делать с «разложить в ноль строк»,
 * решает ручка — у неё для этого есть статус заявки и состояние ревизии, а у тела их нет.
 */
export const putServiceEstimateBreakdownSchema = z
  .object({
    items: z.array(serviceEstimateItemSchema).max(200),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type PutServiceEstimateBreakdownInput = z.infer<typeof putServiceEstimateBreakdownSchema>;

// ── Строки заявки на расходники (Н9, выпуск 3) ──
//
// Своим набором схем, а не расширением сметы: у строки расходника нет ни цены, ни суммы, ни
// гарантии — картридж берут со своего склада, и согласовывать по нему нечего. Общего у двух
// списков ровно одно слово «строка», и склеенная схема заставляла бы каждую половину объяснять,
// почему у неё пустует чужая половина полей.

/**
 * Сколько штук просят и сколько выдали. Верхняя граница — не учётное правило, а защита от опечатки
 * (тот же приём, что у остатка в справочнике): заявок на тысячу картриджей у ИТ-службы не бывает, а
 * «120» вместо «12» в поле ввода от правды ничем не отличается.
 */
const consumableQuantitySchema = z
  .number()
  .int('Количество — целое число')
  .max(1000, 'Проверьте количество: слишком большое число');

/**
 * Причина расхождения факта с запрошенным. Живёт в самой строке заявки, а не только в событии
 * журнала: её читают в карточке заявки и в отчёте по расходу, а событие журнала — это склад.
 */
const issueNoteSchema = z.string().trim().max(500);

/** Строка запроса: позиция справочника и сколько её просят. */
export const serviceConsumableLineSchema = z.object({
  consumableId: uuidSchema,
  requestedQuantity: consumableQuantitySchema.positive('Количество больше нуля'),
});
export type ServiceConsumableLineInput = z.infer<typeof serviceConsumableLineSchema>;

/**
 * Состав строк передаётся целиком (`PUT /:id/consumables`), как и смета: это список того, что
 * просят, и «добавить одну позицию» без остальных заставляло бы сервер угадывать, снимали ли
 * что-то.
 *
 * Пустой список запрещён, в отличие от черновика сметы: заявка на расходники без строк — это заявка
 * без предмета, и ни одного состояния, в котором она законно пуста, у неё нет.
 *
 * Повторы позиции отбиваются здесь, а не уникальным ключом БД: две строки «Тонер Ricoh 201» — это
 * не два расходника, а ошибка формы, и человек обязан прочитать про неё словами, а не получить
 * `23505`.
 */
export function addConsumableDuplicateIssues(
  items: readonly { consumableId: string }[],
  ctx: z.RefinementCtx,
  basePath: (string | number)[] = [],
): void {
  const seen = new Set<string>();
  items.forEach((item, index) => {
    if (seen.has(item.consumableId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Позиция уже есть в заявке — измените количество в её строке',
        path: [...basePath, index, 'consumableId'],
      });
    }
    seen.add(item.consumableId);
  });
}

export const putServiceConsumablesSchema = z.object({
  items: z
    .array(serviceConsumableLineSchema)
    .min(1, 'Добавьте хотя бы одну позицию')
    .max(50)
    .superRefine((items, ctx) => addConsumableDuplicateIssues(items, ctx)),
  version: z.number().int().nonnegative(),
});
export type PutServiceConsumablesInput = z.infer<typeof putServiceConsumablesSchema>;

/**
 * Факт по одной строке: сколько выдали и почему это не то, что просили.
 *
 * `issuedQuantity` присылается всегда, в том числе нулём: ноль — это «съездили, тонер оказался цел»
 * (В9б), законный исход закрытия, а не «поле не заполнили». Пустого значения у ручки нет вовсе —
 * незаполненный факт означает «работу не закрывали», и вернуть строку в это состояние правкой
 * нельзя: событие журнала уже случилось.
 */
export const serviceConsumableIssueSchema = z.object({
  /** Идентификатор СТРОКИ заявки, а не позиции справочника: правят конкретную строку. */
  id: uuidSchema,
  issuedQuantity: consumableQuantitySchema.min(0, 'Выдано не может быть отрицательным'),
  issueNote: issueNoteSchema.optional().default(''),
});
export type ServiceConsumableIssueInput = z.infer<typeof serviceConsumableIssueSchema>;

/**
 * Правка факта выдачи (`PATCH /:id/consumables/issued`, Р6). Приходят только те строки, которые
 * меняют, — отсюда и `PATCH`: состав заявки эта ручка не трогает, она двигает склад событием на
 * разницу по каждой названной строке.
 */
export const setServiceConsumablesIssuedSchema = z.object({
  items: z.array(serviceConsumableIssueSchema).min(1).max(50),
  version: z.number().int().nonnegative(),
});
export type SetServiceConsumablesIssuedInput = z.infer<typeof setServiceConsumablesIssuedSchema>;

/**
 * Причина обязательна при ЛЮБОМ расхождении факта с запрошенным — и когда выдали больше (В9а), и
 * когда меньше, и когда ноль (В9б). Совпал факт с заявкой — объяснять нечего, и требовать текст
 * значило бы заставлять писать «всё как просили» две тысячи раз в год.
 *
 * Функцией, а не схемой на месте, по той же причине, что `urgencyIssue`: в теле запроса лежит
 * половина пары — сколько выдали, — а вторая половина, сколько просили, живёт в строке заявки.
 * Считать условие можно только по склеенному состоянию, и делает это сервер. Тот же ответ обязан
 * получаться у портала, который считает его до отправки формы, — поэтому правило одно и лежит здесь,
 * а `CHECK` `service_request_consumables_note_check` в базе повторяет его последним рубежом.
 */
export function serviceConsumableIssueIssue(line: {
  requestedQuantity: number;
  issuedQuantity: number;
  issueNote: string;
}): string | null {
  if (line.issuedQuantity === line.requestedQuantity) return null;
  if (line.issueNote.trim()) return null;
  if (line.issuedQuantity === 0) return 'Объясните, почему ничего не выдали';
  return line.issuedQuantity > line.requestedQuantity
    ? 'Объясните, почему выдали больше, чем просили'
    : 'Объясните, почему выдали меньше, чем просили';
}

// ── Гарантийное обращение ──

export const WARRANTY_CLAIM_SOURCES = ['equipment', 'item'] as const;
export const warrantyClaimSourceSchema = z.enum(WARRANTY_CLAIM_SOURCES);
export type WarrantyClaimSource = (typeof WARRANTY_CLAIM_SOURCES)[number];

export const warrantyClaimSourceLabels: Record<WarrantyClaimSource, string> = {
  equipment: 'Гарантия на технику',
  item: 'Гарантия на прошлый ремонт',
};

/**
 * Источник гарантийного обращения: не флаг «гарантийная», а ответ на вопрос, по чьей гарантии
 * обращаются. Без него спор с сервисом не разрешить — «гарантийная заявка» ничего не подтверждает.
 *
 * У источника `item` обязательна ссылка на строку сметы прошлой заявки, у `equipment` её быть не
 * должно: гарантия поставщика висит на самой единице.
 */
export const warrantyClaimSchema = z
  .object({
    source: warrantyClaimSourceSchema.nullish(),
    itemId: uuidSchema.nullish(),
  })
  .refine((v) => v.source !== 'item' || !!v.itemId, {
    message: 'Укажите, по какой позиции прошлого ремонта обращаетесь',
    path: ['itemId'],
  })
  .refine((v) => v.source !== 'equipment' || !v.itemId, {
    message: 'У гарантии на технику позиции ремонта не бывает',
    path: ['itemId'],
  });

// ── Заявка ──

export const SERVICE_REQUEST_SORT_FIELDS = [
  'num',
  'status',
  'equipment',
  'object',
  'service',
  'statusChangedAt',
  'createdAt',
] as const;

// Сообщение схемы нейтрально к виду заявки (Р2): одна и та же схема проверяет и обслуживание, и
// расходники, а «Опишите неисправность» в ответ на заявку про закончившийся тонер читалось как
// придирка не по адресу. Заодно слова «неисправность» не остаётся и в ответах API — критерий
// просьбы 7 про портал целиком, а не только про подписи на экране.
const descriptionSchema = z.string().trim().min(5, 'Опишите, о чём заявка').max(4000);

/** Что режиму «предыдущие» позволено сопровождать: только листание и порядок, но не отбор. */
const REPEAT_FOR_KEEPS = new Set(['page', 'pageSize', 'sortBy', 'sortOrder', 'repeatFor']);

export const serviceRequestListQuerySchema = baseListQuery(SERVICE_REQUEST_SORT_FIELDS)
  .extend({
    status: serviceRequestStatusSchema.optional(),
    objectId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    equipmentId: uuidSchema.optional(),
    equipmentTypeId: uuidSchema.optional(),
    serviceCounterpartyId: uuidSchema.optional(),
    /** «Ждёт меня» — очередь, из которой оператор и сервис начинают работу. */
    waitingOnMe: z
      .enum(['true', 'false'])
      .optional()
      .transform((v) => v === 'true'),
    /** Заявки, заведённые самим пользователем: «где мои». */
    mine: z
      .enum(['true', 'false'])
      .optional()
      .transform((v) => v === 'true'),
    /**
     * Очередь расхождений по объекту (Р16): заявитель сказал «аппарат стоит на другом объекте», а в
     * справочнике его до сих пор не перенесли. Отбор — конъюнкция трёх условий, и каждое обязательно:
     * расхождение **заявлено**, оно **не устранено** (снимок ≠ карточка) и заявка **открыта**. Без
     * последнего в очередь попали бы закрытые заявки прошлого года: технику с тех пор возили, и их
     * снимок расходится с карточкой законно.
     */
    objectMismatch: z
      .enum(['true', 'false'])
      .optional()
      .transform((v) => v === 'true'),
    /**
     * ПРЕДМЕТ ЗАЯВКИ НА ПРОВЕРКЕ ЛИБО ОТКЛОНЁН — две очереди одним параметром (план кандидатов, §9).
     *
     * ОДНО ЗНАЧЕНИЕ ИЗ ДВУХ, А НЕ ДВА ПРИЗНАКА-ЧЕКБОКСА. Состояние у кандидата ровно одно, и пара
     * булевых допускала бы запрос «и на проверке, и отклонён» — конъюнкцию, у которой ответ пуст
     * всегда. Пустая выдача без причины читается как поломка списка, а не как бессмысленный вопрос.
     *
     * ЗНАЧЕНИЙ ДВА, А НЕ ЧЕТЫРЕ, и это отбор, а не урезанный словарь. `confirmed` и `duplicate`
     * означают, что проверка кончилась и у заявки ПОЯВИЛСЯ обычный предмет — карточка парка; такие
     * заявки отбирают по самой карточке (`equipmentId`, `equipmentTypeId`), и третий путь к ним
     * означал бы два ответа на один вопрос. Здесь спрашивают про работу, которая ещё не сделана:
     * `pending` — очередь проверяющего («съездить и посмотреть на шильдик»), `rejected` — очередь
     * ведущего заявку («предмет не подтвердился, решить, что с ней делать»). Отсутствие параметра —
     * третье состояние отбора, «не спрашивали», и оно не то же самое, что «любой из двух».
     *
     * ПРАВОМ НЕ ЗАКРЫТ, в отличие от `awaitingDocuments` (ADR 0160, решение 9). Тот отбор — оракул:
     * по нему перебором читается, подшит ли по заявке счёт, то есть закрытая заявителю цифра. Этот
     * не открывает ничего: он отбирает по состоянию блока, который тот же читатель видит в карточке
     * заявки открытым текстом (`equipmentCandidate` в карте аудиторий), и заявок сверх своей области
     * видимости не показывает — предикат области стоит до него и одинаков для всех отборов.
     */
    candidateStatus: z.enum(['pending', 'rejected']).optional(),
    /** Предъявлена или принята, а закрывающих документов нет — планка приёмки (Р112, Р114). */
    awaitingDocuments: z
      .enum(['true', 'false'])
      .optional()
      .transform((v) => v === 'true'),
    warrantyClaim: z
      .enum(['true', 'false'])
      .optional()
      .transform((v) => v === 'true'),
    /**
     * Only requests that are not closed (`isServiceRequestClosed`), archived ones excluded by the
     * archive filter as usual. The request form asks it together with `equipmentId` to hint at
     * requests already open on the chosen unit (ADR 0236). The answer is narrowed by the reader's
     * scope like any other list, so an empty page means "none visible to you", not "none at all".
     *
     * Named `openOnly`, not `open`: the portal's `open` URL parameter already names the card to
     * open, and one word with two meanings in one address would be read the wrong way.
     */
    openOnly: z
      .enum(['true', 'false'])
      .optional()
      .transform((v) => v === 'true'),
    /** Только срочные: очередь, с которой начинают день оператор и ИТ. */
    urgent: z
      .enum(['true', 'false'])
      .optional()
      .transform((v) => v === 'true'),
    /**
     * ТОЛЬКО ПОВТОРНЫЕ (план `docs/office-equipment-repeat-request-plan.md`, Р10): заявки, у которых
     * по тому же аппарату есть закрытая либо отменённая ремонтная заявка в окне `W` — правило Р1
     * целиком, включая область видимости читателя.
     *
     * ФЛАГОМ, КАК У СОСЕДЕЙ: значение приходит строкой из query и превращается в «нет», пока его не
     * включили, — `undefined` в предикате означал бы фильтр, применённый наполовину.
     *
     * ЧТО ИМЕННО ОН ОТБИРАЕТ, РЕШАЕТ СЕРВЕР, И ТОЛЬКО ОН. Собери портал тот же отбор из
     * `equipmentId` + `status` + `kind`, он показал бы не то, что посчитала метка: окно у каждой
     * строки своё, а число совпадений считается в области смотрящего (Н7, Р8). Одно правило — одно
     * место, и место это `services/service-request-repeat.ts` на сервере.
     *
     * ПРИ ВЫКЛЮЧЕННОМ ОКНЕ (`SERVICE_REQUEST_REPEAT_WINDOW_DAYS = 0`) ОТБОР ПУСТ, А НЕ ОШИБОЧЕН
     * (§10.1, тест 8): «повторных нет» — честный ответ выключенного признака, а 422 на параметр,
     * который портал помнит с прошлого сеанса (ADR 0139), означал бы сломанный список у человека,
     * ничего сегодня не нажимавшего.
     */
    repeat: z
      .enum(['true', 'false'])
      .optional()
      .transform((v) => v === 'true'),
    /**
     * ССЫЛКА «ПРЕДЫДУЩИЕ» (Р10 того же плана): показать те самые заявки, которые посчитала метка
     * повтора у заявки `R`. Не отбор, а РЕЖИМ — потому и живёт отдельным параметром, а не парой
     * `equipmentId` + `status`: окно у каждой строки своё, считается оно в области смотрящего, и
     * собранный порталом «похожий» отбор показал бы не то число, которое человек только что видел
     * в теге.
     */
    repeatFor: uuidSchema.optional(),
    createdFrom: dateOnlySchema.optional(),
    createdTo: dateOnlySchema.optional(),
    archive: archiveFilterSchema,
  })
  /*
   * РЕЖИМ «ПРЕДЫДУЩИЕ» НЕ СМЕШИВАЕТСЯ С ОТБОРОМ (Р10). Ссылка обещает ровно те заявки, которые
   * посчитал тег, и число строк обязано совпасть с `repeat.count`. Позволь мы дописать к ней хоть
   * один фильтр — человек увидел бы «Повтор ×3» и список из одной строки, причём без единого
   * признака, что виноват отбор, оставшийся с прошлого сеанса (списки помнят отборы, ADR 0139).
   *
   * Проверка идёт ОТ ОБРАТНОГО — перечислены разрешённые ключи, а не запрещённые. Перечень
   * запрещённых пришлось бы дополнять при каждом новом фильтре списка, и первый же забытый молча
   * вернул бы ту самую рассогласованную выдачу.
   */
  .superRefine((value, ctx) => {
    if (!value.repeatFor) return;
    const busy = Object.entries(value)
      .filter(([key]) => !REPEAT_FOR_KEEPS.has(key))
      // «Нейтральное» — это не только отсутствие: булев фильтр в выключенном виде приходит `false`,
      // архив по умолчанию — `exclude`. Считать их заданными значило бы запретить саму ссылку.
      .filter(([, v]) => !(v === undefined || v === false || v === '' || v === 'exclude'))
      .map(([key]) => key);
    if (busy.length === 0) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['repeatFor'],
      message: `Список предыдущих заявок показывается целиком и не сочетается с отбором: снимите ${busy.join(', ')}`,
    });
  });

/**
 * Срочность: флаг и объяснение неразрывны (то же CHECK держит и БД). Чекбокс без причины через
 * месяц стоит у всех заявок — отбирать им становится нечего, а очередь, отсортированная по
 * признаку, который есть у каждого, ничем не отличается от несортированной.
 *
 * Проверка вынесена функцией, а не написана схемой на месте: срочность ставят в трёх местах
 * (заведение, правка, своя ручка), и в правке пара приходит наполовину — `PATCH` присылает только
 * изменившееся. Там условие считается по «склеенному» состоянию на сервере, а не по телу запроса.
 */
export const urgencyReasonSchema = z.string().trim().max(500);

export function urgencyIssue(value: { isUrgent: boolean; urgencyReason: string }): string | null {
  const reason = value.urgencyReason.trim();
  if (value.isUrgent && !reason) return 'Объясните, почему заявка срочная';
  if (!value.isUrgent && reason) return 'Причина без отметки «Срочная» ничего не объявляет';
  return null;
}

/** Схема с полной парой: заведение и своя ручка присылают оба поля разом. */
const withUrgency = <T extends z.ZodType<{ isUrgent: boolean; urgencyReason: string }>>(
  schema: T,
): T =>
  schema.superRefine((value, ctx) => {
    const issue = urgencyIssue(value);
    if (issue) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue, path: ['urgencyReason'] });
    }
  }) as unknown as T;

export const createServiceRequestSchema = withUrgency(
  z.object({
    /**
     * Аппарат — НЕОБЯЗАТЕЛЕН (Р5, ADR 0146, решение 6): половина работы модуля предметом в
     * справочнике не описывается вовсе («поставьте розетку», «настройте почту новому сотруднику»,
     * «принтер привезли, инвентарного номера ещё нет»), и до сих пор такую заявку заводили на
     * посторонний аппарат — после чего вся история обслуживания этой единицы врала.
     *
     * **Пустой аппарат схема принимает ВСЕГДА, а отказывает по нему МАРШРУТ** — правом
     * `serviceRequests.createWithoutEquipment` и кодом 403. Запиши мы условие «поле обязательно у
     * того, у кого нет права», рядовой заявитель получал бы 422 по полю: «заполните аппарат» —
     * то есть ответ «вы ошиблись формой» там, где человек не ошибся ничем, ему просто не положено.
     * Схема к тому же прав не видит и видеть не может: она одна на все учётки.
     *
     * `nullish`, а не `optional`: `null` и «поля нет» означают здесь **одно и то же** — аппарата у
     * заявки нет. Это не тот случай, что у `customerDepartmentId` ниже, где `null` — самостоятельный
     * ответ («заявка от площадки, подсказку не применять»): подсказывать аппарат неоткуда, и
     * различать два вида пустоты было бы различением без последствий.
     */
    officeEquipmentId: uuidSchema.nullish(),
    /**
     * ТРЕТИЙ СПОСОБ НАЗВАТЬ ПРЕДМЕТ — сообщение об аппарате, которого нет в справочнике (план
     * `docs/office-equipment-candidate-plan.md`, Р2, Р5).
     *
     * Кандидат приезжает ТЕМ ЖЕ ЗАПРОСОМ, что и заявка, а не отдельной ручкой «сначала кандидат,
     * потом заявка»: та оставляла бы кандидатов-сирот при каждом обрыве на втором шаге и требовала
     * бы уборки. Обе строки появляются одной транзакцией либо не появляются вовсе.
     *
     * Поле необязательное, потому что способов назвать предмет три и схема сверяет их между собой
     * ниже, в `superRefine`. Право `officeEquipment.propose` при этом спрашивает МАРШРУТ и отвечает
     * 403 — ровно по тому же доводу, что и у пустого аппарата выше: схема одна на все учётки и прав
     * не видит, а «заполните аппарат» в ответ рядовому заявителю означало бы «вы ошиблись формой»
     * там, где человек не ошибся ничем.
     *
     * `lazy`, а не прямая ссылка на схему, — из-за кольца импортов, и это не осторожность впрок:
     * файл кандидата берёт у справочника `createOfficeEquipmentSchema` (полная форма карточки в
     * подтверждении, Р13), справочник берёт отсюда `projectByAudiencePolicy`, и прямая ссылка
     * замкнула бы тройку на ЗНАЧЕНИИ, вычисляемом при загрузке модуля. Порядок вычисления модулей
     * в кольце зависит от того, кто вошёл первым, и одно из направлений входа падало бы с
     * «Cannot access before initialization» — молча и не у всех. `lazy` откладывает обращение к
     * схеме до первого разбора, когда все три модуля уже вычислены.
     */
    equipmentCandidate: z.lazy(() => equipmentCandidateInputSchema).optional(),
    /**
     * «Не тот объект» (Р16, ответ В3): аппарат стоит не там, где записано в его карточке, и объект
     * заявки называет человек. Пара «объект + пометка», а не одно поле, потому что признаков в
     * модели два и делят они работу так: ХРАНИМАЯ пометка — факт заявления («объект в этой заявке
     * указал человек, а не подставила карточка»), она исторична и неизменна, как остальные снимки
     * заявки; РАСХОЖДЕНИЕ — величина вычисляемая, соединением с карточкой техники, и гаснет оно
     * само, когда ИТ-служба перенесёт единицу в справочнике. Хранимый флаг сам не гаснет ничем, и
     * отбор по нему через месяц перестал бы быть очередью, став списком всего, что когда-либо
     * поправляли.
     *
     * Справочник заявка не правит: перенос единицы — решение ИТ-службы после проверки, а карточку
     * заводит всякий заявитель, и опечатка в заявке возила бы аппараты по объектам.
     *
     * Чужой объект отбивает сервер (422): `equipment_object_id` задаёт область видимости роли
     * объекта, и свободный выбор означал бы, что заявку можно отправить в чужую область — и увести
     * из своей. Портал показывает тот же отбор по привязкам автора, но портал не защита.
     *
     * У ЗАЯВКИ БЕЗ АППАРАТА У ЭТОГО ПОЛЯ ВТОРОЙ СМЫСЛ (Р6): оно называет не «где стоит аппарат», а
     * **заказчика** — площадку, от имени которой заявка. Колонка та же (`equipment_object_id`), и
     * это не совпадение: областью роли площадки заведует именно она, и завести туда заявку иначе
     * значило бы завести вторую колонку с тем же назначением. Пометка при этом запрещена (ниже), и
     * потому два смысла не путаются: с пометкой — «не тот объект», без аппарата — «для кого».
     */
    objectId: uuidSchema.optional(),
    objectOverridden: z.boolean().optional().default(false),
    /**
     * Вид заявки (Н1). До выпуска 3 контракт заведения этого поля не имел вовсе, и заявка на
     * расходники была незаводима — не запретом, а отсутствием поля (§7.3 плана): без строк
     * номенклатуры, формы и списания она была бы заявкой без предмета. Поле появляется вместе с
     * M12, то есть вместе со строками, формой и списанием.
     *
     * `optional()` БЕЗ `default('repair')`, и это не небрежность. `default` в zod делает поле
     * обязательным в РАЗОБРАННОМ теле, а его строит портал объектным литералом — то есть каждый
     * существующий вызов заведения пришлось бы дописать полем, которого он не знает. Умолчание при
     * этом никуда не девается: оно стоит колонкой в базе (`DEFAULT 'repair'`, M3) и останется там
     * навсегда. «Поля нет» читается как «ремонт» — ровно так, как читает его старый код в окне
     * выката.
     */
    kind: serviceRequestKindSchema.optional(),
    /**
     * Строки номенклатуры — у заявки на расходники и только у неё (§6.2). Обязательными они быть
     * перестали (Р15): заявитель номенклатуры не знает, и его дело — сказать словами, чего не
     * хватает; состав заполняет исполнитель ручкой `PUT /:id/consumables`.
     *
     * Принимать их заведение продолжает, и это не остаток прежнего правила: заявку заводит и
     * ИТ-служба за сотрудника, зная состав сразу, — запретив, мы заставили бы её делать два шага
     * там, где хватало одного.
     *
     * `optional()` без `default([])` по той же причине, что у вида выше: пустой список и
     * отсутствие поля означают здесь одно и то же — «строк нет», — и платить за их различение
     * правкой каждого существующего вызова заведения не за что.
     */
    consumables: z.array(serviceConsumableLineSchema).max(50).optional(),
    description: descriptionSchema,
    /**
     * Отдел, от имени которого заявка (ADR 0085 §8). Подсказывается владельцем техники либо
     * единственным отделом автора, но выбирается человеком: сотрудник соседнего отдела чинит «чужой»
     * принтер чаще, чем кажется.
     *
     * `null` и «поля нет» — разные ответы, и разница здесь важнее самого поля: `null` означает
     * **заявка от площадки, подсказку не применять** — так отвечает форма, где заказчик обязателен
     * и пустого состояния у него нет; `undefined` означает «клиент про поле не знает», и только
     * тогда сервер подставляет подсказку (Р12, Р12а).
     */
    customerDepartmentId: uuidSchema.nullish(),
    /**
     * Подразделение **заявителя** (Н11) — не заказчик выше: заказчика выбирает человек («чужой»
     * принтер соседнего отдела), а здесь записано, где числится он сам.
     *
     * Присылают эти два поля **только чтобы выбрать одно из своих**: сервер берёт привязки учётки
     * `created_by` и на чужое отвечает 422. Единственная привязка подставляется без вопроса, и
     * тогда полей в теле нет вовсе; выбор требуется лишь у учётки с двумя отделами (или двумя
     * площадками). Прислать оба сразу нельзя — подразделение либо отдел, либо площадка.
     */
    requesterDepartmentId: uuidSchema.nullish(),
    requesterObjectId: uuidSchema.nullish(),
    /**
     * Заявитель и его телефон — обязательны (план модернизации, Р49). До этого обязательность жила
     * только в форме портала (`ResponsibleFields`), а схема принимала пустые строки: заявка без
     * контакта заводилась любым клиентом мимо формы, и именно её сервис получал первой — ехать по
     * адресу, не зная, к кому.
     */
    responsibleName: contactNameSchema,
    responsiblePhone: contactPhoneSchema,
    comment: z.string().trim().max(2000).optional().default(''),
    warrantyClaim: warrantyClaimSchema.optional(),
    isUrgent: z.boolean().optional().default(false),
    urgencyReason: urgencyReasonSchema.optional().default(''),
    fileIds: z.array(uuidSchema).max(20).optional().default([]),
  }),
).superRefine((value, ctx) => {
  /**
   * Правила предмета расходятся НАТРОЕ — ровно как трёхсоставный `service_requests_subject_check`
   * (миграции 0230 и 0264, Р7 ADR 0146 и Р5 плана кандидатов). У заявки С АППАРАТОМ объект — это
   * «где он стоит», и он ходит парой с пометкой; у заявки БЕЗ АППАРАТА тот же `objectId` означает
   * уже другое — **заказчика**, — и пометке там взяться неоткуда; у заявки С КАНДИДАТОМ предмет
   * называет сам заявитель, и место аппарата приезжает внутри кандидата. Одним общим набором
   * условий эти три смысла не описываются: «объект без пометки ничего не объявляет» верно только в
   * первой части и запрещало бы вторую, а XOR «объект либо отдел» верен во второй и запретил бы
   * третью (заявку от лица отдела на кандидата, Р7, последний абзац).
   *
   * ВЕТКА КАНДИДАТА ОТДЕЛЯЕТСЯ ПЕРВОЙ, и порядок здесь содержателен: способы назвать предмет
   * ВЗАИМОИСКЛЮЧАЮЩИ (Р2), и «прислали два сразу» обязано читаться отказом про кандидата, а не
   * молчаливым выбором одного из двух по порядку разбора.
   */
  if (value.equipmentCandidate) {
    /*
     * ЧЕТЫРЕ ЗАПРЕТА ВЕТКИ, и каждый закрывает своё, а не «поле, которое здесь не нужно»:
     *
     *   * АППАРАТ ССЫЛКОЙ — второй способ назвать тот же предмет. Прими мы оба, сервер завёл бы
     *     кандидата на аппарат, который заявитель тут же и выбрал, а проверяющий получил бы в
     *     очередь сообщение о технике, стоящей в справочнике. Отказ до всякой работы (Р2);
     *   * ПОМЕТКА «НЕ ТОТ ОБЪЕКТ» — утверждение о РАСХОЖДЕНИИ снимка заявки с карточкой единицы, а
     *     карточки ещё нет: расходиться не с чем. Место аппарата здесь называет сам кандидат
     *     (`equipmentCandidate.objectId`), и второго способа сказать то же самое не заводится;
     *   * ОБЪЕКТ ВЕРХНЕГО УРОВНЯ — тот же самый второй способ. Молча проигнорировать его нельзя:
     *     человек, приславший заявку с одним объектом снаружи и другим внутри кандидата, обязан
     *     узнать, какой из них не сработал, а не искать потом заявку в чужой области;
     *   * ГАРАНТИЙНОЕ ОБРАЩЕНИЕ — спор о КОНКРЕТНОЙ единице и её гарантии поставщика, которую
     *     читают из карточки парка. У непроверенного предмета карточки нет, и гарантию читать
     *     неоткуда (Р16: тот же запрет держит и сервер, пока кандидат `pending`).
     *
     * А `customerDepartmentId` ЗАКОННЫМ ОСТАЁТСЯ, и это не забытый пятый запрет (Р7, последний
     * абзац). Заявку на кандидата заводят и от лица отдела: тогда `customer_department_id` держит
     * заказчика, а `equipment_object_id` — физическое место аппарата. Запрети мы пару, сотрудник
     * отдела потерял бы собственную заявку сразу после отправки по правилу ADR 0146 — она ушла бы
     * в объектную область, которой у него нет.
     */
    if (value.officeEquipmentId != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Предмет заявки один: либо аппарат из справочника, либо сообщение о новом',
        path: ['equipmentCandidate'],
      });
    }
    if (value.objectOverridden) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Пометка «не тот объект» бывает только у заявки с аппаратом из справочника',
        path: ['objectOverridden'],
      });
    }
    if (value.objectId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Место аппарата называет само сообщение о технике — объект заявки не нужен',
        path: ['objectId'],
      });
    }
    if (value.warrantyClaim?.source) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обращаются по гарантии аппарата из справочника, а этот ещё не проверен',
        path: ['warrantyClaim'],
      });
    }
  } else if (value.officeEquipmentId != null) {
    // Пометка «не тот объект» и сам объект — одно утверждение, а не два соседних поля, и
    // проверяются они парой ровно как срочность с её причиной: пометка без объекта не говорит, где
    // аппарат стоит на самом деле, а объект без пометки — молчаливая подмена области видимости.
    if (value.objectOverridden && !value.objectId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите, на каком объекте стоит аппарат',
        path: ['objectId'],
      });
    }
    if (!value.objectOverridden && value.objectId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Объект без пометки «не тот объект» ничего не объявляет',
        path: ['objectOverridden'],
      });
    }
  } else {
    /*
     * ДВЕ ЗАКРЫТЫЕ ДВЕРИ (Р7). Обе — не «пока не сделали», а «не бывает вовсе»:
     *
     *   * пометка «не тот объект» — утверждение о РАСХОЖДЕНИИ снимка заявки с карточкой единицы;
     *     без единицы расходиться не с чем, а поднятая пометка сделала бы объект-заказчика
     *     похожим на исправление несуществующей карточки — и он попал бы в очередь ИТ-службы
     *     «перенесите аппарат», где переносить нечего;
     *   * гарантийное обращение — спор о КОНКРЕТНОЙ единице: либо о гарантии поставщика на неё,
     *     либо о работе, выполненной на ней же. Сервер отвечает то же самое и по существу
     *     (`resolveWarrantyClaim`), но схема успевает раньше и называет поле.
     */
    if (value.objectOverridden) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Пометка «не тот объект» бывает только у заявки с аппаратом',
        path: ['objectOverridden'],
      });
    }
    if (value.warrantyClaim?.source) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Обращаются по гарантии конкретного аппарата, а у этой заявки аппарата нет',
        path: ['warrantyClaim'],
      });
    }
    /*
     * ЗАКАЗЧИК РОВНО ОДИН — объект ИЛИ отдел (Р7), тем же XOR, что стоит второй ветвью
     * `service_requests_subject_check`. Обе половины условия обязательны, и обе отвечают на свой
     * вопрос:
     *
     *   * ДВОЕ СРАЗУ — заявка, которую роль площадки и роль отдела считают своей каждая: предикат
     *     области читает те же три колонки, и такая строка попадает в оба среза;
     *   * НИ ОДНОГО — заявка вне области ЛЮБОЙ роли: её не увидит никто, включая автора, если он
     *     сам площадочный или отдельский.
     *
     * Отбивает это схема, а база стоит вторым рубежом: без схемы человек получил бы `23514` вместо
     * фразы, а поля отказ не назвал бы вовсе.
     */
    const byObject = !!value.objectId;
    const byDepartment = !!value.customerDepartmentId;
    if (byObject && byDepartment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'У заявки без аппарата заказчик один: либо объект, либо отдел',
        path: ['objectId'],
      });
    }
    if (!byObject && !byDepartment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите, для кого заявка: объект или отдел',
        path: ['objectId'],
      });
    }
  }
  // Требования «хотя бы одна позиция» у расходников больше нет (Р15): предмет такой заявки —
  // описание словами, а номенклатуру заполняет исполнитель. Обратная проверка осталась: строки у
  // ремонта — это состав, который никто не выдаст и не спишет, потому что формы у него нет.
  const lines = value.consumables ?? [];
  if (value.kind !== 'consumable' && lines.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Строки номенклатуры бывают только у заявки на расходники',
      path: ['consumables'],
    });
  }
  addConsumableDuplicateIssues(lines, ctx, ['consumables']);
});
export type CreateServiceRequestInput = z.infer<typeof createServiceRequestSchema>;

/**
 * Правка присылает только изменившееся, поэтому у срочности здесь **нет значения по умолчанию**:
 * `default(false)` означал бы, что правка телефона молча снимает срочность — тот же подвох, из-за
 * которого поля со значением по умолчанию переобъявляются в `updateOfficeEquipmentTypeSchema`.
 * Пару сверяет сервер по склеенному состоянию (`urgencyIssue`), потому что схема видит половину.
 */
export const updateServiceRequestSchema = z.object({
  description: descriptionSchema.optional(),
  /**
   * Смысл значений тот же, что при заведении, но сервер сверяет присланное **со строкой заявки**:
   * форма шлёт заказчика всегда, и «поле пришло» не означает «человек его менял» (Р12б).
   */
  customerDepartmentId: uuidSchema.nullish(),
  responsibleName: contactNameSchema.optional(),
  responsiblePhone: contactPhoneSchema.optional(),
  comment: z.string().trim().max(2000).optional(),
  warrantyClaim: warrantyClaimSchema.optional(),
  isUrgent: z.boolean().optional(),
  urgencyReason: urgencyReasonSchema.optional(),
  version: z.number().int().nonnegative(),
});
export type UpdateServiceRequestInput = z.infer<typeof updateServiceRequestSchema>;

/**
 * Срочность отдельной ручкой: её ставят и снимают не только при правке заявки. Заказчик правит
 * только «Новую» (ADR 0085 §8), а «сломался единственный принтер на площадке» выясняется и когда
 * заявка уже у сервиса, — поэтому оператор меняет признак в любой момент до закрытия.
 */
export const setServiceUrgencySchema = withUrgency(
  z.object({
    isUrgent: z.boolean(),
    urgencyReason: urgencyReasonSchema.default(''),
    version: z.number().int().nonnegative(),
  }),
);
export type SetServiceUrgencyInput = z.infer<typeof setServiceUrgencySchema>;

// ── Ход заявки ──
// Каждый переход с содержанием — своя ручка со своей схемой (ADR 0085 §18): так проверка условия
// перехода не может разъехаться с проверкой данных этого перехода. `/status` остаётся отмене и
// административным откатам, у которых из данных только причина.

/**
 * Причина, которой объясняют чужую работу: отмену, отказ, переназначение, заморозку.
 *
 * ЭКСПОРТИРУЕТСЯ РАДИ ПАКЕТНОЙ РУЧКИ (план `docs/office-equipment-bulk-actions-plan.md`, §6.1):
 * общая причина пачки — та же причина, что у одиночного действия, и переписанная рядом строка
 * `z.string().trim().min(3).max(1000)` и была бы тем самым вторым набором правил, только
 * маленьким. Разойдись они — пачка принимала бы объяснение, которое одиночная ручка отвергает.
 */
export const reasonSchema = z.string().trim().min(3, 'Укажите причину').max(1000);

export const assignServiceSchema = z.object({
  serviceCounterpartyId: uuidSchema,
  /** Причина обязательна при переназначении: у прежнего сервиса отбирают работу. */
  reason: reasonSchema.optional(),
  comment: z.string().trim().max(1000).optional().default(''),
  version: z.number().int().nonnegative(),
});
export type AssignServiceInput = z.infer<typeof assignServiceSchema>;

/**
 * Назначение исполнителей одним действием (Н5, Н6): список своих сотрудников **и**
 * исполнитель-контрагент. Одно тело, а не две ручки, потому что «наш сисадмин + КопиЛайт» —
 * обычный случай постановки, и разложенный на два запроса он давал бы промежуточное состояние,
 * в котором заявка уже переназначена, но ещё наполовину.
 *
 * Оба поля передаются **целиком**: список — это состав, и «добавить одного» без остальных
 * означало бы, что сервер должен угадывать, снимали ли кого-то. Пустой список вместе с
 * `null`-контрагентом — не «снять всех», а ошибка: заявка в рабочем статусе без исполнителя
 * запрещена триггером (M4), и отвечает на неё маршрут словами, а не ошибкой БД.
 *
 * Причина обязательна при переназначении: у прежнего исполнителя отбирают работу. Проверяет её
 * сервер по строке заявки — из тела не видно, первое это назначение или второе.
 */
export const putServiceExecutorsSchema = z.object({
  /** Поимённые исполнители — учётки с правом `serviceRequests.execute` (сервер проверяет). */
  userIds: z.array(uuidSchema).max(20),
  /** Исполнитель-контрагент; `null` — заявку ведут только свои. */
  serviceCounterpartyId: uuidSchema.nullable(),
  reason: reasonSchema.optional(),
  comment: z.string().trim().max(1000).optional().default(''),
  version: z.number().int().nonnegative(),
});
export type PutServiceExecutorsInput = z.infer<typeof putServiceExecutorsSchema>;

export const declineServiceRequestSchema = z.object({
  reason: reasonSchema,
  version: z.number().int().nonnegative(),
});
export type DeclineServiceRequestInput = z.infer<typeof declineServiceRequestSchema>;

/**
 * «Принять в работу»: исполнитель берётся за заявку сам (В3). Данных у хода нет — есть только
 * версия, — но своя ручка нужна, потому что очередь «назначено, но никто не взялся» должна
 * оставаться видимой: сделай мы переход побочным следствием первой же правки сметы, эта очередь
 * исчезла бы вместе с ним.
 */
export const startServiceRequestSchema = z.object({
  version: z.number().int().nonnegative(),
});

/**
 * ЗАЯВЛЕНИЕ ОБ ОСВОБОЖДЕНИИ ОТ ПОДПИСИ (Р2, Р3): в теле — только пояснение.
 *
 * ЭТО ЗАЯВЛЕНИЕ, А НЕ РЕЗУЛЬТАТ. Исход (`applied` / `observed`) считает сервер рубильником
 * (`evaluateExemption`); приди он полем от клиента — автоподпись под денежным решением ставил бы
 * браузер, и выключенный рубильник не значил бы ничего.
 *
 * Основание списком не заводится (ответ В4 заказчика от 11.09.2026: «один пункт без выбора»), а
 * «по условиям договора» в интерфейсе не пишется вовсе: политики нет (ответ В12), договор системе
 * неизвестен, и обещание, которого она не проверяет, было бы ложью. Пояснение остаётся
 * необязательным — им исполнитель и скажет «мелкий ремонт на месте», если захочет.
 */
const serviceEstimateExemptionDeclarationSchema = z
  .object({
    note: z.string().trim().max(1000).optional(),
  })
  .strict();
export type ServiceEstimateExemptionDeclaration = z.infer<
  typeof serviceEstimateExemptionDeclarationSchema
>;

/** Общая половина всех трёх форм предъявления: слово вдогонку и версия (Р30). */
const submitServiceEstimateCommonFields = {
  comment: z.string().trim().max(1000).optional().default(''),
  version: z.number().int().nonnegative(),
};

/** Построчное предъявление — сегодняшнее: строки и их суммы правит своя ручка, тело их не несёт. */
const submitServiceEstimateItemsSchema = z
  .object({
    mode: z.literal('items'),
    exemption: serviceEstimateExemptionDeclarationSchema.optional(),
    ...submitServiceEstimateCommonFields,
  })
  .strict();

/**
 * Подача счётом. `fileIds` — страницы одного документа, минимум одна: документный формат без
 * документа не формат, а пустая ревизия. Суммы и реквизитов счёта тело не принимает вовсе (ответы
 * В5 и В13): их положит позже разбор документа, а ноль вместо неизвестной суммы читался бы как
 * «работы бесплатны». Порядок страниц — порядок списка, и его сервер кладёт в `page_no`.
 */
const submitServiceEstimateDocumentSchema = z
  .object({
    mode: z.literal('document'),
    fileIds: z.array(uuidSchema).min(1, 'Приложите счёт').max(20),
    exemption: serviceEstimateExemptionDeclarationSchema.optional(),
    ...submitServiceEstimateCommonFields,
  })
  .strict();

/**
 * Гарантийный ремонт (Р27): смета из одной служебной строки с нулевой суммой. Освобождения у него
 * НЕ БЫВАЕТ — и не как запрет, а как отсутствие поля: нулевая сумма не нуждается в подписи, и
 * освобождать от неё нечего.
 */
const submitServiceEstimateWarrantySchema = z
  .object({
    mode: z.literal('warranty'),
    ...submitServiceEstimateCommonFields,
  })
  .strict();

/**
 * СТАРОЕ ТЕЛО ПРЕДЪЯВЛЕНИЯ — плоское `{ warrantyRepair, comment, version }` без формата.
 *
 * ПРИНИМАЕТСЯ ПОТОМУ, ЧТО КЛИЕНТ ОБНОВЛЯЕТСЯ ПОЗЖЕ СЕРВЕРА. Уже открытая вкладка портала переживает
 * парный выкат, баннер обновления ненавязчив и имеет «Позже» — гарантии, что старая форма перестанет
 * слать старое поле, нет никакой. Поэтому совместимость выражена СХЕМОЙ, а не надеждой: тело без
 * `mode` нормализуется до разбора — `warrantyRepair: true` означает `warranty`, всё прочее `items`.
 * Ровно эти два значения старая форма и умела посылать.
 *
 * ПОЧЕМУ ПРЕДОБРАБОТКОЙ, А НЕ СОЮЗОМ «НОВАЯ СХЕМА | LEGACY». Союз оставил бы старое плоское тело в
 * выходном типе, и каждый читатель — ручка, тесты, письмо — разбирал бы два разных представления
 * одного предъявления; снятие совместимости тогда означало бы правку всех читателей, а не одной
 * функции. Здесь же наружу выходит ровно союз трёх форматов, а про алиас знает одно место (тот же
 * приём, что у старого `constructionObjectId` в отделах).
 *
 * ОБА ПОЛЯ СРАЗУ — ОТКАЗ, А НЕ СТАРШИНСТВО: тело задаёт формат дважды, и вопрос «какой из двух
 * главный» ответа не имеет. Старый клиент под это правило не попадает никогда — `mode` он не знает;
 * попадает только новый код, написавший лишнее.
 */
function normalizeLegacyEstimateSubmit(raw: unknown, ctx: z.RefinementCtx): unknown {
  // Не объект — судить не о чем: пусть об этом скажет сама схема своим обычным сообщением.
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  const body = raw as Record<string, unknown>;
  const legacy = body.warrantyRepair;
  if (body.mode !== undefined) {
    if (legacy === undefined) return raw;
    ctx.addIssue({
      code: 'custom',
      message:
        'Формат объёма работ задан дважды: «mode» и устаревшее «warrantyRepair» — ' +
        'оставьте только «mode»',
      path: ['warrantyRepair'],
    });
    return raw;
  }
  /*
   * Непохожее на признак значение — отказ, а не «считаем построчным». Старая форма слала сюда
   * только `true`/`false`, и строка `"true"` в этом поле означала бы не гарантийный ремонт, а
   * ошибку в чужом коде: молча переведи мы её в `items` — гарантийное предъявление ушло бы с
   * ненулевой подписью.
   */
  if (legacy !== undefined && typeof legacy !== 'boolean') {
    ctx.addIssue({
      code: 'custom',
      message: 'Поле «warrantyRepair» принимает только true или false',
      path: ['warrantyRepair'],
    });
    return raw;
  }
  const normalized: Record<string, unknown> = {
    ...body,
    mode: legacy === true ? 'warranty' : 'items',
  };
  delete normalized.warrantyRepair;
  return normalized;
}

/**
 * ПРЕДЪЯВЛЕНИЕ ОБЪЁМА РАБОТ (Р2 плана
 * `docs/office-equipment-on-site-and-invoice-estimate-plan.md`): формат — ВНЕШНИЙ дискриминатор, а
 * не поле внутри плоского тела.
 *
 * ЗАЧЕМ СОЮЗ, А НЕ ОБЪЕКТ С ПРОВЕРКАМИ. Законных сочетаний три, и каждое незаконное обязано быть
 * НЕСОБИРАЕМЫМ, а не отбиваемым: `warranty` с освобождением не существует как тип, `document` без
 * файлов не проходит `min(1)`. Проверкой поверх плоского тела это жило бы до первой правки — забытая
 * ветка `superRefine` молчит, а тип нет, — и цена такой тишины здесь денежная: автопринятие суммы.
 *
 * `items` + освобождение — ЗАКОННО, и это главный сценарий разбора: мелкий ремонт на месте вписывают
 * строками и сразу помечают «согласование не требуется» (ответ заказчика 11.09.2026, им же отвергнуто
 * требование «при освобождении обязателен счёт» — оно закрыло бы этот сценарий целиком).
 *
 * ЧТО ОСТАЁТСЯ СЕРВЕРУ ПОД БЛОКИРОВКОЙ: файл, не принадлежащий заявке, недогруженный файл
 * (`requireActive`, Р7) и право самого заявления (`canDeclareExemption`). Схема отвечает за состав
 * тела, а не за принадлежность чужих строк.
 *
 * `.strict()` у каждой ветки — по той же причине, что у раскладки (ADR 0179): молчаливое
 * отбрасывание неизвестного поля у денежной ручки уже стоило разбора.
 *
 * СОБИРАЕМОЕ ТЕЛО ЕЩЁ НЕ ОЗНАЧАЕТ ПРИНИМАЕМОЕ: контракты волны едут первым выпуском, а документную
 * ревизию и исход заявления сервер научится записывать только следующим (§7, шаг 1). До тех пор
 * ручка предъявления отвечает на `document` и на `exemption` отказом 422 — вслух, а не молчаливым
 * отбрасыванием: иначе документное тело разобралось бы ветвью `items`, и денежное решение осталось бы
 * без записи. Снятие этих двух отказов — работа Э4, и заменяют их рубильники, а не удаление.
 */
export const submitServiceEstimateSchema = z.preprocess(
  normalizeLegacyEstimateSubmit,
  z.discriminatedUnion('mode', [
    submitServiceEstimateItemsSchema,
    submitServiceEstimateDocumentSchema,
    submitServiceEstimateWarrantySchema,
  ]),
);
export type SubmitServiceEstimateInput = z.infer<typeof submitServiceEstimateSchema>;

/**
 * Согласование объёма работ: одна ручка на «да» и «нет» — у них одно право, одна область и один
 * момент. Статус «да» не меняет вовсе (Р8) — заявка стоит в «В работе», как просил заказчик; «нет»
 * уводит её в «Отменена» (В1).
 *
 * У отказа спрашиваются ДВА текста, и это не дублирование (Р12): причина отвечает, ПОЧЕМУ объём не
 * согласован («вдвое дороже нового аппарата»), решение — ЧТО ДЕЛАЕМ вместо («меняем, заявка на
 * закупку заведена»). Расходятся они и путём: причина уходит комментарием перехода в историю, как у
 * всякого перехода с объяснением, а решение остаётся полем заявки — с него начинается разбор
 * отклонённой заявки через месяц, и оставленное только в ленте оно читалось бы не там, где его
 * ищут. Свободной строкой, а не списком: решений на практике может оказаться три, а может
 * тридцать, и заведённый вслепую список отобрал бы у отбора догадки (§9.2).
 *
 * Галочка замены больше не ставится за человека. Прежде отказ ИТ означал «не чинить, значит
 * менять», и флаг проставляла сама ручка; после слияния подписей «не согласовано» означает много
 * чего ещё, и проставленный автоматически флаг был бы решением, которого никто не принимал (Р8).
 */
export const approveServiceEstimateSchema = z
  .object({
    approved: z.boolean(),
    reason: reasonSchema.optional(),
    /** Решение при отказе: что делаем вместо ремонта (Р12). У согласия его не бывает. */
    resolution: z.string().trim().max(500).optional(),
    /** «Ремонт нецелесообразен, аппарат под замену» — теперь ответ человека, а не вывод ручки. */
    replacementRecommended: z.boolean().optional().default(false),
    version: z.number().int().nonnegative(),
  })
  .superRefine((v, ctx) => {
    if (v.approved) return;
    if (!v.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите причину отклонения',
        path: ['reason'],
      });
    }
    if (!v.resolution) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Опишите решение: что делаем вместо ремонта',
        path: ['resolution'],
      });
    }
  });
export type ApproveServiceEstimateInput = z.infer<typeof approveServiceEstimateSchema>;

/*
 * СХЕМЫ ВИЗЫ ИТ (`approveServiceItSchema`) ЗДЕСЬ БОЛЬШЕ НЕТ. Виза упразднена планом упрощения цикла
 * (Р10): ручку `PATCH /:id/it-approval` сняли, вопрос «чинить за эти деньги или менять аппарат»
 * задаёт себе тот же человек, что смотрит на объём работ (ответ В2), — и схему держали ровно до
 * уборки права `serviceRequests.approveIt` из выданных наборов, обещанной тем же выпуском. Право
 * убрано (план профилей оргтехники, Э9, миграция E), и схема без ручки, без права в наборе и без
 * единого зовущего уехала следом.
 *
 * Подписи, поставленные до упразднения, никуда не делись: поля `it_approved_*` читает
 * `hasCurrentItApproval`, и карточка по-прежнему показывает, к текущей ли ревизии сметы относится
 * подпись от 22.08. Решающих мест у визы нет ни одного.
 */

export const reopenServiceEstimateSchema = z.object({
  reason: reasonSchema,
  version: z.number().int().nonnegative(),
});

// ── Спор об освобождении от подписи (Р9) ──
//
// Оспаривает «Ведение», и до приёмки (ответ В3 заказчика от 11.09.2026). Механика остановки —
// обычная заморозка с видом `estimate_exemption_dispute`: коридор для неё уже есть, включая вход из
// «Решена», и второго способа остановить заявку модулю не нужно. Кому открыты обе ручки — предикаты
// `canOpenServiceEstimateDispute` и `canResolveServiceEstimateDispute`.

/**
 * Открыть спор. Причина ОБЯЗАТЕЛЬНА, и тем же `reasonSchema`, что у отмены, отказа и заморозки:
 * спор останавливает заявку, и «почему» — единственное, что объясняет исполнителю остановку. Второй
 * набор правил для той же причины разошёлся бы с первым (см. комментарий у `reasonSchema`).
 */
export const openServiceEstimateDisputeSchema = z
  .object({
    reason: reasonSchema,
    version: z.number().int().nonnegative(),
  })
  .strict();
export type OpenServiceEstimateDisputeInput = z.infer<typeof openServiceEstimateDisputeSchema>;

/**
 * Разрешить спор — союзом по исходу, а не объектом с проверкой, по той же причине, что у
 * предъявления (Р2): «отменить заявку без причины» не должно СОБИРАТЬСЯ.
 *
 * ПРИЧИНА ОБЯЗАТЕЛЬНА РОВНО У `cancel`, и это не экономия на словах. `keep` возвращает заявку туда,
 * откуда её остановили, а `require_signature` снова собирает подпись — объяснение обоим уже лежит в
 * самом споре, и требовать второе значило бы спрашивать «почему вы передумали передумывать».
 * `cancel` же закрывает заявку, и у отмены причина обязательна по общему правилу модуля — тем же
 * `reasonSchema`, которым её требует ручка `/status`.
 *
 * Исход `require_signature` открывает ожидание с происхождением `dispute` — отсюда и подпись в
 * «Решена» (`allowsEstimateApprovalInStatus`). Куда при каждом исходе уходит статус, решает матрица
 * на сервере: тело запроса про статусы не знает.
 */
const resolveServiceEstimateDisputeCommonFields = {
  comment: z.string().trim().max(1000).optional().default(''),
  version: z.number().int().nonnegative(),
};

export const resolveServiceEstimateDisputeSchema = z.discriminatedUnion('outcome', [
  z
    .object({
      outcome: z.literal('keep'),
      ...resolveServiceEstimateDisputeCommonFields,
    })
    .strict(),
  z
    .object({
      outcome: z.literal('require_signature'),
      ...resolveServiceEstimateDisputeCommonFields,
    })
    .strict(),
  z
    .object({
      outcome: z.literal('cancel'),
      reason: reasonSchema,
      ...resolveServiceEstimateDisputeCommonFields,
    })
    .strict(),
]);
export type ResolveServiceEstimateDisputeInput = z.infer<
  typeof resolveServiceEstimateDisputeSchema
>;

/**
 * Закрытие работ. Итог **не принимается**: его считает сервер из строк, иначе сумма строк и итог
 * разошлись бы молча. Клиент присылает отметки факта по каждой строке и, если нужно, скидку акта.
 */
export const completeServiceRequestSchema = z.object({
  completedOn: dateOnlySchema,
  /** An explicit operator override; an absent field preserves the ordinary document gate. */
  forceWithoutDocument: z.boolean().optional(),
  items: z
    .array(
      z.object({
        id: uuidSchema,
        performed: z.boolean(),
        /** Фактическое количество; больше согласованного не бывает — это удорожание. */
        actualQuantity: quantitySchema.nullish(),
        /** Дата гарантии из талона; пусто — сервер посчитает её от даты выполнения. */
        warrantyUntil: dateOnlySchema.nullish(),
      }),
    )
    .max(200),
  /**
   * Факт выдачи по строкам расходников — у заявки на расходники и только у неё (Р3). Приходит
   * закрытием, а не отдельной ручкой следом, потому что списание со склада идёт ТОЙ ЖЕ
   * транзакцией, что и переход в «Решена» (Р5): блокировка позиций, проверка остатков, события
   * журнала и смена статуса неразделимы — иначе заявка успевала бы стать решённой при неудавшемся
   * списании.
   *
   * Пустой список у заявки на расходники законен ровно в одном случае: факт уже проставлен правкой
   * (`PATCH /:id/consumables/issued`) и на закрытии не менялся. Умолчание строк — «сколько
   * просили» — подставляет форма, а не схема: сервер не должен списывать со склада по молчанию
   * клиента.
   *
   * `optional()` без `default([])` — тот же приём, что у строк заведения: окно закрытия ремонта
   * строит тело литералом и про расходники ничего не знает, а «поля нет» и «пустой список» здесь
   * означают одно и то же.
   */
  consumables: z.array(serviceConsumableIssueSchema).max(50).optional(),
  /** Скидка по акту: строго отрицательная сумма и обязательная причина — порознь не бывают. */
  adjustmentAmount: z.number().negative('Скидка — отрицательная сумма').nullish(),
  adjustmentReason: z.string().trim().max(500).optional().default(''),
  comment: z.string().trim().max(1000).optional().default(''),
  version: z.number().int().nonnegative(),
});
export type CompleteServiceRequestInput = z.infer<typeof completeServiceRequestSchema>;

export const acceptServiceRequestSchema = z.object({
  comment: z.string().trim().max(1000).optional().default(''),
  version: z.number().int().nonnegative(),
});

export const reworkServiceRequestSchema = z.object({
  reason: reasonSchema,
  version: z.number().int().nonnegative(),
});

/**
 * Заморозка. Причина обязательна (Р107) — тем же `reasonSchema`, что у отмены и отказа: даты
 * «отложена до» у заморозки нет, и на вопрос «когда ждать» отвечает только она. Куда вернуть,
 * клиент не присылает: исходный статус сервер берёт из самой заявки (Р104).
 */
export const serviceHoldSchema = z.object({
  reason: reasonSchema,
  version: z.number().int().nonnegative(),
});
export type ServiceHoldInput = z.infer<typeof serviceHoldSchema>;

/** Возврат в работу: цель берётся из `held_from_status`, от человека нужно лишь слово вдогонку. */
export const serviceResumeSchema = z.object({
  comment: z.string().trim().max(1000).optional().default(''),
  version: z.number().int().nonnegative(),
});
export type ServiceResumeInput = z.infer<typeof serviceResumeSchema>;

/**
 * Отмена и административные откаты: причина — главное содержание такого перехода.
 *
 * ДВА ПОЛЯ РЯДОМ С НЕЙ — ВТОРОЙ ВХОД В «РЕКОМЕНДОВАНА ЗАМЕНА» (Р10 плана
 * `office-equipment-card-and-list-cleanup-plan.md`, Н3). Прежде и пометку, и решение ставил ровно
 * один ход — отказ по объёму работ; после Р5 у внутреннего ремонта объёма работ не бывает вовсе, и
 * сказать «чинить нецелесообразно, аппарат под замену» стало нечем. А именно по этой пометке
 * собирают список того, что пора менять, — волна закрыла бы этап вместе со списком.
 *
 * ОБА НЕОБЯЗАТЕЛЬНЫЕ И БЕЗ `default`, и это не мелочь схемы. Отсутствие поля сервер трактует как
 * «замена не рекомендована»: тем же телом ходит МАССОВАЯ отмена, и общий `default(false)` был бы
 * безобиден лишь до первой попытки прочитать «поле не прислали» — а различать эти два случая
 * приходится ровно там, где одна галочка не должна помечать к замене всю пачку аппаратов.
 *
 * Принимает их сервер только у ремонта и только при `status === 'cancelled'`; на административном
 * откате непустые значения — `422`, а не молчаливое игнорирование: отменённая заявка могла
 * остаться открытой в чужом браузере, и «сохранилось, но не сохранилось» — худший из исходов.
 */
export const serviceStatusChangeSchema = z.object({
  status: serviceRequestStatusSchema,
  reason: z.string().trim().max(1000).optional().default(''),
  /** «Что делаем вместо ремонта» — то же поле заявки, что пишет отказ по объёму работ. */
  resolution: z.string().trim().max(500).optional(),
  replacementRecommended: z.boolean().optional(),
  version: z.number().int().nonnegative(),
});
export type ServiceStatusChangeInput = z.infer<typeof serviceStatusChangeSchema>;

// ── Массовые действия над заявками (план `docs/office-equipment-bulk-actions-plan.md`) ──
/**
 * ОДНА ИЗМЕНЯЮЩАЯ РУЧКА НА ВСЕ ОПЕРАЦИИ (Р1), и потому — размеченное объединение по `operation`.
 *
 * Девять пакетных ручек означали бы девять мест, где написан один и тот же протокол (версии, ключ
 * идемпотентности, отчёт, порядок строк, аудит), и первое же исправление разъехалось бы по ним.
 * Цена решения названа прямо: союз в схеме и отдельная строка в обоих реестрах доступа — и она
 * меньше девяти копий протокола.
 *
 * ЧТО ЗДЕСЬ НЕ ЗАВОДИТСЯ: ни одного нового правила доменной проверки. Тело описывает КОМАНДУ, а
 * «можно ли её на этой строке» отвечают те же предикаты, что и одиночной ручке (Н9, Р2).
 */

/**
 * Потолок пачки. Число выбрано не наугад: это наименьший размер страницы (`PAGE_SIZE_OPTIONS[0]`),
 * то есть «выбрать всё на странице» при обычной настройке в него не упирается, а при странице в
 * 100+ портал говорит об этом до нажатия (Р13). Потолок держит и время: 50 строк — это 50
 * транзакций со сборкой писем.
 */
export const SERVICE_REQUEST_BULK_LIMIT = 50;

/**
 * Девять вариантов команды. Включение и снятие срочности считаются отдельно — причина у них
 * разная: включение требует объяснения, снятие не требует вовсе (§4).
 *
 * Чего здесь нет и почему: возврат на доработку (причина у каждой строки своя), закрытие работ
 * (факт по каждой строке сметы и закрывающий документ), объём работ и его согласование (деньги
 * строками), административные откаты (каждый откат разбирает свою ошибку). У каждого «нет» в §4
 * названа причина, а не «пока не будем».
 */
export const SERVICE_REQUEST_BULK_OPERATIONS = [
  'cancel',
  'hold',
  'resume',
  'urgency_on',
  'urgency_off',
  'assign',
  'start',
  'accept',
  'archive',
] as const;
export type ServiceRequestBulkOperation = (typeof SERVICE_REQUEST_BULK_OPERATIONS)[number];
export const serviceRequestBulkOperationSchema = z.enum(SERVICE_REQUEST_BULK_OPERATIONS);

/** Человеческое имя операции: подпись кнопки, строка отчёта и тема письма-сводки. */
export const serviceRequestBulkOperationLabels: Record<ServiceRequestBulkOperation, string> = {
  cancel: 'Отменить',
  hold: 'Отложить',
  resume: 'Возобновить',
  urgency_on: 'Поставить срочность',
  urgency_off: 'Снять срочность',
  assign: 'Назначить исполнителей',
  start: 'Принять в работу',
  accept: 'Принять работу',
  archive: 'Архивировать',
};

/**
 * Пара «идентификатор + версия» (Р4). Версия не необязательна: список сортирован по возрасту
 * ожидания, соседняя вкладка живёт своей жизнью, и выбранная строка успевает уехать между показом
 * и нажатием (Н3). Присланная версия и есть ответ «я собирался сделать это с тем, что видел».
 */
const bulkRowSchema = z.object({ id: uuidSchema, version: z.number().int().nonnegative() });

/**
 * Список строк. Повтор идентификатора — ошибка ЗАПРОСА, а не отказ строки: приём показаний
 * (`accept-batch`) отвечает так же, и по делу — «примени дважды» это не команда, а сбитый клиент,
 * и построчный отказ спрятал бы поломку в отчёте.
 */
const bulkRowsSchema = z
  .array(bulkRowSchema)
  .min(1, 'Выберите хотя бы одну заявку')
  .max(
    SERVICE_REQUEST_BULK_LIMIT,
    `За один раз обрабатывается не больше ${SERVICE_REQUEST_BULK_LIMIT} заявок`,
  )
  .superRefine((rows, ctx) => {
    const seen = new Set<string>();
    for (const [index, row] of rows.entries()) {
      if (seen.has(row.id)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Заявка названа в списке дважды',
          path: [index, 'id'],
        });
      }
      seen.add(row.id);
    }
  });

/**
 * Тело пакетной команды.
 *
 * ПРИЧИНЫ — ТЕМИ ЖЕ СХЕМАМИ, что у одиночных ручек (`reasonSchema`, `urgencyReasonSchema`).
 * Обязательность их здесь местами строже: первое назначение поодиночке причины не требует, а
 * массовое требует всегда (§4) — «раскидать двадцать заявок» это решение о чужой работе, и одно
 * объяснение на пачку честно описывает все её строки.
 *
 * Отсутствие обязательной причины — отказ на ВЕСЬ запрос (Р5): валидность тела не должна зависеть
 * от ещё не прочитанных состояний строк.
 */
export const serviceRequestBulkSchema = z
  .discriminatedUnion('operation', [
    z.object({ operation: z.literal('cancel'), rows: bulkRowsSchema, reason: reasonSchema }),
    z.object({ operation: z.literal('hold'), rows: bulkRowsSchema, reason: reasonSchema }),
    z.object({
      operation: z.literal('resume'),
      rows: bulkRowsSchema,
      comment: z.string().trim().max(1000).default(''),
    }),
    z.object({
      operation: z.literal('urgency_on'),
      rows: bulkRowsSchema,
      urgencyReason: urgencyReasonSchema,
    }),
    z.object({ operation: z.literal('urgency_off'), rows: bulkRowsSchema }),
    z.object({
      operation: z.literal('assign'),
      rows: bulkRowsSchema,
      /** Состав приходит целиком, как у одиночной ручки: «добавить одного» сервер не угадывает. */
      userIds: z.array(uuidSchema).max(20),
      serviceCounterpartyId: uuidSchema.nullable(),
      reason: reasonSchema,
      comment: z.string().trim().max(1000).default(''),
    }),
    z.object({ operation: z.literal('start'), rows: bulkRowsSchema }),
    z.object({
      operation: z.literal('accept'),
      rows: bulkRowsSchema,
      comment: z.string().trim().max(1000).default(''),
    }),
    z.object({ operation: z.literal('archive'), rows: bulkRowsSchema }),
  ])
  /*
   * Пара «флаг + причина» у включения срочности проверяется на союзе, а не внутри ветки:
   * `z.discriminatedUnion` разбирает объекты по литералу, и ветка, обёрнутая в `superRefine`,
   * перестала бы им быть (тот же приём, что у двери ремонта в `assignment-periods.ts`).
   *
   * Спрашивается это ТЕМ ЖЕ `urgencyIssue`, что и одиночная ручка: сообщение человеку обязано
   * совпасть слово в слово, иначе форма пачки объясняла бы отказ иначе, чем форма заявки.
   */
  .superRefine((value, ctx) => {
    if (value.operation !== 'urgency_on') return;
    const issue = urgencyIssue({ isUrgent: true, urgencyReason: value.urgencyReason });
    if (issue) ctx.addIssue({ code: 'custom', message: issue, path: ['urgencyReason'] });
  });
export type ServiceRequestBulkInput = z.infer<typeof serviceRequestBulkSchema>;
export type ServiceRequestBulkBody = z.input<typeof serviceRequestBulkSchema>;

/** Ключ пачки в пути читающей ручки: тот же `Idempotency-Key`, которым её и запускали. */
export const serviceRequestBulkKeyParams = z.object({ key: uuidSchema });

/**
 * Коды отказа строки — ГРУБЫЕ, для портала (§6.2).
 *
 * Грубость намеренная: отсутствие заявки, её архивность, чужая область и непредвиденный сбой
 * получают общие тексты, чтобы отчёт не стал способом перебирать чужие UUID или читать
 * внутренности базы. Безопасные доменные объяснения (422 своей ручки) уходят в `reason`.
 */
export const SERVICE_REQUEST_BULK_FAILURE_CODES = [
  /** Строка изменилась между показом и нажатием — присланная версия разошлась. */
  'version',
  /** Состояние заявки хода не даёт: не тот статус, висит предъявление, исполнителей нет. */
  'blocked',
  /** Право, область или сторона: субъекту эта строка недоступна (Н7 — построчно, а не на запрос). */
  'forbidden',
  /** Заявки нет либо она в архиве — один код на оба случая, чтобы не раскрывать существование. */
  'gone',
  /** Пачку бросили: строка так и не была обработана, а операция закрыта уборкой (§6.3). */
  'abandoned',
  /** Непредвиденный сбой: человеку общий текст, разбор — в логе по `operationId`. */
  'error',
] as const;
export type ServiceRequestBulkFailureCode = (typeof SERVICE_REQUEST_BULK_FAILURE_CODES)[number];

/** Состояние пачки: `finishing_notifications` — строки готовы, digest ещё не поставлен (Р10). */
export const SERVICE_REQUEST_BULK_STATES = [
  'running',
  'finishing_notifications',
  'finished',
] as const;
export type ServiceRequestBulkState = (typeof SERVICE_REQUEST_BULK_STATES)[number];

/**
 * Коды 409 пакетной ручки. Своими кодами, а не общим `version_conflict`: исходы у них разные —
 * занятый ключ означает «возьмите новый», а выполняющаяся пачка означает «читайте состояние».
 */
export const SERVICE_REQUEST_BULK_CONFLICT_CODES = {
  /** Ключ занят ДРУГОЙ командой: тело под ним уже принято другим отпечатком. */
  idempotency: 'bulk_idempotency',
  /** Ключ занят ТОЙ ЖЕ командой, и она сейчас выполняется: клиент читает `GET /bulk/:key`. */
  inProgress: 'bulk_in_progress',
} as const;

export interface ServiceRequestBulkRowResultDto {
  /** Исходная позиция: по ней восстанавливается порядок и называется скрытая строка. */
  index: number;
  id: string;
  /** `null` у невидимой либо несуществующей строки — номер чужой заявки не раскрывается. */
  displayNumber: string | null;
  outcome: 'done' | 'failed';
  code?: ServiceRequestBulkFailureCode;
  /** Безопасный текст: отсутствие заявки и чужая область объясняются общими словами. */
  reason?: string;
}

export interface ServiceRequestBulkResultDto {
  /** Строка журнала пачек: по ней читается повтор и наблюдаемость. */
  operationId: string;
  operation: ServiceRequestBulkOperation;
  done: number;
  failed: number;
  /** Перед ответом сортируются по `index`: порядок отчёта — порядок запроса (Р3). */
  rows: ServiceRequestBulkRowResultDto[];
}

export interface ServiceRequestBulkStatusDto {
  operationId: string;
  state: ServiceRequestBulkState;
  requested: number;
  processed: number;
  /** До завершения — `null`: отчёт появляется целиком и сразу (Н12). */
  result: ServiceRequestBulkResultDto | null;
}

/**
 * ДОПУСК К МАССОВОМУ РЕЖИМУ (Р5) — продуктовое ограничение поверх прав, а не новое право.
 *
 * Требует хотя бы одного ОПЕРАЦИОННОГО права: `assign | status | execute | hold | urgency`.
 * Наличие только `read/create/update/delete/estimate` его не удовлетворяет, и это закрывает Н11:
 * у заявителя есть `serviceRequests.delete` на свою новую заявку, но пункт 11 разбора прямо
 * исключает его из массового интерфейса — иначе прямой вызов API вернул бы возможность, которую
 * продукт не давал.
 *
 * `estimate` здесь нет НАМЕРЕННО, и это единственное место, где пакет строже одиночной ручки.
 * У одиночного «Принять в работу» третья ветка `actsAsServiceExecutor` открывает ход держателю
 * `serviceRequests.estimate` без назначения на заявку; в массовом режиме такой субъект набрал бы
 * пятьдесят чужих назначенных заявок и взял их в работу одним нажатием, не будучи исполнителем ни
 * одной. Прав это ни у кого не отнимает — одиночная кнопка в карточке работает по-прежнему.
 *
 * Ту же функцию спрашивает портал, решая, показывать ли полосу выбора: разойдись они, чекбоксы
 * вели бы в 403.
 */
export function canUseServiceBulk(
  subject: AccessSubject | null | undefined,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  return (
    has('serviceRequests.assign') ||
    has('serviceRequests.status') ||
    has('serviceRequests.execute') ||
    has('serviceRequests.hold') ||
    // Cancel is a right of its own (workflow-fixes plan Р2) and the top gate of the batch together
    // with the route guard: without this line a holder of the cancel alone would not pass the
    // admission, while the IT service would be let through «on the side» by its `assign` and `hold`.
    has('serviceRequests.cancel') ||
    has('serviceRequests.urgency')
  );
}

/**
 * ТОЧНОЕ ПРАВО ОПЕРАЦИИ — второй рубеж после допуска и до построчного предиката (Р5).
 *
 * Отвечает ровно на вопрос «бывает ли У ЭТОГО СУБЪЕКТА такая команда вообще»; «на этой ли строке»
 * решает доменный шаг, и подменять один вопрос другим нельзя ни в какую сторону. Условия взяты у
 * одиночных ручек и второй раз не выводятся:
 *
 *   · отмена — `serviceRequests.status` ∨ `serviceRequests.cancel` (Р2);
 *   · приёмка работы — `serviceRequests.status` (коридор оператора);
 *   · заморозка и возврат — `canHoldService` (`hold ∨ status`, и исполнителю закрыто вовсе);
 *   · срочность — `serviceRequests.urgency`;
 *   · назначение — `serviceRequests.assign`;
 *   · «принять в работу» — `status ∨ execute`, как у стража одиночной ручки;
 *   · архивирование — `serviceRequests.delete`.
 */
export function canRunServiceBulkOperation(
  subject: AccessSubject | null | undefined,
  operation: ServiceRequestBulkOperation,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  switch (operation) {
    case 'cancel':
      // `status` or the cancel right of its own (workflow-fixes plan Р2): the corridor answers «what exactly».
      return has('serviceRequests.status') || has('serviceRequests.cancel');
    case 'accept':
      return has('serviceRequests.status');
    case 'hold':
      return canHoldService(subject, has);
    case 'resume':
      return canResumeService(subject, has);
    case 'urgency_on':
    case 'urgency_off':
      return has('serviceRequests.urgency');
    case 'assign':
      return has('serviceRequests.assign');
    case 'start':
      return has('serviceRequests.status') || has('serviceRequests.execute');
    case 'archive':
      return has('serviceRequests.delete');
  }
}

// ── Файлы ──

export const SERVICE_FILE_KINDS = [
  'attachment',
  'estimate',
  'act',
  'invoice',
  'warranty_card',
] as const;
export const serviceFileKindSchema = z.enum(SERVICE_FILE_KINDS);
export type ServiceFileKind = (typeof SERVICE_FILE_KINDS)[number];

export const serviceFileKindLabels: Record<ServiceFileKind, string> = {
  attachment: 'Вложение',
  // Р17: вид документа переименован вместе с разделом — «Объём работ», к которому подшивают файл
  // вида «Смета», заставлял бы искать между ними разницу, которой нет. Имя значения (`estimate`)
  // при этом прежнее: оно стоит в схеме базы, и переименование ради подписи стоило бы миграции.
  estimate: 'Объём работ',
  act: 'Акт',
  invoice: 'Счёт',
  warranty_card: 'Гарантийный талон',
};

/**
 * РОЛЬ ФАЙЛА В ЗАЯВКЕ (Р5 плана `docs/office-equipment-on-site-and-invoice-estimate-plan.md`): чем
 * подшитый документ является — подтверждением работ или ОСНОВАНИЕМ денежного решения.
 *
 * РОЛЬ НАЗНАЧАЕТ СЕРВЕР, КЛИЕНТ ЕЁ НЕ ВЫБИРАЕТ. `estimate_basis` ставится ровно одной дорогой —
 * подшивкой из команды предъявления (Р7), — и только виду `invoice`; в базе это закреплено
 * `CHECK (purpose <> 'estimate_basis' OR (kind = 'invoice' AND estimate_revision IS NOT NULL))`.
 * Прими роль от клиента обычная подшивка — и фотография поломки стала бы основанием автоподписи, а
 * снять её было бы уже нельзя: основание не снимается никогда (Р6).
 *
 * `closing_evidence` — УМОЛЧАНИЕ И СЕГОДНЯШНЕЕ ПОВЕДЕНИЕ: им миграция размечает все существующие
 * связи, и им же читается отсутствие значения в окне выката. Раздай умолчание обратную роль —
 * предикат закрывающего документа счёл бы любое вложение подтверждением работ.
 */
export const SERVICE_FILE_PURPOSES = ['closing_evidence', 'estimate_basis'] as const;
export const serviceFilePurposeSchema = z.enum(SERVICE_FILE_PURPOSES);
export type ServiceFilePurpose = (typeof SERVICE_FILE_PURPOSES)[number];

export const serviceFilePurposeLabels: Record<ServiceFilePurpose, string> = {
  closing_evidence: 'Подтверждение работ',
  estimate_basis: 'Основание объёма работ',
};

/**
 * Виды документов, которыми подтверждают работу. Их можно подшивать и после приёмки — «акт пришлю
 * завтра» иначе означало бы потерянную бумагу (Р16, Р29); удалять их после приёмки нельзя.
 */
export const SERVICE_CLOSING_DOCUMENT_KINDS: readonly ServiceFileKind[] = [
  'act',
  'invoice',
  'warranty_card',
];

/**
 * ТОЛЬКО ПОЛОВИНА ПРАВИЛА — вид без роли и без формата ревизии (Р5). Спрашивать её в одиночку можно
 * ровно там, где речь о самом ВИДЕ документа: «такой вид нельзя снимать после приёмки», «такой вид
 * разрешено подшивать к закрытой заявке». Вопрос «закрывает ли ЭТОТ ФАЙЛ ЭТУ ЗАЯВКУ» она не
 * отвечает — на него отвечает `isServiceClosingFile` ниже, и читает он два признака, а не один.
 */
export function isServiceClosingDocument(kind: ServiceFileKind): boolean {
  return SERVICE_CLOSING_DOCUMENT_KINDS.includes(kind);
}

/**
 * Виды, которые закрывают заявку у ДОКУМЕНТНОЙ ревизии, — только акт (ответ В10 заказчика от
 * 11.09.2026: «закрывает только акт, и только у документного режима»).
 *
 * Отдельной константой, а не литералом внутри функции: тот же перечень переписывается на SQL для
 * очереди «Ожидаются документы» и отбора автозакрытия, и матричный тест сверяет две реализации
 * правила по клеткам `вид × роль × формат`.
 */
export const SERVICE_DOCUMENT_FORMAT_CLOSING_KINDS: readonly ServiceFileKind[] = ['act'];

/**
 * ПЕРВЫЙ ИЗ ДВУХ ПРИЗНАКОВ ЗАКРЫВАЮЩЕГО ДОКУМЕНТА (Р5): какие виды закрывают заявку ПРИ ЭТОМ
 * формате действующей ревизии.
 *
 * ЗАЧЕМ ФОРМАТ ВООБЩЕ СПРАШИВАЕТСЯ. У документной подачи сам счёт и есть объём работ: сочти его
 * закрывающим — и заявка закрылась бы тем самым документом, которым её открыли, без акта о
 * выполненных работах (уточнение заказчика 10–11.09.2026: «документ — это счёт, но закрывается
 * заявка актом»).
 *
 * ПЛАНКА НАСЛЕДИЯ НЕ МЕНЯЕТСЯ ВОВСЕ. У построчной и гарантийной ревизии, у заявки без ревизии и у
 * ответа старого приложения (`null`) перечень сегодняшний: счёт закрывает, как и закрывал.
 * Переинтерпретировать прошлое новым правилом значило бы задним числом открыть заявки, которые
 * служба считала закрытыми.
 *
 * `undefined` В ТИП НЕ ВХОДИТ, и это не придирка к форме. Поле карточки необязательно — сервер его
 * отдаёт, а вот ответ СТАРОГО приложения в окне выката его не несёт, — и пройди `undefined` в
 * аргумент молча, «формата в ответе нет» и «наследственное правило выбрано осознанно» стали бы одним
 * значением. Зовущий пишет `?? null` и
 * этим отвечает за выбор сам.
 */
export function closingKindsForFormat(
  format: ServiceEstimateFormat | null,
): readonly ServiceFileKind[] {
  return format === 'document'
    ? SERVICE_DOCUMENT_FORMAT_CLOSING_KINDS
    : SERVICE_CLOSING_DOCUMENT_KINDS;
}

/**
 * ЗАКРЫВАЮЩИЙ ЛИ ЭТО ФАЙЛ — правило из ДВУХ признаков (Р5): роль файла И вид, допустимый формату
 * действующей ревизии.
 *
 * ОДНОЙ РОЛИ МАЛО: `estimate_basis` — это всегда `invoice`, и предикат, читающий только вид, счёл бы
 * основание автоподписи закрывающим документом той же заявки, то есть закрыл бы её тем, чем открыл.
 * ОДНОГО ВИДА МАЛО С ДРУГОЙ СТОРОНЫ: акт закрывает при любом формате, и перечень без роли пустил бы
 * в него счёт-основание обратно.
 *
 * РОЛЬ ЧИТАЕТСЯ С УМОЛЧАНИЕМ `closing_evidence`, и это обязательное послабление окна выката: старое
 * приложение `purpose` не отдаёт вовсе, а миграционное умолчание у существующих связей ровно такое.
 * Fail-open здесь безопасен по построению — документных ревизий до выпуска читателей не существует,
 * ручка предъявления принимает формат `document` только следующим выпуском (§7, шаги 1–3).
 *
 * А ВОТ ФОРМАТ ТАКОГО ПОСЛАБЛЕНИЯ НЕ ПОЛУЧАЕТ — он обязательный аргумент (та же причина, что у
 * `hasServiceClosingDocument` ниже): перечень мест, которые Э3 обязан перевести на новое правило,
 * должен приходить от компилятора, а не из памяти. С умолчанием все семь потребителей (Н8)
 * собрались бы без единой правки и продолжали отвечать старой планкой.
 */
export function isServiceClosingFile(
  file: { kind: ServiceFileKind; purpose?: ServiceFilePurpose | null },
  format: ServiceEstimateFormat | null,
): boolean {
  if ((file.purpose ?? 'closing_evidence') !== 'closing_evidence') return false;
  return closingKindsForFormat(format).includes(file.kind);
}

/**
 * Планка приёмки (Р112): хватает **любого** закрывающего документа — у построчной заявки это акт,
 * счёт или гарантийный талон, у документной только акт (Р5). Ответ булев, а не перечень недостающих
 * видов: перечисление читалось бы как «нужны все три», хотя запирает приёмку отсутствие всех сразу.
 *
 * Функция живёт в контрактах, потому что спрашивают её оба: сервер — отказом в приёмке, портал —
 * неактивной кнопкой. Разойдись они, кнопка вела бы в 422.
 *
 * ФОРМАТ — ОБЯЗАТЕЛЬНЫЙ ВТОРОЙ АРГУМЕНТ, А НЕ НЕОБЯЗАТЕЛЬНОЕ ПОЛЕ ОБЪЕКТА, и это ответ ревью: у
 * забытого формата умолчание означало бы «счёт закрывает заявку» — самую дорогую из двух ошибок
 * (документная заявка закрылась бы тем же счётом, которым открыта). Необязательным он не отличал бы
 * читателя, ОСОЗНАННО передавшего наследственный `null`, от читателя, который про формат не знает
 * вовсе, — и список работ по переводу семи потребителей (Н8) на новое правило Э3 получал бы от
 * плана, а не от компилятора. Теперь забыть его нельзя: каждый зовущий пишет либо поле карточки,
 * либо явный `null`.
 *
 * `null` ЗНАЧЕНИЕМ по-прежнему законен и означает сегодняшнюю планку: ревизии нет вовсе либо
 * отвечает приложение, не знающее о волне (см. `closingKindsForFormat`). `undefined` в тип не
 * входит намеренно — читателю с необязательным полем карточки приходится написать `?? null`, то
 * есть СКАЗАТЬ вслух, что он спрашивает планку наследия. Сервер передаёт формат, прочитанный под
 * блокировкой: между открытием окна и нажатием помещается раскладка «Ведения», переводящая ревизию
 * в построчную (Р8).
 */
export function hasServiceClosingDocument(
  request: Pick<ServiceRequestDto, 'files'>,
  format: ServiceEstimateFormat | null,
): boolean {
  return request.files.some((file) => isServiceClosingFile(file, format));
}

/**
 * Contractor repairs require payment evidence before ordinary completion (N8). In-house repairs
 * and consumables do not. The explicit coordinator override has its own predicate below; keeping
 * this ordinary requirement intact also keeps auto-close, portal actions and refusal text aligned.
 *
 * The kind matters even though contractors do not currently receive consumables requests: a
 * future contractor delivery must not silently acquire a repair-document requirement. This pair
 * works for both database rows and portal DTOs without making either representation authoritative.
 */
export function serviceRequestNeedsClosingDocument(request: {
  kind: ServiceRequestKind;
  /** A null counterparty means an in-house executor, with no external service payment. */
  serviceCounterpartyId: string | null;
}): boolean {
  return request.kind === 'repair' && request.serviceCounterpartyId !== null;
}

/** A coordinator may take responsibility for a contractor repair without closing evidence. */
export function canForceCompleteServiceWithoutDocument(
  request: { kind: ServiceRequestKind; serviceCounterpartyId: string | null },
  subject: AccessSubject | null | undefined,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  return (
    !!subject &&
    !actsForCounterparty(subject, 'service') &&
    has('serviceRequests.status') &&
    serviceRequestNeedsClosingDocument(request)
  );
}

/**
 * Нужен ли по заявке объём работ (Р4 плана `office-equipment-card-and-list-cleanup-plan.md`).
 *
 * Пара признаков та же, что у закрывающего документа, и это не совпадение: платит компания за
 * работу подрядчика, и обе бумаги — про деньги. Свой сисадмин стоимости не фиксирует вовсе
 * (просьба заказчика 08.09.2026, п. 2), поэтому предъявлять, согласовывать и возвращать в правку у
 * внутреннего ремонта нечего.
 *
 * Одной функцией, а не строкой по месту: правило спрашивают три предиката объёма работ, предикат
 * назначения, четыре ручки сметы, закрытие работ, подшивка документа, вкладка и окно закрытия.
 * Первым признаком разошедшихся копий стала бы карточка, где кнопка «Объём работ» есть, а сервер
 * отвечает на неё отказом.
 *
 * Вид в условии не лишний, хотя у расходников объёма работ не бывает и по другой причине: правило
 * обязано отказывать по той причине, по которой его писали, — иначе в день, когда картриджи
 * повезёт подрядчик, оно молча потребует от него смету.
 */
export function serviceRequestNeedsEstimate(request: {
  kind: ServiceRequestKind;
  /** Исполнитель-контрагент; `null` — заявку ведёт свой сотрудник, и цены по ней не считают. */
  serviceCounterpartyId: string | null;
}): boolean {
  return request.kind === 'repair' && request.serviceCounterpartyId !== null;
}

/**
 * Whose shelf the consumables of this request come from (workflow-fixes plan Р8, owner answer of
 * 08.09.2026: «склад вообще не двигается»): ours exactly when it is a consumables request with NO
 * contractor on it. If the service centre brings the cartridges, there is nothing to write off our
 * shelf, and nobody — «Ведение» and the in-house executor included — marks an issue on such a
 * request.
 *
 * The pair «kind + contractor» and not the reader, like `serviceRequestNeedsEstimate`: it describes
 * the REQUEST, and asked of the server row and of the portal card alike. A mixed assignment (an own
 * administrator next to a service company) is NOT ours: the contractor is on the request, and the
 * issue mark has no author who could be right.
 *
 * Every consumer asks this one function: the issue door, the closing (which then does not touch the
 * warehouse at all), the issue item of the menu and the closing window. A copy in any of them would
 * show a button the server answers with a refusal — or move the shelf the owner said must not move.
 */
export function serviceRequestConsumablesAreOurs(request: {
  kind: ServiceRequestKind;
  /** The contractor on the request; `null` — nobody from outside is assigned. */
  serviceCounterpartyId: string | null;
}): boolean {
  return request.kind === 'consumable' && request.serviceCounterpartyId === null;
}

/**
 * Is the nomenclature of this consumables request hidden from this reader (workflow-fixes plan Р7).
 * True for the contractor's side on a request of kind `consumable`: what exactly is taken from the
 * shelf is the company's own business, and the owner asked to remove its visibility from the card
 * and the documents (§1.2 of the plan).
 *
 * A PAIR «REQUEST × READER», NOT A FLAG OF THE REQUEST. The composition is hidden from one reader,
 * and the same rows are filled in by the in-house executor and «Ведение»: applied to the request
 * alone, the rule would hide the list from those who fill it in.
 *
 * The reader is asked by his counterparty type, not by his assignment to THIS request: the
 * contractor sees only requests assigned to his company anyway (the scope answers that), and a type
 * check is fail-closed for the one who slipped through. The server applies the rule on EVERY
 * channel that carries the nomenclature — the card and the list, the history, the file list and the
 * direct link, the composition and the document doors, the document letter — and the portal's tabs
 * and items are a consequence of it, never the rule itself: a thing hidden by markup stays in the
 * response.
 */
export function serviceConsumablesHiddenFrom(
  request: { kind: ServiceRequestKind },
  subject: AccessSubject | null | undefined,
): boolean {
  return (
    serviceConsumablesHiddenFromContractors(request) && actsForCounterparty(subject, 'service')
  );
}

/**
 * The request half of `serviceConsumablesHiddenFrom`: is the nomenclature of this request hidden
 * from the contractor's side as such. For readers who are not subjects with a counterparty type —
 * the recipients of a letter, planned before anyone is signed in — this is the whole question, and
 * the letter planner asks it here instead of repeating «kind is consumable».
 */
export function serviceConsumablesHiddenFromContractors(request: {
  kind: ServiceRequestKind;
}): boolean {
  return request.kind === 'consumable';
}

/**
 * ДЕЙСТВУЮЩЕЕ ожидание подписи — в отличие от сырой колонки `estimate_pending_revision` (Н11).
 *
 * Колонка отвечает на вопрос «предъявляли ли и получили ли ответ», и у исторической внутренней
 * заявки она законно непуста: до этой волны объём работ был обязателен любому ремонту. Спроси её
 * очередь напрямую — такая заявка вечно ждала бы согласования, которого после Р5 некому дать, и
 * вечно же запрещала бы переназначение. Отвечать «ждут подписи» вправе только та заявка, у которой
 * подпись вообще бывает.
 *
 * Сырой признак при этом остаётся и в БД, и в DTO: историю мы не переписываем — Р7 лишь перестаёт
 * считать её рабочим ожиданием.
 */
export function serviceRequestHasEffectivePendingEstimate(request: {
  kind: ServiceRequestKind;
  serviceCounterpartyId: string | null;
  estimatePendingRevision: number | null;
}): boolean {
  return serviceRequestNeedsEstimate(request) && serviceEstimatePending(request);
}

/**
 * Подшивка документов к заявке. РОЛИ ФАЙЛА (`purpose`) ЗДЕСЬ НЕТ И НЕ БУДЕТ (Р5): её назначает
 * сервер, и `estimate_basis` ставится ровно одной дорогой — подшивкой внутри команды предъявления
 * (Р7). Прими эта ручка роль от клиента — любое вложение можно было бы объявить основанием
 * автоподписи, а снять его потом нельзя: основание не снимается никогда (Р6). Вид документа клиент
 * выбирает по-прежнему — он описывает бумагу, а не её юридическую роль в заявке.
 */
export const attachServiceFilesSchema = z.object({
  fileIds: z.array(uuidSchema).min(1).max(20),
  kind: serviceFileKindSchema.optional().default('attachment'),
});

// ── DTO ──

/**
 * Как называется предмет заявки, у которой аппарата нет (Р8, ADR 0146, решение 7).
 *
 * Константой в контрактах, а не строкой по месту: одно и то же положение вещей называют письмо,
 * заголовок действия в журнале, строка списка, карточка и мобильная карточка портала. Разойдись
 * они в словах — человек решил бы, что «Без аппарата» и «Аппарат не указан» это два разных
 * состояния, и пошёл бы искать разницу, которой нет.
 *
 * Пустая строка вместо этих слов не годится: пробел в заголовке письма читается как потерянные
 * данные, а не как законное состояние заявки.
 */
export const SERVICE_REQUEST_NO_EQUIPMENT = 'Без аппарата';

export interface ServiceRequestEquipmentDto {
  id: string;
  /** Реквизиты — снимок на момент заведения: карточку могли переименовать и перезакрепить. */
  name: string;
  serialNumber: string;
  inventoryNumber: string;
  typeName: string;
  /** Место внутри объекта на момент заведения: «Корпус 3, каб. 214». Тот же снимок (Р57). */
  location: string;
  /**
   * Срок гарантии поставщика на саму единицу — ЖИВОЕ значение справочника, а не снимок заявки
   * (Ф3 плана `docs/office-equipment-candidate-plan.md`, §13).
   *
   * ПОЧЕМУ НЕ СНИМОК, хотя все соседние поля блока — снимок. Реквизиты отвечают на вопрос «что
   * чинили тогда», и переименованная карточка не вправе переписать прошлогоднюю заявку. Гарантия
   * отвечает на другой вопрос — «действует ли она СЕЙЧАС», по нему и решают, чинить за деньги или
   * требовать с поставщика. Снятый при заведении срок отвечал бы на него неверно ровно в тех
   * случаях, ради которых колонку завели: талон нашли и вписали в карточку через неделю после
   * заявки, гарантию продлили, ошибочную дату исправили. Снимок пришлось бы обновлять вторым
   * действием и человеком, который обязан не забыть, — а не обновлённый молчал бы правдоподобно.
   *
   * ПОЧЕМУ НЕ ВТОРЫМ ЗАПРОСОМ, как было до Ф3. Колонка «Гарантия» в списке заявок питалась
   * справочником целиком — страницей в 500 строк (`DICTIONARY_PAGE_SIZE`), — и это тот же потолок,
   * что у селектора формы (Н11): парк перерастает страницу, и колонка замолкает у части строк
   * молча, в первую очередь у площадок в конце алфавита. Поисковым срезом это не лечится: у
   * колонки нет поискового запроса, ей нужен ответ про единицу КАЖДОЙ показанной заявки, а срез в
   * полсотни строк обнулил бы её почти везде. Лечится источником — соединением при выдаче: список
   * и карточка соединяются с этой же карточкой ради названия типа и пометки расхождения (Р16), и
   * лишнего обращения к базе поле не стоит.
   *
   * `null` — срока в карточке нет: гарантию не знают, а не «её нет» (`WarrantyTag` рисует на это
   * прочерк, а не тег «без гарантии»). Состояния «показывать нечего» у поля не бывает вовсе, и это
   * не упущение, а причина, по которой оно лежит ЗДЕСЬ, внутри блока предмета, а не рядом с
   * `equipment` в самом DTO: у заявки без аппарата (Р8) и у заявки с непроверенным кандидатом
   * (Р2 плана кандидата) блока нет целиком — карточки, у которой бывает гарантия, ещё не
   * существует, — и «спросить не у чего» выражено устройством типа, а не договорённостью о
   * значении `null`.
   *
   * ПОЛЕ ЗАКРЫТО ТОМУ, КОМУ ЗАКРЫТ СПРАВОЧНИК (решение владельца 04.09.2026). Единственное поле
   * блока, приходящее не из снимка заявки, а из живой карточки парка, — и видно оно ровно тому, у
   * кого есть `officeEquipment.read`. У сервисной компании его нет намеренно
   * (`COUNTERPARTY_TYPE_PERMISSIONS.service`): «её» техника в справочнике ничем не отмечена, право
   * означало бы доступ ко всему парку, и реквизиты нужной единицы приходят подрядчику снимком в
   * самой заявке. Отдав ему сверх снимка живую колонку справочника, Ф3 открыл бы боковую дверь в
   * закрытый ему справочник — по одной дате за заявку, зато на всех видимых ему заявках сразу.
   * Сервису при этом приходит тот же `null`, и отдельного «нельзя показывать» в форме ответа нет:
   * прочерк читается как «срок не заведён», то есть портал молчит, а не отвечает за сервер.
   *
   * ГЕЙТ СТОИТ В СБОРКЕ DTO, А НЕ В `SERVICE_REQUEST_FIELD_AUDIENCE` (ADR 0160), и это решение, а
   * не место, куда пришлось. Карта аудиторий отвечает на вопрос «видны ли этому читателю ДЕНЬГИ
   * ЭТОЙ заявки» и считается по правам заявки и назначению (`serviceRequestAudienceOf`); здесь
   * вопрос другой — «открыт ли ему справочник», — и ответ на него даёт другое право. Свести их в
   * одну карту значило бы либо третью аудиторию («деньги без справочника»), то есть удвоение
   * решений в каждой строке карты, либо вторую карту видимости рядом с первой — а две карты
   * расходятся молча, ровно как расходятся две копии любого правила в этом модуле. Поэтому
   * закрытие живёт там же, где поле и рождается: в единственной сборке блока предмета
   * (`toDto` в `routes/service-requests.ts`), одним условием рядом со значением.
   */
  warrantyUntil: string | null;
}

/**
 * ТРЕТИЙ СПОСОБ НАЗВАТЬ ПРЕДМЕТ ЗАЯВКИ — сообщение об аппарате, которого в справочнике ещё нет
 * (план `docs/office-equipment-candidate-plan.md`, Р2, Р5, Р15, Р16, §9).
 *
 * Блок отвечает на два вопроса карточки заявки и больше ни на что: «чем назван предмет, пока
 * карточки парка не существует» и «чем кончилась проверка». Ими живут четыре места портала —
 * подпись предмета, плашка состояния, замок приёмки (Р16) и строка списка, — и все четыре читают
 * ОДИН ответ сервера.
 *
 * ПОЧЕМУ ЭТО НЕ `OfficeEquipmentCandidateDto`, а свой, у́же. У кандидата свой DTO с версией
 * содержимого, автором, типом, площадкой, местом, комментарием, именем решившего и ссылкой на
 * заведённую карточку — всё это предмет ОЧЕРЕДИ ПРОВЕРКИ, у которой своя область (Р9) и свои
 * ручки. Встроить его в заявку целиком значило бы отдать область очереди всякому, кто видит
 * заявку: заявку с кандидатом видят автор, служба и назначенный исполнитель, а очередь — только
 * держатель `officeEquipment.review`. Шесть полей ниже — пересечение этих двух кругов, и оно
 * выбрано составом, а не проекцией: «то же DTO, но с вычеркнутыми полями» пришлось бы вычёркивать
 * заново при каждом новом поле кандидата, и первое же забытое уехало бы наружу молча.
 *
 * ПОЧЕМУ БЛОК ЕДЕТ В ЗАЯВКЕ, А НЕ СПРАШИВАЕТСЯ ОТДЕЛЬНОЙ РУЧКОЙ. Связь «заявка ↔ кандидат» —
 * ровно 1:1 (Р2, третья ветвь `service_requests_subject_check`), и сервер соединяет их одним
 * `leftJoin` в той же выборке, которой собирает заявку. Отдельная ручка означала бы второй путь к
 * тем же данным и запрос НА КАЖДУЮ строку списка — там, где предмет заявки нужен каждой строке.
 */
export interface ServiceRequestCandidateDto {
  id: string;
  status: OfficeEquipmentCandidateStatus;
  /** «Kyocera ECOSYS M3145» с шильдика: подпись предмета, пока карточки парка ещё нет (Р7). */
  declaredModel: string;
  /**
   * Оба номера — то, что человек прочёл своими глазами. Непусты не оба: `…_identity_check` требует
   * хотя бы один, и второй остаётся пустой СТРОКОЙ, а не `null`, — колонки кандидата `NOT NULL`, и
   * второе написание пустоты здесь было бы вторым именем одного состояния.
   */
  serialNumber: string;
  inventoryNumber: string;
  /**
   * Причина решения, и читает её в первую очередь АВТОР ЗАЯВКИ (Р15, В5). Дословно, а не «решение
   * принято, подробности в портале»: общая формулировка экономила бы проверяющему выбор слов ценой
   * второго действия человека — он всё равно пойдёт искать причину, а не найдя, позвонит в
   * ИТ-службу, то есть сделает ровно тот звонок, ради отмены которого модуль и заводился.
   *
   * Пустая строка у `pending` и у двух положительных исходов законна: отказ без причины не бывает
   * (`…_reason_check`), а подтверждение объясняет себя самой заведённой карточкой.
   */
  decisionReason: string;
}

export interface ServiceRequestObjectDto {
  id: string;
  code: string;
  name: string;
}

/**
 * Where the unit stands today, when it moved after the request was filed (ADR 0215).
 *
 * The request keeps its own snapshot (`object`, `equipment.location`) because that snapshot is the
 * request's scope and its testimony — moving the unit must not move the request out of its
 * customer's area. But executors travel to the unit, not to the testimony: without this block the
 * header kept naming the declared site (or the stale room copied from the card) after IT had
 * already recorded where the unit really is.
 *
 * The values are the LIVE card, not the last movement's target side: the room and the state note
 * can also be edited on the card without a movement, and "stands now" must not lag behind that.
 * The movement journal only answers whether the unit moved since filing — without that gate the
 * block would contradict the declaration before IT has checked anything.
 */
export interface ServiceRequestCurrentPlaceDto {
  object: ServiceRequestObjectDto;
  location: string;
  state: OfficeEquipmentState;
  stateNote: string;
  /** Day of the latest movement (the physical move day, not the day it was recorded). */
  movedOn: string;
}

export interface ServiceRequestDepartmentDto {
  id: string;
  code: string;
  name: string;
}

export interface ServiceRequestCounterpartyDto {
  id: string;
  name: string;
}

/**
 * Подразделение заявителя (Н11): откуда человек, заведший заявку. Ссылка **и** снимок названия —
 * отдел переименуют, и прошлогодняя заявка обязана показать имя того времени, а не сегодняшнее.
 *
 * Одним полем, а не парой «отдел / площадка»: заполнена всегда ровно одна из двух связей (CHECK в
 * БД), и две ветки в карточке означали бы состояние «и то и другое», которого не бывает. `null` —
 * привязок у учётки нет вовсе (администратор портала) либо заявка заведена до выпуска 1.
 */
export interface ServiceRequestRequesterPlaceDto {
  kind: 'department' | 'object';
  id: string;
  /** Снимок названия на момент заведения. */
  name: string;
}

/**
 * Поимённый исполнитель заявки (Н5). Сотрудники сервисной компании сюда не попадают: назначается
 * контрагент целиком, и кто из инженеров поедет, портал не знает (`service` в карточке).
 */
export interface ServiceRequestExecutorDto {
  userId: string;
  name: string;
  assignedAt: string;
}

export interface ServiceRequestItemDto {
  id: string;
  kind: ServiceItemKind;
  name: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  /** Факт: `null` — работы ещё не закрывали, и у строки нет ни выполнения, ни суммы факта. */
  performed: boolean | null;
  actualQuantity: number | null;
  actualAmount: number | null;
  warrantyMonths: number | null;
  warrantyUntil: string | null;
  warrantyUntilManual: boolean;
}

/**
 * Строка заявки на расходники (Н9): позиция справочника, сколько просили, сколько выдали и почему
 * разошлось.
 *
 * Реквизиты позиции — снимком поля не являются, в отличие от реквизитов техники: строка ссылается на
 * живую карточку справочника (`ON DELETE RESTRICT`), и переименование позиции обязано читаться в
 * заявке новым именем. Склад — не история заявки, а действующий перечень, и «Тонер Ricoh 201»,
 * переименованный вчера в «Тонер Ricoh 201 (шт)», — это та же полка.
 *
 * ПОЛЕ `consumables` В `ServiceRequestDto` ЗАВЕДЕНО ВОЛНОЙ МАРШРУТА, а не вместе с этим типом, —
 * ровно по той причине, по которой `kind` ждал своей волны (§7.3 плана): обязательное поле DTO
 * уронило бы компиляцию API и портала раньше, чем сервер научился бы его отдавать. Тип был объявлен
 * заранее, чтобы волне маршрута осталось дописать одну строку, а не придумывать состав.
 */
export interface ServiceRequestConsumableDto {
  /** Идентификатор СТРОКИ заявки: им адресуется правка факта и на него ссылается журнал склада. */
  id: string;
  consumableId: string;
  /** Код номенклатуры учётной системы: по нему сверяют со счётом. */
  code: string;
  name: string;
  /** Цвет позиции или `null` у чёрно-белой (Р5): у цветной серии по позиции на цвет. */
  color: string | null;
  requestedQuantity: number;
  /**
   * Сколько выдали. `null` — работу ещё не закрывали, и факта у строки нет вовсе; `0` — закрыли, но
   * не выдали («съездили, тонер оказался цел», В9б). Это разные состояния, и показывать их одинаково
   * нельзя: первое ждёт исполнителя, второе — законченная работа.
   */
  issuedQuantity: number | null;
  /** Причина расхождения; пусто у совпавшего факта и у незаполненного (`serviceConsumableIssueIssue`). */
  issueNote: string;
}

export interface ServiceRequestFileDto {
  id: string;
  /**
   * У КАРАНТИННОГО ФАЙЛА ИМЯ ПУСТО (Р6, п. 4): имя файла само бывает персональными данными —
   * «Паспорт_Иванова.pdf» в карточке остаётся утечкой даже без содержимого. Ссылки на скачивание в
   * карточке нет вовсе, и её карантин закрывает своим замком — приоритетным запретом в
   * `canAccessFile` раньше любой ветки видимости, иначе «скрытый» документ продолжал бы скачиваться
   * по прямой ссылке.
   */
  filename: string;
  contentType: string;
  size: number;
  kind: ServiceFileKind;
  /**
   * РОЛЬ ФАЙЛА (Р5): подтверждение работ или основание объёма работ. Порталу она нужна затем, чтобы
   * у страницы-основания не рисовать кнопку снятия — снять его нельзя никогда (Р6), и кнопка, за
   * которой стоит отказ, здесь хуже отсутствующей.
   *
   * НЕОБЯЗАТЕЛЬНАЯ, И ЭТО ОКНО ВЫКАТА, А НЕ НЕОБЯЗАТЕЛЬНОСТЬ ПРАВИЛА: старое приложение поля не
   * отдаёт, и его отсутствие читается как `closing_evidence` — тем же умолчанием, каким миграция
   * размечает существующие связи (`isServiceClosingFile`).
   */
  purpose?: ServiceFilePurpose;
  /**
   * Файл поставлен в карантин (Р6, п. 4) — аварийный выход для ошибочно загруженного секретного или
   * чужого документа. Не метка вежливости: содержимое закрыто всем, включая автора, и открыто только
   * по отдельному праву аудита.
   *
   * Признак остаётся ВИДИМЫМ, а сама строка ревизии, её сумма и след освобождения — на месте:
   * «доказательство скрыто по обращению» и «доказательства не было» — разные факты, и стирать первый
   * до второго значило бы переписывать историю денежного решения.
   */
  quarantined?: boolean;
  attachedAt: string;
}

/**
 * Виза отдела ИТ: кто и когда решил, что внешний ремонт нужен (Р51). Имя — снимком, как у
 * согласования сметы: подпись должна читаться и после того, как учётку закрыли.
 */
export interface ServiceRequestItApprovalDto {
  by: string | null;
  byName: string;
  at: string;
  /** Виза проставлена самим заведением: заявку завёл обладатель права (Р52). */
  auto: boolean;
}

export interface ServiceRequestApprovalDto {
  by: string | null;
  byName: string;
  at: string;
  /** Какая именно ревизия согласована: по ней сервер и пускает работы к закрытию. */
  revision: number;
  /**
   * WHAT SET THE SIGNATURE (Р11): `human` — a person, `auto` — the automatic acceptance on the
   * service operator's declaration. `auto` has no signer at all (`by` empty, `byName` empty), and the
   * card must say so in words rather than show a signature without a name.
   *
   * Always present (release B, ADR 0183): the database keeps the signature and its source as one
   * pair, and every server this portal can meet sends the field — the portal ships with its own
   * server, and in the rollout window it meets only the previous release, which has sent the
   * field since release A. So there is no "empty means a person" fallback to apply.
   */
  source: ServiceEstimateApprovalSource;
}

/**
 * ЗАЯВЛЕНИЕ ОБ ОСВОБОЖДЕНИИ И ЕГО ИСХОД (Р3, Р13) — то, чем карточка и список объясняют, почему по
 * заявке нет подписи либо почему её всё-таки ждут.
 *
 * РЕВИЗИЯ В СОСТАВЕ НЕ ЛИШНЯЯ. Заявление относится к той ревизии, под которой его сделали, а
 * раскладка «Ведения» и возврат в правку переиздают ревизию — ровно как у визы ИТ, карточка обязана
 * различать «освобождение по действующей ревизии» от «освобождения по прошлой». Без номера она
 * показывала бы снятое основание как живое.
 */
export interface ServiceRequestEstimateExemptionDto {
  revision: number;
  /** Кто заявил; `null` — учётки уже нет, и подпись читается по снимку имени. */
  by: string | null;
  byName: string;
  at: string;
  /** Необязательное пояснение исполнителя: «мелкий ремонт на месте» (Р1). */
  note: string;
  outcome: ServiceEstimateExemptionOutcome;
}

/**
 * СПОР ОБ ОСВОБОЖДЕНИИ (Р9). Пусто — спора не было ни разу; `state: 'resolved'` — спор был и
 * разрешён, и его след остаётся: именно он объясняет, почему у заявки второе окно приёмки или почему
 * подпись собрали уже в «Решена».
 */
export interface ServiceRequestEstimateDisputeDto {
  /** Ревизия, чьё освобождение оспорено: подпись после спора пускается только по ней (Р9). */
  revision: number;
  state: ServiceEstimateDisputeState;
  /** Причина, с которой спор открыт: обязательная, и потому всегда непустая. */
  reason: string;
  openedBy: string | null;
  openedByName: string;
  openedAt: string;
  /** Исход; `null` — спор ещё ведётся, и заявка стоит в заморозке. */
  outcome: ServiceEstimateDisputeOutcome | null;
  resolvedBy: string | null;
  resolvedByName: string;
  resolvedAt: string | null;
}

export interface ServiceRequestCompletionDto {
  completedAt: string;
  /** Итог по акту: сумма выполненных строк плюс скидка. Считает сервер. */
  totalAmount: number | null;
  adjustmentAmount: number | null;
  adjustmentReason: string;
}

export interface ServiceRequestWarrantyClaimDto {
  source: WarrantyClaimSource;
  /** Позиция прошлого ремонта — только у источника `item`. */
  itemId: string | null;
  itemName: string;
  /** Номер заявки, по гарантии которой обращаются: спор ведут именно по нему. */
  sourceRequestNum: number | null;
}

// ── Повторное обращение по аппарату (план `docs/office-equipment-repeat-request-plan.md`) ──
//
// ТРЕТЬЕ ПОНЯТИЕ РЯДОМ С ДВУМЯ УЖЕ РАБОТАЮЩИМИ (§4 плана), и путать их нельзя ни в коде, ни в
// словах. «Уже чиним?» отвечают частичные уникальные индексы `service_requests_open_*_unique` —
// они не дают завести вторую НЕЗАКРЫТУЮ заявку того же вида. «Чините по гарантии» отвечает
// `warrantyClaim` — обращение с юридическим содержанием и четырьмя серверными проверками. Здесь
// же — наблюдение и только оно: «по этому аппарату недавно уже была закрытая заявка».
//
// СЕМАНТИКА ПРАВИЛА ЖИВЁТ В КОНТРАКТАХ, А ЕГО SQL — НА СЕРВЕРЕ (Р4). Здесь стоят те части, о
// которых спорят портал, сервер и тесты: какие статусы считаются терминальными, какой вид заявки
// вообще участвует и что означает нулевое окно. Сам предикат собрать этими константами нельзя и не
// нужно — он один на четырёх потребителей (список, карточка, `repeat=true`, `repeatFor`) и живёт в
// `apps/api/src/services/service-request-repeat.ts`: тащить в общий пакет drizzle и схему БД ради
// одного условия значило бы отдать порталу зависимость, которой он не пользуется.

/**
 * ПРЕДЫДУЩАЯ ЗАЯВКА СЧИТАЕТСЯ ЗАКРЫТОЙ В ЭТИХ ДВУХ СТАТУСАХ (Р1, условие 3).
 *
 * Отменённая входит НАРЯДУ с принятой (Р11, развилка В3): «завели, отменили, завели снова» — это и
 * есть обращение второй раз, а причина отмены («дубль», «не подтвердилось») читается в самой
 * заявке. Сузить перечень до одной `accepted` — решение заказчика, а не правка кода: и предикат, и
 * условие частичного индекса `service_requests_repeat_idx` собраны отсюда, и разъехаться им нечем.
 *
 * «Решена» (`done`) сюда НЕ входит намеренно: работы закрыты, но заявку ещё принимают, и повтором
 * по ней считалась бы заявка, заведённая до того, как первую вообще досмотрели.
 */
export const SERVICE_REQUEST_REPEAT_TERMINAL_STATUSES = [
  'accepted',
  'cancelled',
] as const satisfies readonly ServiceRequestStatus[];

export type ServiceRequestRepeatTerminalStatus =
  (typeof SERVICE_REQUEST_REPEAT_TERMINAL_STATUSES)[number];

/**
 * ПРИЗНАК СЧИТАЕТСЯ ТОЛЬКО У РЕМОНТА, И СРАВНИВАЕТСЯ РЕМОНТ С РЕМОНТОМ (Р3).
 *
 * Повторная заявка на расходники — норма, а не сигнал: картридж кончается регулярно, и «Повтор ×5»
 * у принтера, которому пять раз меняли тонер, обесценило бы саму метку. Частота расхода — другой
 * вопрос, и отвечает на него отчёт по расходникам.
 *
 * Значением, а не переписанной строкой `'repair'` в трёх местах: тем же самым видом собраны и
 * условие индекса, и обе половины предиката (`P.kind = R.kind = 'repair'`).
 */
export const SERVICE_REQUEST_REPEAT_KIND = 'repair' as const satisfies ServiceRequestKind;

/**
 * ОКНО ВЫКЛЮЧЕНО. Ноль — не «окно нулевой длины», а рубильник признака целиком (Р5): при нём
 * сервер не ходит в базу вовсе, поля `repeat` в ответе нет ни у одной строки, а отбор
 * `repeat=true` отвечает пустой выдачей.
 *
 * Это же значение — кодовое умолчание `SERVICE_REQUEST_REPEAT_WINDOW_DAYS`: порог берут из данных
 * пилота (§8), и отсутствие переменной окружения не вправе включить эвристику само собой.
 */
export const SERVICE_REQUEST_REPEAT_WINDOW_DISABLED = 0;

/**
 * Признак повторного обращения — то, что портал рисует меткой «Повтор ×N» (Р10).
 *
 * ЧИСЛО ЗАВИСИТ ОТ ЧИТАТЕЛЯ (Р8): у оператора по той же заявке бывает «×3», у заявителя «×1» —
 * совпадения считаются в области видимости смотрящего. Это не расхождение, а то же правило, по
 * которому у них разные списки; главное свойство признака — число совпадает с тем, что человек
 * увидит по ссылке.
 */
export interface ServiceRequestRepeatDto {
  /** Сколько закрытых и отменённых ремонтов по тому же аппарату попало в окно. Ноль — метки нет. */
  count: number;
  /**
   * Окно, по которому считали, — днями. Приходит с каждым признаком не ради красоты: подпись
   * обязана называть период («2 заявки за 30 дней»), а не «недавно», иначе метку нечем оспорить.
   */
  windowDays: number;
  /** Последнее из совпадений — для подсказки «последний раз чинили 12.08.2026». */
  lastAt: string | null;
}

/**
 * ПРИМЕНИМ ЛИ ПРИЗНАК К ЭТОЙ ЗАЯВКЕ ВООБЩЕ — до всякого похода в базу и одинаково у всех
 * потребителей.
 *
 * Три условия из Р1, которые видны в самой строке: окно включено, заявка ремонтная (Р3) и предмет
 * у неё назван карточкой парка. Заявке без аппарата признака нет вовсе — сравнивать не с чем, а
 * `NULL = NULL` в SQL и так ложен: явное условие здесь стоит затем, чтобы такая строка не занимала
 * места в пакетном запросе и чтобы правило читалось словами, а не выводилось из свойств `NULL`.
 *
 * Функцией в контрактах, а не тремя проверками на сервере: тот же вопрос задаёт портал, решая,
 * рисовать ли пустое место под метку.
 */
export function serviceRequestRepeatApplies(
  row: { kind: ServiceRequestKind; officeEquipmentId: string | null },
  windowDays: number,
): boolean {
  return (
    windowDays > SERVICE_REQUEST_REPEAT_WINDOW_DISABLED &&
    row.kind === SERVICE_REQUEST_REPEAT_KIND &&
    row.officeEquipmentId !== null
  );
}

export interface ServiceRequestDto {
  /**
   * В КАКОМ ОБЪЁМЕ СОБРАН ЭТОТ ОТВЕТ (план `docs/office-equipment-requester-card-plan.md`, Р4).
   * Поле отвечает не про заявку, а про читателя — как и `chat.participantSides`, посчитанный
   * сервером для него же.
   *
   * Без него «скрыто» неотличимо от «нет данных»: `estimatedTotalAmount: null` читается и как
   * «объём работ ещё не собран», и как «его не показывают», а это разные вещи. Портал, не
   * различая их, нарисовал бы заявителю честный прочерк там, где рисовать не надо ничего.
   *
   * Обязательное, а не `?`: значение считает сервер на каждом ответе (`serviceRequestAudienceOf`),
   * и `undefined` здесь означал бы карточку, про которую никто не решил, что в ней показано.
   * Портал аудиторию САМ ПО ПРАВАМ НЕ СЧИТАЕТ — читает отсюда: два ответа на один вопрос
   * разъедутся, и первым разъедется тот, что в браузере.
   */
  audience: ServiceRequestAudience;
  /**
   * ВИДНА ЛИ ЗАЯВКА ЧИТАТЕЛЮ СО СТОРОНЫ ЗАКАЗЧИКА — по оси его роли (его площадка, его отдел), а
   * не по сквозной области набора (`inServiceRequestCustomerScope`; находка Н8, решение заказчика
   * 04.09.2026). Поле отвечает не про заявку, а про читателя — как `audience` и стороны
   * обсуждения рядом.
   *
   * ЗАЧЕМ ОНО ПОРТАЛУ. Правку и удаление открывает `canChangeRequestAsCustomer`, и второй его
   * признак — эта самая сторона. Вывести её портал не может ничем: `ServiceRequestDto` не
   * раскрывает `createdBy`, а сложить ответ самому — значит завести на клиенте вторую копию
   * правила области, которая разойдётся с серверной молча. Без поля пункты меню пропадали бы у
   * сисадмина на заявке ЕГО ЖЕ площадки, где сервер их разрешает, — расхождение, которое §11
   * плана считает дефектом строки наравне с обещанием несуществующего действия.
   *
   * Обязательное, а не `?`: значение считает сервер на каждом ответе, и `undefined` здесь означал
   * бы строку, про сторону которой никто не решил, — то есть тихое «нет».
   */
  inCustomerScope: boolean;
  id: string;
  num: number;
  displayNumber: string;
  /**
   * Вид заявки (Н1). Поле заводится волной В3 вместе с маршрутом, а не контрактами В1: обязательное
   * поле DTO уронило бы компиляцию API и портала раньше, чем сервер научился бы его отдавать.
   * Без него портал не может позвать `serviceRequestNeedsClosingDocument` — предикат требует пару
   * «вид + исполнитель-контрагент».
   */
  kind: ServiceRequestKind;
  status: ServiceRequestStatus;
  statusChangedAt: string;
  waitingOn: ServiceWaitingOn;
  /**
   * Заморозка: куда заявка вернётся и почему её остановили (Р104, Р107). Поля ходят парой — при
   * `on_hold` они непусты, в остальных статусах пусты оба (CHECK в БД). По `heldFromStatus`
   * считается и эффективный статус: виды файлов отложенной «Диагностики» — те же, что у неё
   * (Р110).
   */
  heldFromStatus: ServiceRequestStatus | null;
  holdReason: string;
  /**
   * Предмет заявки — аппарат и площадка, где он стоит. Оба поля НЕОБЯЗАТЕЛЬНЫ, и оба пустеют
   * ВМЕСТЕ: у заявки «от отдела» аппарата нет, а значит нет и снимка площадки — брать его неоткуда
   * (Р8 плана `office-equipment-consumables-and-purchase-plan.md`, ADR 0146, решение 7).
   *
   * ПОЧЕМУ `null` ОБЪЯВЛЕН РАНЬШЕ, ЧЕМ ТАКИЕ ЗАЯВКИ ПОЯВЛЯЮТСЯ. Выпуск, который научит их заводить
   * (2б), везёт миграцию, право и форму; этот выпуск (2а) не включает ничего — он только учит
   * ЧИТАТЬ. Порядок обратный привычному не по осторожности, а потому, что иначе он не работает:
   * первая же заявка без аппарата, встреченная старым кодом, — это `undefined` в чтении
   * `equipment.name` в браузере и потерянная строка в списке на сервере. Выкатить чтение вторым
   * шагом значило бы выкатить его ПОСЛЕ того, как ломаться уже есть чему.
   *
   * Пустой аппарат при непустом объекте — законное состояние (заявка от площадки без аппарата),
   * пустой объект при непустом аппарате — нет: у аппарата площадка есть всегда (`CHECK` выпуска
   * 2б). Порознь эти поля читать нельзя — «Без аппарата» в карточке рисуется по паре.
   */
  equipment: ServiceRequestEquipmentDto | null;
  /**
   * Сообщение об аппарате, которого нет в справочнике, — ТРЕТЬЕ состояние предмета рядом с двумя
   * прежними (план кандидатов, Р5, §9). Предмет заявки описывается либо карточкой парка
   * (`equipment`), либо ничем («Без аппарата», Р8), либо ЭТИМ блоком; пустой `equipment` при
   * непустом блоке — не потеря, а законное «карточки ещё не существует».
   *
   * ПОЧЕМУ ПОЛЕ НЕОБЯЗАТЕЛЬНОЕ, а не `| null` рядом с соседями. Порядок работ здесь обратный
   * привычному: портал волны Э6 написан и читает это поле РАНЬШЕ, чем сервер научился его
   * отдавать, — а браузер после выката ещё какое-то время держит старый бандл и разговаривает с
   * новым сервером (и наоборот). Обязательный тип утверждал бы про такой ответ неправду, причём
   * ровно в том месте, где портал и без того обязан не упасть. `kind` и `consumables` объявлялись
   * здесь по той же причине, только зеркально: тогда впереди шли контракты, а сервер догонял.
   *
   * ДВУХ НАПИСАНИЙ ПУСТОТЫ ПРИ ЭТОМ НЕ ЗАВОДИТСЯ: сервер отдаёт ровно одно — `null` у заявки без
   * кандидата (`toDto` собирает поле всегда), а `undefined` означает единственное — «отвечал
   * сервер, который про кандидатов ещё не знал». Читатель портала берёт `?? null` и не различает
   * их намеренно: разница между «кандидата нет» и «сервер старый» для плашки состояния одна и та
   * же — показывать нечего.
   *
   * КОМУ ВИДЕН БЛОК: ВСЕМ, КТО ВИДИТ САМУ ЗАЯВКУ, — автору, службе и назначенному исполнителю
   * (решение этой работы; разбор альтернатив — при строке `equipmentCandidate` в
   * `SERVICE_REQUEST_FIELD_AUDIENCE`). Круг читателей заявки считает не этот блок, а
   * `serviceRequestVisibilityWhere`; здесь решается только одно — не вычитается ли блок у
   * заявителя проекцией аудитории. Не вычитается: заявленная модель, два номера и причина отказа —
   * не деньги заявки, а её ПРЕДМЕТ.
   */
  equipmentCandidate?: ServiceRequestCandidateDto | null;
  object: ServiceRequestObjectDto | null;
  /**
   * Объект заявки назвал человек, а не подставила карточка техники (Р16). Факт заявления, и только
   * он: расхождение со справочником портал отсюда не читает — оно вычисляется соединением с
   * карточкой на сервере и гаснет само, когда единицу перенесут. Пара нужна целиком, поэтому
   * плашку «объект расходится» рисует не это поле в одиночку.
   */
  objectOverridden: boolean;
  /**
   * Расхождение **не устранено**: заявитель указал объект сам (`objectOverridden`), и снимок заявки
   * до сих пор расходится с карточкой техники. Считается сервером соединением с карточкой, а не
   * колонкой (Р16), и в этом вся суть: перенос единицы в справочнике гасит признак сам, без
   * второго действия и без человека, который обязан не забыть.
   *
   * Порознь два признака отвечают неверно. Один хранимый флаг не гаснет ничем — отбор ИТ-службы
   * через месяц перестал бы быть очередью и стал списком всего, что когда-либо поправляли. Одного
   * вычисляемого мало: технику возят, и у прошлогодних заявок снимок расходится с карточкой сплошь
   * и рядом, хотя никто ничего не заявлял.
   */
  objectMismatch: boolean;
  /**
   * Кем и когда разобрано заявленное расхождение (план перемещения из карточки заявки, Р8).
   * `null` — не разобрано либо расхождения не заявляли вовсе.
   *
   * Поле объясняет, почему строка ушла из очереди ИТ-службы: без него «заявляли, а в очереди нет»
   * читалось бы как потеря, а не как разбор. Разбирают его подтверждающим перемещением — тем самым,
   * которым ответственный говорит «аппарат стоит здесь», — поэтому в снимке автор и время события,
   * а не отметка в самой заявке: заявка остаётся рассказом о том, что заявили.
   */
  objectMismatchResolvedBy: {
    movementId: string;
    at: string;
    actorName: string | null;
  } | null;
  /**
   * Where the unit stands now (ADR 0215): `null` — the request is closed, has no unit, or the unit
   * has not moved since the request was filed, so the snapshot above is still the answer.
   *
   * Optional for the rollout window, the same way as `equipmentCandidate`: a cached bundle may talk
   * to the old server, which never sends the field. The server always sends it; readers take
   * `?? null` and treat both as "nothing moved".
   *
   * Open requests only: executors need the place while the work is going on, and a closed request
   * showing "stands now" would describe a unit nobody is travelling to any more.
   */
  currentPlace?: ServiceRequestCurrentPlaceDto | null;
  /** Отдел-заказчик и отдел-владелец: по ним считается область роли отдела. */
  customerDepartment: ServiceRequestDepartmentDto | null;
  equipmentDepartment: ServiceRequestDepartmentDto | null;
  /** Откуда сам заявитель (Н11). Областью видимости не является — это реквизит карточки. */
  requesterPlace: ServiceRequestRequesterPlaceDto | null;
  description: string;
  /** Заявитель: кто обратился и по какому номеру с ним связываться (Р49). */
  responsibleName: string;
  responsiblePhone: string;
  /** Срочность и её объяснение: без второго первое не показывается — их и не бывает порознь. */
  isUrgent: boolean;
  urgencyReason: string;
  service: ServiceRequestCounterpartyDto | null;
  /**
   * Поимённые исполнители (Н5) — второй слой рядом с `service`. Пустой список при непустом
   * контрагенте — обычное дело: сервисная компания назначается целиком.
   */
  executors: ServiceRequestExecutorDto[];
  /** Виза ИТ; `null` — заявка ещё ждёт решения отдела (Р51). */
  itApproval: ServiceRequestItApprovalDto | null;
  warrantyClaim: ServiceRequestWarrantyClaimDto | null;
  /**
   * ПОВТОРНОЕ ОБРАЩЕНИЕ ПО ТОМУ ЖЕ АППАРАТУ (§6 плана) — наблюдение, а не основание: гарантийным
   * обращением оно не становится и никаких действий не запускает (Р9). У заявки бывают обе пометки
   * сразу, и это честно.
   *
   * НЕОБЯЗАТЕЛЬНОЕ, И РАЗНИЦА СУЩЕСТВЕННА ДЛЯ ПОРТАЛА. Отсутствие поля означает «признак к этой
   * строке не применяется» — окно выключено (`W = 0`), заявка на расходники или заявка без
   * аппарата (`serviceRequestRepeatApplies`); тега нет вовсе. `count: 0` означает другое —
   * «считали, повторов не нашли»: тега тоже нет, но признак работает, и отбор «только повторные»
   * человеку показывать можно.
   *
   * СЧИТАЕТСЯ ПАКЕТНО НА СТРАНИЦУ, А НЕ КОЛОНКОЙ ЗАПРОСА (Р6, Н6): окно у каждой строки своё —
   * оно отсчитывается от её собственного `createdAt`, — поэтому группировка по аппарату здесь
   * неверна, а коррелированное поле в списке `SELECT` drizzle молча переписывает. Сборка зовёт
   * `serviceRequestRepeatByRequest` одним запросом на всю страницу.
   */
  repeat?: ServiceRequestRepeatDto;
  /** Объём работ: текущая ревизия и её строки. */
  estimateRevision: number;
  /*
   * ПОЛЯ ВОЛНЫ «ОСВОБОЖДЕНИЕ И ОБЪЁМ РАБОТ ДОКУМЕНТОМ» — ЧЕТЫРЕ, И ВСЕ НЕОБЯЗАТЕЛЬНЫЕ ПО ОДНОЙ
   * ПРИЧИНЕ. Контракты волны едут первым этапом, сервер учится их отдавать следующими; обязательными
   * они уронили бы сборку сервера и портала раньше, чем появилось бы чему ломаться (тот же довод, что
   * у `kind` и `consumables` выше). Отсутствие поля означает «отвечает приложение, которое о волне не
   * знает», и читается оно как сегодняшнее состояние: построчный формат, подпись человека, ни
   * заявления, ни спора.
   */
  /**
   * ФОРМАТ ДЕЙСТВУЮЩЕЙ РЕВИЗИИ (Р4): чем предъявлен объём работ. `null` — ревизии нет вовсе (объём
   * работ ни разу не предъявляли либо заявка старше волны).
   *
   * ПОРТАЛУ ОН НУЖЕН ТРЕМЯ РАЗНЫМИ МЕСТАМИ: планка закрывающего документа (`hasServiceClosingDocument`
   * читает его отсюда), «сумма не разобрана» вместо «0 ₽» у документной заявки и скрытые поля окна
   * объёма работ (Р10). Вывести его из карточки нельзя ничем — см. `SERVICE_ESTIMATE_FORMATS`.
   */
  estimateFormat?: ServiceEstimateFormat | null;
  /**
   * WHAT OPENED THE PENDING WAIT FOR THE SIGNATURE (Р9): a submission or the outcome of a resolved
   * dispute. A card field, because `canApproveServiceEstimate` decides by it whether the signature
   * is admitted in «Решена», and the portal asks the same predicate with the same fields.
   *
   * `null` exactly when `estimatePendingRevision` is: the database keeps them one pair (release B
   * of ADR 0183), and the requester's projection blanks both. Required, although it used to be
   * optional: every server since release A sends it, so the wire shape old bundles read is the same.
   */
  estimatePendingSource: ServiceEstimatePendingSource | null;
  /** Заявление об освобождении от подписи и его исход (Р3); `null` — заявления не было. */
  exemption?: ServiceRequestEstimateExemptionDto | null;
  /** Спор об освобождении (Р9); `null` — спора не было ни разу. */
  dispute?: ServiceRequestEstimateDisputeDto | null;
  /**
   * Непогашенное предъявление (Р2): `null` — ответ получен либо исполнитель отозвал предъявление,
   * число — висит предъявление этой ревизии. Полем DTO, а не выводом портала из даты: признаки Р11
   * (согласовать, вернуть в правку, предъявить заново, переназначить) портал считает этой самой
   * колонкой, и посчитанный им по-своему ответ разошёлся бы с сервером молча.
   */
  estimatePendingRevision: number | null;
  /**
   * Когда объём работ предъявляли в последний раз. Активным предъявлением НЕ является (Р9): возврат
   * в правку эту дату не трогает, и у отозванного она непуста — активное состояние определяет
   * исключительно `estimatePendingRevision`.
   */
  estimateSubmittedAt: string | null;
  estimatedTotalAmount: number | null;
  approval: ServiceRequestApprovalDto | null;
  items: ServiceRequestItemDto[];
  /**
   * Строки заявки на расходники (Н9). У ремонта список пуст всегда — предмет заявки там смета, и
   * двух списков предмета у одной заявки не бывает.
   *
   * Поле заведено волной маршрута, а не контрактами В1, и по той же причине, что `kind`:
   * обязательное поле DTO уронило бы компиляцию API и портала раньше, чем сервер научился бы его
   * отдавать. Тип `ServiceRequestConsumableDto` объявлен заранее — волне маршрута оставалось
   * дописать одну строку.
   */
  consumables: ServiceRequestConsumableDto[];
  completion: ServiceRequestCompletionDto | null;
  acceptedByName: string;
  acceptedAt: string | null;
  /**
   * Кто закрыл заявку (Н7): `human` — человек, `auto` — портал по истечении суток молчания.
   *
   * `null` читается **как `human`**, а не как ошибку, и это не мягкость: заявка, принятая старым
   * кодом между накатом M2 и перезапуском, живёт с датой приёмки и пустым источником до M9 — то
   * есть неделю (план §5, M2). У непринятой заявки поле пусто всегда.
   */
  acceptanceSource: 'human' | 'auto' | null;
  /**
   * «Ремонт нецелесообразен, аппарат под замену» (Н3, В21) — теперь галочка человека при отказе по
   * объёму работ (Р8), а не вывод снятой визы ИТ. Стоит только у отменённой заявки: пометка
   * объясняет, почему её закрыли без ремонта, и возврат в «Новую» её снимает
   * (`serviceResetOnTransition`, флаг `rejection`).
   */
  replacementRecommended: boolean;
  /**
   * Решение при отказе: что делаем вместо ремонта (Р12). Пусто у всех, кроме отменённой заявки
   * (`CHECK` в БД).
   *
   * Показывается САМО ПО СЕБЕ, полем рядом с пометкой замены, а не «под причиной»: причины отмены в
   * DTO нет вовсе — она уходит комментарием перехода и живёт на вкладке истории, где её и читают.
   * Тянуть её сюда отдельным запросом за последним отменяющим переходом значило бы вторую дорогу к
   * тому, что уже показано вкладкой рядом.
   */
  rejectionResolution: string;
  comment: string;
  /**
   * Обсуждение заявки (ADR 0141): всё, что порталу нужно знать о переписке, не открывая её.
   *
   * Обязательное поле, а не `?`, и приезжает оно тем же этапом, что и серверный счёт. Необязательным
   * оно означало бы «переписки может не быть» — а её не бывает только у заявки, у которой ленту не
   * посчитали: ноль реплик — это `total: 0`, а не отсутствие блока. Портал по нему рисует кнопку со
   * счётчиком и обе метки строки, и `undefined` там читался бы как «непрочитанного нет».
   */
  chat: ServiceRequestChatSummaryDto;
  files: ServiceRequestFileDto[];
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  version: number;
}

/**
 * Ответ действия, которое сопровождается письмом службе (план
 * `office-equipment-mail-and-history-plan.md`, Р67): заявка плюс исход почтовой части.
 *
 * Отдельный тип, а не поле карточки: исход относится к **действию**, а не к заявке — в списке и
 * при повторном открытии его нет и быть не может. И он обязан дойти до человека сразу: «заявка
 * заведена, но служба не оповещена» узнаётся в момент заведения, а не когда за ней не приехали.
 */
export interface ServiceRequestWithMailDto {
  request: ServiceRequestDto;
  mail: ModuleMailOutcome;
  /**
   * Исход ВТОРОГО письма — того, которым проверяющих зовут разобрать сообщение о технике (план
   * кандидатов, §10). Поле необязательное, и это не мягкость типа: второе письмо бывает ровно у
   * заведения с `equipmentCandidate`, а у обычной заявки его нет и не может быть — `undefined`
   * здесь означает «события не было», а не «исход неизвестен».
   *
   * Отдельным полем, а не заменой соседнего: письма два, и факты у них разные. `mail` отвечает
   * «служба узнала, что заявку надо разобрать», это поле — «проверяющие узнали, что надо съездить
   * и посмотреть на шильдик». Слитые в один исход, они соврали бы человеку дважды: успех одного
   * прикрыл бы отказ другого, а отказ — обесценил успех.
   */
  candidateMail?: ModuleMailOutcome | null;
}

/**
 * Повторная отправка письма службе (Р70). Ключ идемпотентности генерирует портал — один на открытие
 * диалога, а не на нажатие: два одновременных клика и повтор HTTP обязаны дать одно письмо.
 *
 * Порядкового номера попытки здесь нет намеренно: его пришлось бы считать чтением, а между чтением
 * и вставкой помещается второе нажатие.
 */
export const notifyServiceRequestSchema = z.object({ idempotencyKey: uuidSchema }).strict();
export type NotifyServiceRequestInput = z.infer<typeof notifyServiceRequestSchema>;

export interface ServiceRequestNotifyResultDto {
  mail: ModuleMailOutcome;
  /** Куда ушло письмо — адреса без секретов: по ним видно, что настройка та самая. */
  recipients: string[];
}

/**
 * Есть ли у заявки письмо, которое можно повторить. Одна функция на сервер и портал: разойдись
 * они — кнопка вела бы в 422 либо повтор оставался бы недоступным там, где сервер его позволяет.
 *
 * Событие письма привязано к **входу в статус**, поэтому повторяются ровно два: «Новая» (служба
 * зовётся разобрать заявку) и «Отменена» (чтобы не выезжали зря).
 *
 * Строку, а не голый статус (Р14): у «Новой» письмо зовёт службу РАЗОБРАТЬ заявку, и повторять его
 * после назначения незачем — задание исполнителю ушло своим письмом, привязанным к действию. Пока
 * «Новая» означала «ещё не назначена», на этот вопрос отвечал статус; после слияния он половину
 * ответа потерял бы молча — кнопка осталась бы на месте, а письмо звало бы разбирать заявку,
 * которую уже разобрали.
 */
export function serviceMailRepeatable(
  row: ServiceExecutorsRow & { status: ServiceRequestStatus },
): boolean {
  if (row.status === 'cancelled') return true;
  return row.status === 'new' && !serviceHasExecutors(row);
}

/**
 * Правит ли субъект саму заявку: заказчик — пока её никому не отдали (§6.1). После назначения за
 * заявкой стоят договорённости с исполнителем, и менять её предмет задним числом нельзя.
 *
 * Строку, а не голый статус (Р14). Набор заявок при этом не изменился: прежняя «Новая» и была
 * заявкой без исполнителей — назначение уводило её в «Назначенную», — и условие переписано ровно
 * затем, чтобы остаться верным после слияния. Оставь мы `status === 'new'`, правка молча открылась
 * бы у заявки, которую исполнитель уже прочитал и по которой договорился.
 *
 * `it_approved` из списка ушёл вместе с самим статусом (`0197`): заявок в нём больше не бывает, и
 * ветка под него отвечала бы на вопрос, которого никто не задаёт.
 *
 * `on_hold` в список не входит (Р110): заморозка останавливает ход заявки, и правка её предмета
 * была бы ходом мимо остановки. Отложенную из «Новой» правят, вернув в работу.
 */
export function isServiceRequestEditable(
  row: ServiceExecutorsRow & { status: ServiceRequestStatus },
): boolean {
  return row.status === 'new' && !serviceHasExecutors(row);
}

/**
 * Удаляет ли субъект заявку — в архив. Своё правило, а не «то же, что правка» (В20): удалять можно
 * и заявку, которую уже кому-то назначили, — работа по ней не начиналась, — а править её уже
 * нельзя: предмет заявки исполнитель прочитал и по нему договорился. Переиспользуй мы
 * `isServiceRequestEditable`, два разных решения заказчика держались бы на одном условии и
 * разъехались бы на первой же правке любого из них.
 *
 * ПОВЕДЕНИЕ СЛИЯНИЕ НЕ ИЗМЕНИЛО, и это проверено, а не предположено: удаляли «Новую» и
 * «Назначенную» — то есть заявку до того, как за неё взялись, — оба состояния теперь зовутся
 * «Новой», и один `new` покрывает ровно тот же набор заявок, что покрывала прежняя пара. Записано
 * это здесь затем, чтобы следующая правка не приняла совпадение с `isServiceRequestEditable` за
 * недосмотр и не свела оба правила к одному: условие правки ДРУГОЕ — она требует ещё и отсутствия
 * исполнителей (Р14). Строка сюда поэтому и не передаётся: статуса достаточно.
 *
 * Дальше удаление не идёт ни при каких условиях: с «В работе» по заявке уже могли списать
 * расходники (Р6), и архивная заявка означала бы списание без основания.
 */
export function isServiceRequestDeletable(status: ServiceRequestStatus): boolean {
  return status === 'new';
}

/** Права модуля — одним списком: он нужен и матрице, и проверке «открыт ли раздел». */
export const SERVICE_REQUEST_PERMISSIONS: readonly Permission[] = [
  'serviceRequests.read',
  // Деньги и объём работ — оговорка к чтению, а не отдельная работа (план
  // `docs/office-equipment-requester-card-plan.md`, Р1): порядок здесь повторяет словарь
  // `PERMISSIONS`, и страж модуля сверяет оба перечня строка в строку.
  'serviceRequests.finance',
  'serviceRequests.create',
  // Заявка без аппарата — своё право рядом с «завести» (Р5, ADR 0146, решение 6): порядок здесь
  // повторяет словарь `PERMISSIONS`, и страж модуля сверяет оба перечня строка в строку.
  'serviceRequests.createWithoutEquipment',
  'serviceRequests.update',
  'serviceRequests.delete',
  'serviceRequests.assign',
  'serviceRequests.estimate',
  // Раскладка объёма работ по графам — своя дверь у «Ведения» (план
  // `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р2), а не оговорка к
  // `estimate`: порядок здесь повторяет словарь `PERMISSIONS`, и страж модуля сверяет оба перечня
  // строка в строку.
  'serviceRequests.estimateRewrite',
  'serviceRequests.approveEstimate',
  'serviceRequests.approveIt',
  'serviceRequests.status',
  'serviceRequests.hold',
  // Cancel — the IT service's right of its own (workflow-fixes plan Р2); the order of `PERMISSIONS`.
  'serviceRequests.cancel',
  'serviceRequests.urgency',
  'serviceRequests.execute',
  'serviceRequests.files',
];

/**
 * Подписи полей в истории заявки; ключи проставляет сервер при вычислении изменений
 * (`service-request-diff.ts`). Словарь один на правку заявки, правку сметы и закрытие: читателю
 * истории всё равно, какая ручка породила строку, — ему важно, что именно изменилось.
 */
export const serviceRequestChangeLabels = {
  // «Описание» — общая подпись на оба вида (Р2, просьба 7): заказчик просит единообразия, и
  // кинд-зависимость, введённая Р17 ADR 0145, отменена целиком. Ключ поля прежний, поэтому
  // подпись переименовывает и записи прошлых месяцев — это верно: поле осталось тем же, менялось
  // только его имя для читателя.
  description: 'Описание',
  // Поле убрано отовсюду (Р115), а подпись осталась (Р121): история строится из событий аудита, и
  // записи прошлых месяцев несут ключ `dueDate`. Без строки словаря `RequestHistory` показывает
  // `labels[field] ?? field` — то есть сырое имя поля в строке, которую читает человек.
  dueDate: 'Желаемый срок',
  customerDepartment: 'Отдел-заказчик',
  responsibleName: 'Заявитель',
  responsiblePhone: 'Телефон',
  isUrgent: 'Срочная',
  urgencyReason: 'Причина срочности',
  itApproval: 'Виза ИТ',
  comment: 'Комментарий',
  warrantyClaim: 'Обращение по гарантии',
  filesAdded: 'Прикреплены файлы',
  filesRemoved: 'Удалены файлы',
  // Р17: «Смета» стала «Объёмом работ» сплошняком, ключи полей при этом прежние — они приходят из
  // аудита прошлых месяцев и переименованию не подлежат.
  estimateItemsAdded: 'Добавлено в объём работ',
  estimateItemsRemoved: 'Убрано из объёма работ',
  /**
   * Решение при отказе по объёму работ (Р12). Подпись нужна потому, что решение доезжает до истории
   * элементом `changes`, а не верхнеуровневым полем события: сборка истории читает только их, и
   * поле рядом с `revision` и `reason` она молча пропустила бы. Без строки словаря `RequestHistory`
   * показал бы человеку сырое имя поля.
   */
  rejectionResolution: 'Решение',
  // Что предъявил исполнитель при закрытии: без этих двух строк «закрыли дешевле» осталось бы
  // необъяснённым, а гарантию на неустановленную деталь искали бы годом позже.
  itemsNotPerformed: 'Не выполнено',
  itemsPartial: 'Выполнено частично',
  /*
   * Движение склада по заявке на расходники (Р10). Две подписи, а не одна со знаком: «−2» в
   * истории читается как опечатка, а «Списано со склада» и «Возвращено на склад» — это два разных
   * события для того, кто разбирает, куда делись картриджи.
   *
   * Ключи проставляет не дифф, а сборка истории (`service-request-history.ts`): движения приезжают
   * в аудите своим полем `movements`, потому что складом двигает не правка полей заявки, а отметка
   * факта выдачи.
   */
  consumablesIssued: 'Списано со склада',
  consumablesReturned: 'Возвращено на склад',
  /*
   * Состав расходников до и после правки: спорят о том, что именно просили и в каком количестве.
   *
   * Подпись переименована вместе со вкладкой карточки (Р13): «Номенклатура» читалась как название
   * справочника, а спор идёт про состав ОДНОЙ заявки. Предметный термин при этом остаётся собой —
   * «код номенклатуры», набор «Оргтехника: номенклатура» и разделы справочника не переименованы, и
   * глобальная замена слова по репозиторию была бы ошибкой.
   */
  consumables: 'Состав расходников',
  /*
   * Перемещение техники, записанное ПО ЭТОЙ ЗАЯВКЕ (план перемещения из карточки заявки, Р12) —
   * четвёртый источник истории. Ключи проставляет сборка истории, а не дифф полей заявки: переезд
   * меняет карточку аппарата, а не заявку, и без подписей `RequestHistory` показал бы человеку
   * сырые имена ключей.
   */
  moveObject: 'Объект',
  moveLocation: 'Место',
  moveDepartment: 'Отдел-владелец',
  moveState: 'Где находится',
  /*
   * `satisfies`, а не `Record<string, string>`, и это не украшение типа. Ключи словаря — тот
   * ЗАКРЫТЫЙ перечень изменений, по которому история режется аудиторией
   * (`SERVICE_HISTORY_CHANGE_AUDIENCE` ниже): объявленный `Record<string, string>`, он дал бы
   * `keyof` равный `string`, и карта аудиторий перестала бы требовать решения на новый ключ.
   * Читателям словаря переход ничего не меняет — `Record<string, string>` он по-прежнему
   * удовлетворяет, и `RequestHistory` в портале принимает его тем же аргументом.
   */
} satisfies Record<string, string>;

// ── Аудитории карточки: кому видны деньги и объём работ ──
//
// План `docs/office-equipment-requester-card-plan.md`. Заказчик просил убрать заявителю вкладку
// «Объём работ», суммы и закрытые виды документов — и убрать их не условным рендером, а из ответа
// сервера. Правила живут в контрактах, а не в маршруте, потому что спрашивают их трое: сборка DTO,
// карточка единицы техники и портал (виды в форме подшивки). Правило, написанное в маршруте, они
// переписали бы у себя, а расходятся такие копии молча — ровно тот довод, которым в ADR 0141 в
// контрактах оставлены стороны разговора.
//
// Прецедента серверной проекции DTO по читателю в портале не было: кабинет водителя решает ту же
// задачу своим контуром ручек (ADR 0102), а заявитель работает в ОБЩЕМ списке, общей карточкой и
// общей историей. Механизм заводится впервые, и цена ошибки в нём — не «некрасиво», а «утекло».
// Отсюда форма всех карт ниже: они закрыты компилятором, и забыть поле в них нельзя, а не «нужно
// внимательно не забыть».

/**
 * Две аудитории, и обе названы по тому, что видно, а не по должности (Р2). `finance` видит
 * финансовую часть ЭТОЙ строки, `requester` — строку без денег и состава работ. Какой набор строк
 * читателю доступен вообще, решает область видимости, а не аудитория: это разные вопросы, и
 * третьей аудитории («состав работ без цен») план не заводит — она означала бы третий набор правил
 * в каждом месте, где сегодня их два.
 */
export type ServiceRequestAudience = 'finance' | 'requester';

/**
 * Аудитория конкретной строки: положительное право ЛИБО факт назначения на эту заявку (Р1).
 *
 * Вторая ветка нужна внутреннему исполнителю — назначение открывает ему деньги ровно одной заявки,
 * — и написана она вызовом `isServiceExecutor`, а НЕ своим условием «назначен + `execute`». Ровно
 * этот вопрос тот предикат уже решает, и решает шире, чем выглядит: поимённое назначение с
 * `execute` ЛИБО оператор назначенного контрагента. Повтори мы условие здесь, второй ответ разошёлся
 * бы с коридором ходов, и первым признаком расхождения стала бы карточка, где кнопки исполнителя
 * есть, а сумм нет.
 *
 * Отрицательного основания («у него нет ни одного из прав ведения») здесь нет намеренно: такое
 * правило ломается молча — первое же новое право модуля открыло бы деньги тому, кому их не
 * открывали, и ошибка была бы в неперечисленном, то есть в том, чего не проверяет ни один тест.
 */
export function serviceRequestAudienceOf(
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
): ServiceRequestAudience {
  return can(subject, 'serviceRequests.finance') || isServiceExecutor(subject, assignment)
    ? 'finance'
    : 'requester';
}

/**
 * Всё, что решает вид документа: в каком статусе его принимают, кому он виден и кто его кладёт
 * (матрицы §4.1 и Р9).
 *
 * Одной таблицей, а не тремя перечнями. Список файлов карточки и прямая ссылка обязаны резаться
 * ОДНИМ перечнем видов: расхождение означало бы файл, невидимый в карточке и открывающийся по
 * ссылке, — то есть ровно ту дыру, ради которой план написан. А видимость и подшивка стоят рядом
 * потому, что порознь они дают «подшил и потерял»: человек кладёт файл видом «Счёт», и тот
 * исчезает из его же карточки.
 */
interface ServiceFileKindPolicy {
  /**
   * Статусы, в которых вид принимается. Таблица переехала сюда из
   * `apps/api/src/routes/service-requests.ts` (`FILE_KIND_STATUSES`), где жила второй копией рядом
   * с копией портала (`attachableKinds` в `ServiceRequestDocuments.tsx`): две копии одного правила
   * дают либо форму, предлагающую вид, на котором придёт отказ, либо отказ там, где форма
   * промолчала.
   *
   * Закрытость статуса заявки отдельным условием не проверяется — она уже записана перечнями:
   * `attachment` в `accepted`/`cancelled` не стоит, а закрывающие виды стоят намеренно, потому что
   * «акт пришлю завтра» иначе означало бы потерянную бумагу (Р16, Р29).
   */
  readonly statuses: readonly ServiceRequestStatus[];
  /** Кому вид виден — и в списке файлов карточки, и по прямой ссылке. */
  readonly visibleTo: readonly ServiceRequestAudience[];
  /**
   * Кто вид кладёт. Не выводится из `visibleTo`, и это решение матрицы: гарантийный талон заявителю
   * ВИДЕН (обращаться по гарантии будет он), но кладёт его исполнитель.
   */
  readonly attachedBy: readonly ServiceRequestAudience[];
  /**
   * Какой СТОРОНЕ заявки вид разрешён — второй слой подшивки (план аудита исполнителей, Р3).
   *
   * Аудитории для этого мало, и это остаток находки Н2: `serviceRequests.finance` есть у ИТ-службы
   * по всей компании, а `act`, `invoice` и `warranty_card` разрешены уже в «В работе» — наличие
   * любого из них снимает планку закрывающего документа. То есть основание платежа мог подшить
   * всякий, кому заявку открыли деньгами, и подрядчик закрывал работу под чужой бумагой.
   *
   * Список, а не одно значение: у закрывающих видов сторон две — исполнитель и «Ведение», — и
   * записать это дизъюнкцией в одном слове значило бы завести четвёртое понятие стороны рядом с
   * тремя существующими.
   */
  readonly attachedBySide: readonly ServiceFileAttachingSide[];
}

/**
 * Сторона, которой вид документа разрешён. Не путать со стороной разговора и не заменяет её:
 * вопрос здесь один — «чья это бумага».
 *
 * `'any'` означает «сторона не спрашивается вовсе», а не «можно всем»: до этого слоя доходит только
 * тот, у кого есть право `serviceRequests.files` и кому заявка видна, — фотографию поломки грузит
 * заказчик, и требовать от него стороны цикла было бы отказом половине заявок.
 */
export type ServiceFileAttachingSide = 'any' | 'executor' | 'estimate_author' | 'operator';

/**
 * Сторона словами — для текста отказа. Рядом с таблицей, а не в маршруте: перечень сторон вида
 * читает сервер, и составленный на его стороне текст разъехался бы с таблицей на первом же
 * изменении матрицы.
 */
export const serviceFileAttachingSideLabels: Record<ServiceFileAttachingSide, string> = {
  any: 'участник заявки',
  executor: 'назначенный исполнитель',
  estimate_author: 'назначенная сервисная компания',
  operator: 'тот, кто ведёт заявку',
};

/**
 * `Record` по всем видам: новый вид документа обязан ответить на все четыре вопроса, иначе сборка
 * не соберётся. Умолчания «как у соседа» здесь нет намеренно — им и утекают такие таблицы.
 */
const SERVICE_FILE_KIND_POLICY: Record<ServiceFileKind, ServiceFileKindPolicy> = {
  // Фотография поломки — половина заявки, и грузит её сам заявитель.
  attachment: {
    statuses: ['new', 'in_work', 'done'],
    visibleTo: ['finance', 'requester'],
    attachedBy: ['finance', 'requester'],
    // Стороны у фотографии поломки нет: её грузит тот, кто заявку и завёл, а сторона цикла у него
    // появится в лучшем случае позже.
    attachedBySide: ['any'],
  },
  // Тот же предмет, что и скрытая вкладка: документ с составом работ и ценами.
  estimate: {
    statuses: ['in_work'],
    visibleTo: ['finance'],
    attachedBy: ['finance'],
    // Объём работ предлагает тот, кто будет их делать. «Ведение» его СОГЛАСУЕТ, а не пишет:
    // подпись под собственным предложением не согласование (та же граница, что у прав в матрице —
    // `estimate` и `approveEstimate` одному субъекту не выдают).
    //
    // Its own side, not `executor` (workflow-fixes plan Р6): the document of the contractor's offer
    // is attached by the estimate's author. With the wide executor side the own employee of a
    // mixed assignment would attach a priced document to the contractor's request — the same door
    // `PUT /:id/estimate` closes, reopened by the file route.
    attachedBySide: ['estimate_author'],
    // Пятого столбца «нужен ли объём работ вообще» в таблице нет и быть не может: он зависит от
    // строки заявки, а не от вида документа, — и спрашивается отдельной строкой в
    // `canAttachServiceFile` (Р5, Н17). Ищущему здесь оставлена эта ссылка.
  },
  // В акте стоят суммы: «акт виден» отменяло бы половину задачи (Р6, §12 п. 1).
  act: {
    statuses: ['in_work', 'done', 'accepted', 'cancelled'],
    visibleTo: ['finance'],
    attachedBy: ['finance'],
    /*
     * Закрывающая бумага: её кладёт исполнитель либо «Ведение» (Р3). Вторая сторона здесь не
     * послабление, а рабочий случай — акт приходит на бумаге и почтой, и сканирует его тот, кто
     * заявку ведёт; отбери мы у него подшивку, работа внешнего подрядчика не закрылась бы вовсе.
     */
    attachedBySide: ['executor', 'operator'],
  },
  // Исходное требование заказчика: основание платежа — отношения компании с подрядчиком.
  invoice: {
    statuses: ['in_work', 'done', 'accepted', 'cancelled'],
    visibleTo: ['finance'],
    attachedBy: ['finance'],
    attachedBySide: ['executor', 'operator'],
  },
  // Обращаться по гарантии будет заявитель, и талон нужен ему на руках; сумм в нём нет.
  warranty_card: {
    statuses: ['in_work', 'done', 'accepted', 'cancelled'],
    visibleTo: ['finance', 'requester'],
    attachedBy: ['finance'],
    attachedBySide: ['executor', 'operator'],
  },
};

/** Виден ли вид документа этой аудитории — вопрос и списка файлов, и прямой ссылки на файл (Р7). */
export function isServiceFileKindVisible(
  kind: ServiceFileKind,
  audience: ServiceRequestAudience,
): boolean {
  return SERVICE_FILE_KIND_POLICY[kind].visibleTo.includes(audience);
}

/**
 * Виды документов, которые аудитория видит. Порядок — порядок `SERVICE_FILE_KINDS`: перечень
 * уходит и в SQL-условие прямой ссылки, и в фильтр списка, и стабильный порядок там дешевле
 * случайного.
 */
export function visibleServiceFileKinds(
  audience: ServiceRequestAudience,
): readonly ServiceFileKind[] {
  return SERVICE_FILE_KINDS.filter((kind) => isServiceFileKindVisible(kind, audience));
}

/**
 * Кладёт ли аудитория такой вид ХОТЬ КОГДА-НИБУДЬ — потолок аудитории, без статуса и без стороны.
 *
 * Вопрос отдельный, потому что и ответ на него отдельный: заявителю, приложившему счёт, «сейчас
 * неподходящий статус» пообещал бы, что в другом статусе получится, — а не получится никогда.
 * Сервер отвечает на это `403` и не разбирает ни статуса, ни стороны.
 */
export function isServiceFileKindAttachable(
  kind: ServiceFileKind,
  audience: ServiceRequestAudience,
): boolean {
  return SERVICE_FILE_KIND_POLICY[kind].attachedBy.includes(audience);
}

/** Чья это бумага — перечнем сторон, как он записан в таблице (для текста отказа). */
export function serviceFileAttachingSides(
  kind: ServiceFileKind,
): readonly ServiceFileAttachingSide[] {
  return SERVICE_FILE_KIND_POLICY[kind].attachedBySide;
}

/**
 * СТОРОНА ЗАЯВКИ разрешает субъекту этот вид документа — второй слой подшивки (Р3).
 *
 * Сторона исполнителя спрашивается КОНТРАКТНЫМ `actsAsServiceExecutor` — тем же, каким её читают
 * коридор ходов, предикаты действий и портал: третьего представления стороны в модуле быть не
 * должно, а разошлись бы они молча — кнопкой, ведущей в 403. The estimate's author is asked of
 * `actsAsEstimateAuthor`, the function the estimate doors themselves ask (workflow-fixes plan Р6).
 *
 * «Ведение» — правом `serviceRequests.status`, и это ровно та сторона, что доводит заявку по
 * циклу. Финансовая аудитория стороной не является ни в одном виде: `serviceRequests.finance`
 * отвечает «этому читателю видны деньги», а не «эта бумага его» (Н2).
 *
 * Признаки назначения приходят ПОСЧИТАННЫМИ, как и у остальных предикатов действий: сервер читает
 * их из строк заявки под блокировкой (Р4), портал — из карточки.
 */
export function canAttachServiceFileSide(
  kind: ServiceFileKind,
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (!subject) return false;
  return SERVICE_FILE_KIND_POLICY[kind].attachedBySide.some((side) => {
    switch (side) {
      case 'any':
        return true;
      case 'executor':
        return actsAsServiceExecutor(subject, assignment, has);
      case 'estimate_author':
        return actsAsEstimateAuthor(subject, assignment, has);
      case 'operator':
        return has('serviceRequests.status');
    }
  });
}

/**
 * Принимает ли заявка этот вид документа ОТ ЭТОГО СУБЪЕКТА прямо сейчас — все три сомножителя
 * разом: аудитория, статус и сторона.
 *
 * Сомножители отвечают на РАЗНЫЕ вопросы, и различие это не формальное: неподходящий статус —
 * ошибка формы, которую человек исправляет выбором (сервер отвечает `422`), а запрет по аудитории
 * или стороне — отсутствие права (`403`). Слитые в один ответ, они предложили бы заявителю
 * «исправить» то, что исправить нельзя, — поэтому сервер спрашивает слои по одному
 * (`isServiceFileKindAttachable`, `canAttachServiceFileSide`), а эта функция отвечает целиком там,
 * где нужен один ответ: перечень видов формы и всякая проверка «можно ли вообще».
 *
 * КОНЪЮНКЦИЯ ДВУХ ПЛАНОВ, а не вторая матрица (Р3): аудитория приехала планом карточки заявителя и
 * осталась первым слоем (`requester` кладёт только вложение), сторона — этим планом. Ни одна из
 * половин не переписана: `finance` сам по себе подшивку не разрешает.
 *
 * ЧЕТВЁРТЫЙ СОМНОЖИТЕЛЬ — САМА ЗАЯВКА (Р5, Н17), и в таблицу видов он не помещается: политика
 * `estimate` знает статус, аудиторию и сторону, но про подрядчика не знает и знать не может —
 * признак Р4 читается из строки, а не из вида документа. Без этой строки внутренний исполнитель
 * подшивал бы к заявке документ с ценами, которых по ней не бывает: этап объёма работ у неё снят
 * целиком, а вид документа остался бы открытым и пронёс бы деньги мимо.
 *
 * ВИДИМОСТЬ УЖЕ ПОДШИТОГО НЕ ОТНИМАЕТСЯ — `isServiceFileKindVisible` этой пары не спрашивает: до
 * выпуска внутренние заявки объём работ проходили, и спрятать бумагу значило бы стереть основание
 * принятого решения (тот же довод, что у исторической вкладки в Р7).
 */
export function canAttachServiceFile(
  kind: ServiceFileKind,
  status: ServiceRequestStatus,
  audience: ServiceRequestAudience,
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  request: { kind: ServiceRequestKind; serviceCounterpartyId: string | null },
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  if (kind === 'estimate' && !serviceRequestNeedsEstimate(request)) return false;
  return (
    isServiceFileKindAttachable(kind, audience) &&
    SERVICE_FILE_KIND_POLICY[kind].statuses.includes(status) &&
    canAttachServiceFileSide(kind, subject, assignment, has)
  );
}

/**
 * Что предложить в форме подшивки. Статус сюда передаётся «эффективный» (Р110): у отложенной
 * заявки виды считаются по тому статусу, из которого её отложили, — заморозка останавливает ход
 * заявки, а не жизнь вокруг неё. Считать это здесь нельзя: `heldFromStatus` знает строка, а не вид
 * документа.
 *
 * Субъект и признаки назначения обязательны с Р3: форма, спрашивающая одну аудиторию, предлагала
 * бы ИТ-службе «Акт», на котором придёт 403, — та самая кнопка в никуда, ради которой правила и
 * живут в контрактах.
 *
 * Признаки самой заявки — с Р5 и по той же причине: перечень производный, и не приди пара Р4 сюда,
 * форма предлагала бы «Объём работ» по внутреннему ремонту, а сервер отвечал бы на выбранный файл
 * отказом.
 */
export function attachableServiceFileKinds(
  status: ServiceRequestStatus,
  audience: ServiceRequestAudience,
  subject: AccessSubject | null | undefined,
  assignment: ServiceExecutorAssignment,
  request: { kind: ServiceRequestKind; serviceCounterpartyId: string | null },
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): readonly ServiceFileKind[] {
  return SERVICE_FILE_KINDS.filter((kind) =>
    canAttachServiceFile(kind, status, audience, subject, assignment, request, has),
  );
}

/**
 * Решение по одному полю DTO (Р3, Р5). Три вида решения, и четвёртого быть не может:
 *
 * - `'all'` — поле уходит обеим аудиториям как есть;
 * - `{ requester: … }` — заявителю подставляется НЕЙТРАЛЬНОЕ значение того же типа. Значение стоит
 *   в самой карте, а не в теле проекции: «чем заменяем» — часть решения об аудитории, и разнесённое
 *   по двум местам оно разъехалось бы;
 * - вложенная карта — для объекта, который вычищается не целиком (`completion`: «работы закрыты 14
 *   августа» не деньги, а факт, которого заявитель ждёт);
 * - `'visible_file_kinds'` — только для списка файлов: он не заменяется значением, а режется
 *   перечнем видимых видов (Р7). Тип разрешает это решение ИСКЛЮЧИТЕЛЬНО там, где поле и правда
 *   список файлов, — поставить его любому другому полю компилятор не даст.
 *
 * Объект со своим полем `requester` вложенной картой быть НЕ МОЖЕТ, и это защита, а не каприз:
 * проекция отличает нейтральное значение от вложенной карты по наличию ключа `requester`, и такой
 * объект она приняла бы за значение. Запретив его на уровне типа, мы получаем однозначность по
 * построению, а не по внимательности читателя.
 */
export type AudienceDecision<V> =
  | 'all'
  | { readonly requester: V }
  | (NonNullable<V> extends readonly ServiceRequestFileDto[] ? 'visible_file_kinds' : never)
  | (NonNullable<V> extends readonly unknown[]
      ? never
      : NonNullable<V> extends { requester: unknown }
        ? never
        : NonNullable<V> extends object
          ? AudiencePolicy<NonNullable<V>>
          : never);

/**
 * Исчерпывающая классификация полей: `-?` требует ключ на КАЖДОЕ поле — и обязательное, и
 * необязательное.
 *
 * Обычный «список того, что вычесть» был бы fail-open: новое поле, которое забыли перечислить,
 * осталось бы в готовом DTO и уехало заявителю целым. Поэтому вычитание из готового DTO допустимо
 * только через исчерпывающую карту — и облегчённый второй DTO при этом всё равно не заводится:
 * форма ответа обязана остаться одна, иначе портал получил бы две.
 */
export type AudiencePolicy<T> = {
  [K in keyof T]-?: AudienceDecision<T[K]>;
};

/**
 * Что видит заявитель в карточке заявки (таблица Р4).
 *
 * Добавление поля в `ServiceRequestDto` без строки здесь ЛОМАЕТ КОМПИЛЯЦИЮ — это и есть главный
 * сторож плана: забыть классифицировать поле невозможно, а не «нужно не забыть».
 *
 * `consumables` остаются целиком, и это решение, а не недосмотр: цен в строках расходников нет ни
 * одной, вкладка «Номенклатура» — предмет заявки на расходники, а не её финансовая сторона.
 * `chat`, `executors`, `service` and `itApproval` stay whole on the same boundary: the plan removes
 * structural financial fields and is not a DLP filter for free text (Г4).
 */
export const SERVICE_REQUEST_FIELD_AUDIENCE = {
  // «В каком объёме собран ответ» — само поле и есть ответ проекции о себе.
  audience: { requester: 'requester' },
  /*
   * Сторона заказчика этого читателя — не деньги и не кухня: она отвечает «моя ли это заявка по
   * оси моей роли», и урезанная аудитория задаёт тот же вопрос, что полная. Спрятать её от
   * `requester` значило бы погасить его собственные кнопки правки и удаления — карта режет
   * финансовую сторону заявки, а не права читателя (Г4).
   */
  inCustomerScope: 'all',
  id: 'all',
  num: 'all',
  displayNumber: 'all',
  kind: 'all',
  status: 'all',
  statusChangedAt: 'all',
  waitingOn: 'all',
  heldFromStatus: 'all',
  holdReason: 'all',
  equipment: 'all',
  /**
   * СООБЩЕНИЕ О ТЕХНИКЕ ВИДЯТ ВСЕ, КТО ВИДИТ ЗАЯВКУ, — автор, служба, назначенный исполнитель.
   * Решение принято словами, а не досталось по умолчанию (план кандидатов, §9; ADR 0160, Р3).
   *
   * ЧЕМ ОТВЕЧАЕТ ЭТА КАРТА. Ровно одним вопросом — «видны ли этому читателю ДЕНЬГИ этой заявки»
   * (Г4): смета, её суммы, согласование, корректировка акта. Блок кандидата не несёт ни одной
   * цифры — заявленная модель, два номера с шильдика, состояние проверки и причина отказа, — и
   * вычитать из него нечего по самому предмету карты.
   *
   * ЗАЯВИТЕЛЬ — ГЛАВНЫЙ ЕГО ЧИТАТЕЛЬ, а не терпимый. Кандидата написал он сам, и оба ответа
   * проверки адресованы ему: «идёт» — чтобы не звонить и не спрашивать, «отклонено, потому что…» —
   * чтобы понять, что делать дальше. Закрой мы блок от `requester`, плашка погасла бы ровно у того
   * человека, ради которого её и рисуют, а автор заявки узнавал бы об отказе звонком в ИТ-службу —
   * тем самым звонком, ради отмены которого модуль заводился.
   *
   * НАЗВАННЫЕ АЛЬТЕРНАТИВЫ И ПОЧЕМУ ОНИ ХУЖЕ:
   *
   *   1. `{ requester: null }` — «кандидат это внутренняя кухня проверки». Отдаёт заявителю
   *      карточку, у которой предмет пуст: `equipment` у такой заявки `null` по построению (Р5), и
   *      без блока автор видел бы «Без аппарата» на заявке, которую сам завёл про конкретный
   *      принтер. Это не сокрытие цифр, а потеря предмета.
   *   2. Вложенная карта: состояние показать, `decisionReason` вычеркнуть. Даёт «Предмет отклонён»
   *      без единого слова — ровно то, что запрещает Р15, и хуже полного молчания: человек видит
   *      отказ и не видит причины.
   *   3. Гейт по `officeEquipment.read` в сборке DTO — тем же приёмом, что закрыт `warrantyUntil`
   *      (решение владельца 04.09.2026). Соблазн симметрии обманчив: `warrantyUntil` приходит из
   *      ЖИВОЙ карточки справочника, а кандидат — не запись справочника вовсе (Р1), он предмет
   *      самой заявки и живёт в своей таблице. Права справочника нет ни у сервисной компании
   *      (`COUNTERPARTY_TYPE_PERMISSIONS.service`), ни у рядового заявителя — то есть гейт погасил
   *      бы блок у подрядчика, который поедет на этот аппарат смотреть, и у автора сообщения. Оба
   *      остались бы с заявкой без предмета.
   *
   * ЧЕГО ЭТА СТРОКА НЕ РЕШАЕТ. Кто видит саму заявку — вопрос `serviceRequestVisibilityWhere`, и
   * посторонний до проекции не доходит вовсе. Область ОЧЕРЕДИ проверки (Р9) тоже осталась при
   * очереди: сюда уехали шесть полей, а не DTO кандидата целиком — почему именно они, написано при
   * `ServiceRequestCandidateDto`.
   */
  equipmentCandidate: 'all',
  object: 'all',
  objectOverridden: 'all',
  objectMismatch: 'all',
  // Кем разобрано расхождение — не про деньги, а про место: видно тем же, кому видна сама заявка.
  objectMismatchResolvedBy: 'all',
  // The place executors travel to. Shown to service contractors too even though the directory is
  // closed to them: the owner accepted that on 29.09.2026 (ADR 0215) — the unit's place is what the
  // request is about, not a view into the fleet.
  currentPlace: 'all',
  customerDepartment: 'all',
  equipmentDepartment: 'all',
  requesterPlace: 'all',
  description: 'all',
  responsibleName: 'all',
  responsiblePhone: 'all',
  isUrgent: 'all',
  urgencyReason: 'all',
  service: 'all',
  executors: 'all',
  itApproval: 'all',
  warrantyClaim: 'all',
  /*
   * Признак повтора — не деньги заявки, а факт о её предмете, и заявителю он не открывает ничего:
   * совпадения посчитаны В ЕГО СОБСТВЕННОЙ области видимости (Р8), то есть по заявкам, которые он
   * и так видит списком. Вычитать здесь нечего.
   *
   * Кому метку ПОКАЗЫВАТЬ — вопрос развилки В4, и решается он не этой картой: карта режет готовое
   * DTO, а «не считать признак для такого-то читателя» — решение сборки. Поставь мы здесь
   * `{ requester: undefined }`, заявитель получал бы поле, вычтенное после того, как сервер его
   * посчитал, — то есть цену расчёта без единого его следа на экране.
   */
  repeat: 'all',
  // Ноль, а не `null`: ревизия — счётчик, и «объёма работ не показываем» здесь читается как «его
  // ещё не собирали». Тип поля при этом не меняется.
  estimateRevision: { requester: 0 },
  estimatePendingRevision: { requester: null },
  estimateSubmittedAt: { requester: null },
  estimatedTotalAmount: { requester: null },
  approval: { requester: null },
  /*
   * ЧЕТЫРЕ ПОЛЯ ВОЛНЫ ОСВОБОЖДЕНИЯ — ВСЕ ВЫЧИТАЮТСЯ У ЗАЯВИТЕЛЯ, и это ровно тот вопрос, на который
   * карта и отвечает (Г4): как в этой заявке обошлись с ДЕНЬГАМИ. «Принято без согласования»,
   * «подана счётом», «оспорено» — три разных ответа про устройство денежного решения между компанией
   * и подрядчиком; заявителю, от которого скрыты и суммы, и подпись, они показали бы обсуждение цены,
   * не показав самой цены.
   *
   * Формат вычитается вместе с остальными намеренно, хотя цифры в нём нет. Заявитель не видит ни
   * строк, ни итога (`items`, `estimatedTotalAmount`), и слово «Документом (счёт)» без них означало бы
   * для него только одно — что счёт существует и его содержимое от него скрыли. Планку закрывающего
   * документа это не ломает: её спрашивают те, кто деньги видит, а приёмку заявитель не делает вовсе.
   */
  estimateFormat: { requester: null },
  estimatePendingSource: { requester: null },
  exemption: { requester: null },
  dispute: { requester: null },
  items: { requester: [] },
  consumables: 'all',
  /*
   * Вложенной картой, а не `{ requester: null }` целиком: дата закрытия работ — не деньги, а факт,
   * которого заявитель ждёт больше всего остального в этой карточке.
   */
  completion: {
    completedAt: 'all',
    totalAmount: { requester: null },
    adjustmentAmount: { requester: null },
    // Пустая строка, а не `null`: поле необязательным не объявлено, и `null` в нём означал бы
    // форму ответа, которой у DTO нет.
    adjustmentReason: { requester: '' },
  },
  acceptedByName: 'all',
  acceptedAt: 'all',
  acceptanceSource: 'all',
  replacementRecommended: 'all',
  rejectionResolution: 'all',
  comment: 'all',
  chat: 'all',
  files: 'visible_file_kinds',
  createdByName: 'all',
  createdAt: 'all',
  updatedAt: 'all',
  deletedAt: 'all',
  version: 'all',
} satisfies AudiencePolicy<ServiceRequestDto>;

/** Нейтральное значение отличается от вложенной карты ключом `requester` — тип это и гарантирует. */
function isNeutralValue(decision: unknown): decision is { readonly requester: unknown } {
  return typeof decision === 'object' && decision !== null && 'requester' in decision;
}

/**
 * Значение из карты копируется, а не кладётся ссылкой: карта — константа модуля, и общий пустой
 * массив, попавший в сотню ответов, первая же мутация испортила бы разом во всех.
 */
function neutralCopy(value: unknown): unknown {
  return Array.isArray(value) ? [...value] : value;
}

/**
 * Обход карты. Аудитория аргументом не ходит: карта описывает ровно ОДНО решение — «что видит
 * `requester`», — и финансовая аудитория до этой функции не доходит вовсе (проекция возвращает DTO
 * раньше). Параметр здесь означал бы, что бывает и вторая карта, которой нет.
 */
function applyAudiencePolicy<T extends object>(value: T, policy: Record<string, unknown>): T {
  const projected: Record<string, unknown> = { ...(value as Record<string, unknown>) };
  for (const [field, decision] of Object.entries(policy)) {
    if (decision === 'all') continue;
    if (decision === 'visible_file_kinds') {
      const files = projected[field] as readonly ServiceRequestFileDto[];
      projected[field] = files.filter((file) => isServiceFileKindVisible(file.kind, 'requester'));
      continue;
    }
    if (isNeutralValue(decision)) {
      projected[field] = neutralCopy(decision.requester);
      continue;
    }
    // Вложенная карта. Пустой объект остаётся пустым: «закрытия не было» и «закрытие есть, но без
    // сумм» — разные состояния, и подменять первое вторым проекция не вправе.
    const nested = projected[field];
    projected[field] =
      nested == null
        ? nested
        : applyAudiencePolicy(nested as object, decision as Record<string, unknown>);
  }
  return projected as T;
}

/**
 * Ответ в объёме аудитории. Зовётся там, где принципал уже есть, — в сборке DTO, — и ОДИН раз на
 * ответ: карточка, список, ответы действий и архив идут одной сборкой все до одного.
 *
 * `finance` получает исходный объект без копирования: проекция не должна стоить лишнего обхода
 * там, где вычитать нечего.
 *
 * ИДЕМПОТЕНТНА, и это требование, а не свойство: результат применяется к ответам действий, и
 * повторное применение к уже урезанному DTO обязано дать то же самое. Держится это тем, что все
 * подстановки — константы, а фильтр файлов повторно ничего не находит.
 */
export function projectServiceRequestForAudience(
  dto: ServiceRequestDto,
  audience: ServiceRequestAudience,
): ServiceRequestDto {
  if (audience === 'finance') return dto;
  return applyAudiencePolicy(dto, SERVICE_REQUEST_FIELD_AUDIENCE);
}

/**
 * The card cut for a reader the nomenclature is hidden from (`serviceConsumablesHiddenFrom`,
 * workflow-fixes plan Р7): no composition rows and no documents.
 *
 * A SECOND PROJECTION NEXT TO THE AUDIENCE ONE, NOT A ROW OF ITS MAP. The audience map answers
 * «may this reader see the money of this row» and has two values; the contractor is on the `finance`
 * side of it (he sees the money of his own repair), so a cut keyed by audience would never reach
 * him. The question here is a pair «request × reader» and the map has no reader.
 *
 * The files go with the rows because every kind of document of a consumables request can name the
 * item: a photograph of the toner, an invoice for it. Idempotent, like the audience projection: the
 * cut fields are replaced by constants.
 */
export function projectServiceRequestForConsumablesReader(
  dto: ServiceRequestDto,
  subject: AccessSubject | null | undefined,
): ServiceRequestDto {
  if (!serviceConsumablesHiddenFrom(dto, subject)) return dto;
  return { ...dto, consumables: [], files: [] };
}

/**
 * Проекция СОСЕДНЕГО типа по его собственной карте (Р13): строка «Истории обслуживания» в карточке
 * единицы техники и событие её ленты — не `ServiceRequestDto`, а сумма ремонта в них та же самая.
 *
 * Общий у них не код карточки, а понятие аудитории (Р3) — поэтому карта у каждого типа СВОЯ и
 * стоит рядом со своим типом, а вот ОБХОД карты один на всех. Второй такой обход означал бы второе
 * правило подстановки нейтрального значения — и разошлись бы они молча, на первом же поле, которое
 * заменяют массивом (общая константа, попавшая в сотню ответов, портится первой же мутацией; это
 * ровно то, ради чего здесь стоит `neutralCopy`).
 *
 * `finance` получает исходный объект без копирования — по той же причине, что и в проекции
 * карточки заявки: вычитать нечего, и лишний обход не нужен.
 */
export function projectByAudiencePolicy<T extends object>(
  value: T,
  audience: ServiceRequestAudience,
  policy: AudiencePolicy<T>,
): T {
  if (audience === 'finance') return value;
  return applyAudiencePolicy(value, policy as Record<string, unknown>);
}

/**
 * Ключ изменения в истории заявки — тот же перечень, что и подписи (`serviceRequestChangeLabels`).
 *
 * Выведен из словаря подписей, а не объявлен вторым списком рядом: разъехавшись, они дали бы либо
 * строку истории с сырым именем поля вместо подписи, либо изменение, о котором карта аудиторий
 * ничего не знает. Один перечень — один ответ на оба вопроса.
 */
export type ServiceRequestChangeKey = keyof typeof serviceRequestChangeLabels;

/** Что делать с изменением при `requester`. */
export type ServiceHistoryChangeDecision = 'all' | 'finance' | 'visible_file_kinds';

/**
 * Классификация ключей истории (Р10): заявитель видит, ЧТО заявка двигалась, но не видит цифр.
 *
 * Живёт здесь, а не в `request-history.ts`, ровно потому, что перечень ключей живёт здесь:
 * `request-history.ts` — общая форма событий для трёх модулей заявок, и модульного в ней только
 * подписи полей. Ключи заявки на обслуживание объявлены рядом с её подписями, и карта аудиторий
 * стоит там же — иначе решение про ключ и сам ключ разъехались бы по файлам.
 *
 * Классификация ЗАКРЫТА `Record`'ом: новый ключ истории без решения не компилируется. Простой
 * список «денежных ключей» был бы blacklist и пропустил бы первое же новое денежное изменение.
 *
 * По ключам, а не по действиям аудита, и это не выбор удобства: записи прошлых месяцев уже лежат в
 * `metadata.changes` снимком, и добавить в них признак «денежное» нечем. Ключ — то единственное,
 * что у старой записи есть и что не меняется.
 */
export const SERVICE_HISTORY_CHANGE_AUDIENCE: Record<
  ServiceRequestChangeKey,
  ServiceHistoryChangeDecision
> = {
  description: 'all',
  dueDate: 'all',
  customerDepartment: 'all',
  responsibleName: 'all',
  responsiblePhone: 'all',
  isUrgent: 'all',
  urgencyReason: 'all',
  itApproval: 'all',
  comment: 'all',
  warrantyClaim: 'all',
  // Перечень режется по видимым видам: «Счёт: s.pdf» назвал бы то, чего заявителю не видно.
  filesAdded: 'visible_file_kinds',
  filesRemoved: 'visible_file_kinds',
  // В тексте строки стоит цена: «Запчасть «Ролик подачи», 1 × 1800,00 ₽».
  estimateItemsAdded: 'finance',
  estimateItemsRemoved: 'finance',
  rejectionResolution: 'all',
  // Состав объёма работ, а не его цена, — и убирается по тому же решению, что и вкладка (§12,
  // п. 2): третьей аудитории «состав без цен» план не заводит.
  itemsNotPerformed: 'finance',
  itemsPartial: 'finance',
  // Расходники остаются целиком: цен в этих строках нет ни одной.
  consumablesIssued: 'all',
  consumablesReturned: 'all',
  consumables: 'all',
  /*
   * Перемещение денег не содержит вовсе, а «куда увезли аппарат» заявителю видеть и положено, и
   * полезно: он по этой заявке его и ждёт.
   */
  moveObject: 'all',
  moveLocation: 'all',
  moveDepartment: 'all',
  moveState: 'all',
};

/**
 * Разделитель перечня в событии-списке — тот же, которым его собрал дифф (`changeSet().listed`).
 * Разъехавшись, они дадут не ошибку, а неразрезанную строку, где скрытый документ уедет целиком.
 */
const HISTORY_LIST_SEPARATOR = ', ';

/**
 * Префиксы скрытых видов документов. Дифф пишет не-вложение как «`Вид`: имя файла», а вложение —
 * голым именем, поэтому режется список именно по префиксу метки.
 *
 * ЦЕНА ЗАПИСАНА ЗДЕСЬ, а не выясняется потом. Перечень в истории хранится ОДНОЙ строкой, и
 * восстановить состав можно только разрезав её: имя файла с «, » внутри разрежется пополам, а
 * вложение, названное «Счёт: 12.pdf», спрячется вместе со счетами. Оба промаха уводят в одну
 * сторону — показать МЕНЬШЕ положенного, — и это единственное направление ошибки, которое здесь
 * допустимо.
 */
function hiddenFileKindPrefixes(audience: ServiceRequestAudience): readonly string[] {
  return SERVICE_FILE_KINDS.filter((kind) => !isServiceFileKindVisible(kind, audience)).map(
    (kind) => `${serviceFileKindLabels[kind]}: `,
  );
}

function projectHistoryChange(
  change: RequestChangeDto,
  hiddenPrefixes: readonly string[],
): RequestChangeDto[] {
  /*
   * Ключ приходит из аудита строкой, поэтому смотрим на карту как на словарь с промахом. Промах
   * возможен по-настоящему: записи прошлых месяцев несут ключи, которых сегодня нет вовсе (так уже
   * вышло с `dueDate` — поле сняли, записи остались).
   */
  const decision = (
    SERVICE_HISTORY_CHANGE_AUDIENCE as Record<string, ServiceHistoryChangeDecision | undefined>
  )[change.field];
  // FAIL-CLOSED: ключ, о котором карта ничего не знает, заявителю НЕ показывается. Обратное
  // умолчание означало бы, что снятое денежное поле всплывает в истории само — и всплывает молча,
  // потому что никто не писал строки, которая бы это разрешила.
  if (decision !== 'all' && decision !== 'visible_file_kinds') return [];
  if (decision === 'all') return [change];
  const hidden = (name: string): boolean =>
    hiddenPrefixes.some((prefix) => name.startsWith(prefix));
  const kept = (change.to ?? '').split(HISTORY_LIST_SEPARATOR).filter((item) => !hidden(item));
  /*
   * ПАРЫ «ИДЕНТИФИКАТОР → ИМЯ» РЕЖУТСЯ ТЕМ ЖЕ ПРАВИЛОМ, ЧТО И СТРОКА.
   *
   * Поле `files` заведено волной карантина (план освобождения от подписи, Р6 п. 4), чтобы читатель
   * истории мог спросить состояние файла и погасить имя запертого. Но оно везёт ИМЕНА — те же самые,
   * что режет строка `to` по префиксам скрытых видов, — и, оставленное без резки, отдало бы
   * заявителю «Счёт: счёт.pdf» новым полем, минуя единственное сегодняшнее место, где это
   * запрещено. Ошибка была бы тихой вдвойне: строка в ленте выглядит урезанной правильно, а имя
   * уезжает рядом, в поле, которое портал показывает не текстом.
   */
  const keptFiles = change.files?.filter((file) => !hidden(file.filename));
  // Изменение, от которого ничего не осталось, уходит: «Прикреплены файлы: » без единого имени —
  // не событие, а пустая строка в ленте.
  return kept.length > 0
    ? [
        {
          ...change,
          to: kept.join(HISTORY_LIST_SEPARATOR),
          ...(change.files ? { files: keptFiles ?? [] } : {}),
        },
      ]
    : [];
}

/**
 * История в объёме аудитории (Р10).
 *
 * СОБЫТИЕ С ОПУСТЕВШИМ СПИСКОМ ИЗМЕНЕНИЙ НЕ ВЫБРАСЫВАЕТСЯ: «Объём работ предъявлен» без цифр — это
 * и есть ответ на вопрос «что происходило с моей заявкой», а выброшенное событие оставило бы в
 * ленте провал, который читается как поломка портала.
 *
 * Обобщена по типу события, а не привязана к `RequestHistoryEntryDto`, и это не универсальность
 * впрок: у истории заявки на обслуживание СВОЙ тип события (`ServiceRequestHistoryEntryDto` —
 * собственный перечень статусов и ревизия сметы, ADR 0085 §5), и он не подставляется на место
 * общего. Требуется от события ровно то, что функция читает, — список изменений; всё остальное она
 * переносит как есть, поэтому расширить событие полем можно, не трогая фильтр.
 */
export function projectHistoryForAudience<T extends { changes: RequestChangeDto[] }>(
  entries: readonly T[],
  audience: ServiceRequestAudience,
): T[] {
  if (audience === 'finance') return [...entries];
  const hiddenPrefixes = hiddenFileKindPrefixes(audience);
  return entries.map((entry) => ({
    ...entry,
    changes: entry.changes.flatMap((change) => projectHistoryChange(change, hiddenPrefixes)),
  }));
}

/**
 * Which history keys carry the nomenclature of a consumables request or the documents attached to
 * it (workflow-fixes plan Р7). Closed by `Record` like the audience map above: a new history key
 * does not compile without an answer here, so the decision cannot be forgotten.
 *
 * `filesAdded` and `filesRemoved` are here although they carry no item names themselves: the file
 * name and the kind of a document of a consumables request («Счёт: Тонер Ricoh MP C305.pdf») tell
 * exactly what the reader is not to learn, and the contractor does not see the documents of such a
 * request at all.
 */
export const SERVICE_HISTORY_CHANGE_NOMENCLATURE: Record<
  ServiceRequestChangeKey,
  'all' | 'hidden_from_contractor'
> = {
  description: 'all',
  dueDate: 'all',
  customerDepartment: 'all',
  responsibleName: 'all',
  responsiblePhone: 'all',
  isUrgent: 'all',
  urgencyReason: 'all',
  itApproval: 'all',
  comment: 'all',
  warrantyClaim: 'all',
  filesAdded: 'hidden_from_contractor',
  filesRemoved: 'hidden_from_contractor',
  estimateItemsAdded: 'all',
  estimateItemsRemoved: 'all',
  rejectionResolution: 'all',
  itemsNotPerformed: 'all',
  itemsPartial: 'all',
  consumablesIssued: 'hidden_from_contractor',
  consumablesReturned: 'hidden_from_contractor',
  consumables: 'hidden_from_contractor',
  moveObject: 'all',
  moveLocation: 'all',
  moveDepartment: 'all',
  moveState: 'all',
};

/**
 * The history cut for a reader the nomenclature is hidden from — the history twin of
 * `projectServiceRequestForConsumablesReader`. `hidden` is the answer of
 * `serviceConsumablesHiddenFrom` computed by the caller for the one request of the page.
 *
 * FAIL-CLOSED, as the audience cut: a key this map knows nothing about is dropped. Rows of past
 * months carry keys that no longer exist, and the reverse default would let a removed field that
 * once held an item name come back into the feed silently. As there, an entry whose changes all
 * disappeared stays: «состав изменён» without the rows is still the answer to «what happened to my
 * request».
 */
export function projectHistoryForConsumablesReader<T extends { changes: RequestChangeDto[] }>(
  entries: readonly T[],
  hidden: boolean,
): T[] {
  if (!hidden) return [...entries];
  const decisions = SERVICE_HISTORY_CHANGE_NOMENCLATURE as Record<
    string,
    'all' | 'hidden_from_contractor' | undefined
  >;
  return entries.map((entry) => ({
    ...entry,
    changes: entry.changes.filter((change) => decisions[change.field] === 'all'),
  }));
}

// ── Request chat (office-equipment-chat-plan.md, ADR 0141) ──
//
// A feed of messages marked "addressed to". A mark, not a visibility restriction (survey decision of
// 28.08.2026): everyone who sees the request sees the text, and the addressee drives highlighting
// and the message letter. The chat replaced the executor note, a single overwritable field per
// request; its route and DTO field were removed in release B.
//
// The rules live here and the facts come in as arguments (§3.2 of the plan), and that is not
// decoration. `AccessSubject` carries neither the account id, nor scopes, nor assignments, and
// `ServiceRequestDto` does not expose `createdBy`, so a shared "subject + DTO -> sides" function is
// impossible on the portal. Written anyway, it would become a second rule diverging from the server
// SILENTLY: highlighting would show one thing and the route would answer another. So the portal
// never calls these functions: it receives a ready `ServiceRequestChatSummaryDto` computed by the
// server with them.

/**
 * Стороны разговора. `all` — не сторона человека, а адресат «всем участникам»: им можно адресовать,
 * но им нельзя БЫТЬ. Поэтому значение стоит в словаре (его пишут строкой в базу, его рисуют
 * ярлыком в ленте), а из `participantSidesOf` не возвращается никогда.
 */
export const SERVICE_CHAT_SIDES = ['all', 'customer', 'operator', 'it', 'service'] as const;
export const serviceChatSideSchema = z.enum(SERVICE_CHAT_SIDES);
export type ServiceChatSide = (typeof SERVICE_CHAT_SIDES)[number];

export const serviceChatSideLabels: Record<ServiceChatSide, string> = {
  all: 'Всем участникам',
  customer: 'Заявителю',
  // «Оргтехнике (ведение)», а не «Оператору»: оператором в портале зовут внешнего исполнителя
  // (роль `operator`, ADR 0038), и ярлык «Оператору» читался бы ровно наоборот — как реплика,
  // адресованная сервисной компании.
  operator: 'Оргтехнике (ведение)',
  it: 'Системному администратору',
  service: 'Сервисному центру',
};

/**
 * Откуда взялась реплика. `import` — перенесённое «Примечание исполнителя» (§3.9): у неё бывает
 * пустой автор и приблизительное время, и портал обязан подписать её иначе, чем написанную
 * человеком, — «перенесено из примечания исполнителя», без имени. Словарь закрыт `CHECK`'ом схемы,
 * а не соглашением: третье значение здесь означало бы реплику, о происхождении которой никто
 * ничего не знает.
 */
export const SERVICE_CHAT_ORIGINS = ['chat', 'import'] as const;
export type ServiceChatOrigin = (typeof SERVICE_CHAT_ORIGINS)[number];

/**
 * Что сервер знает о паре «этот человек ↔ эта заявка»: считается из принципала (id, роль, области,
 * коды наборов) и строк самой заявки — из того, чего в `AccessSubject` нет и не появится.
 *
 * Правовая половина субъекта сюда НЕ копируется булевыми полями, и это осознанно: права
 * спрашиваются у самого `AccessSubject`, который передаётся отдельным аргументом. Разложи мы `can`
 * по фактам — у матрицы прав завелось бы второе представление, и первый же новый набор полномочий
 * (ADR 0106) пришлось бы вписывать в оба, а разъехались бы они молча.
 */
export interface ServiceChatFacts extends RequestCustomerFacts {
  /** Id учётки читателя: с ним сравнивается поимённый адресат реплики. */
  userId: string;
  /*
   * `isAuthor` и `inCustomerScope` наследуются от `RequestCustomerFacts`, а не переписываются
   * здесь: те же два признака решают сторону заказчика у стража правки (`actsAsRequestCustomer`),
   * и вторая их запись рядом означала бы два ответа на вопрос «кто здесь заказчик». Признаки
   * отдельны друг от друга намеренно (§3.1): адресат «Заявителю» бьёт по всей стороне заказчика,
   * чтобы вопрос не завис, пока автор в отпуске, — а вот участником разговора коллега по отделу не
   * становится.
   */
  /** Он работает от контрагента, назначенного исполнителем ИМЕННО этой заявки, а не любого сервиса. */
  actsForAssignedService: boolean;
  /** Он назначен поимённо — строкой в `service_request_executors` этой заявки. */
  isNamedExecutor: boolean;
}

/**
 * Совпадает ли человек со стороной. Одна функция на оба вопроса — «я участник?» и «эта реплика мне?»
 * — потому что различие между ними ровно одно и оно параметром: у `customer` аудитория шире
 * участия (§3.1). Две отдельные копии формул `operator`, `it` и `service` разъехались бы на первой
 * же правке модели сторон, и разъехались бы незаметно: обе выглядели бы правдоподобно.
 *
 * `wideCustomer` — та самая разница. С ним считается аудитория адресата «Заявителю» (автор ∪ вся
 * сторона заказчика), без него — участие (только автор): заказчик решил обе вещи явно и
 * по-разному, «пишут стороны цикла и автор заявки».
 */
function matchesServiceChatSide(
  side: ServiceChatSide,
  subject: AccessSubject | null | undefined,
  facts: ServiceChatFacts,
  wideCustomer: boolean,
): boolean {
  switch (side) {
    // «Всем участникам» подходит каждому, кому заявка видна: это отказ от адресации, а не адресация
    // множеству, — сужать его до состава сторон значило бы прятать реплику от того, кому её и
    // писали «на всякий случай».
    case 'all':
      return true;
    case 'customer':
      return facts.isAuthor || (wideCustomer && facts.inCustomerScope);
    /*
     * Сторона «Ведение» — КОД ВЫДАННОГО НАБОРА, а не сумма прав (план профилей оргтехники, Р9), и
     * формула у неё общая с дверью повтора служебного письма: `actsAsServiceOperator`, где и
     * записано, почему код, почему админ отдельной строкой и почему подрядчик исключён явно.
     *
     * Прежде здесь стояла конъюнкция `status && assign`. Она давала ровно «Ведение» и админа, но
     * держалась на том, что эту пару прав больше никому не выдают вместе: `serviceRequests.status`
     * есть у типа контрагента `service` (подрядчику она открывает ЕГО половину цикла), `assign` у
     * подрядчика нет, `status` у ИТ-службы нет намеренно. С назначаемыми полномочиями (ADR 0106)
     * это перестало быть правдой — набор с той же парой прав собирается в проде руками, — и профиль
     * модуля начал бы выводиться из состава чужого набора.
     *
     * Смена поведения ОСОЗНАННАЯ: субъект, у которого `status` и `assign` есть, а кода «Ведения»
     * нет, стороной обсуждения больше не становится. Профиль — это код, а не сумма прав; иначе
     * человек, которому профиля никто не давал, видел бы яркими реплики администратору модуля.
     *
     * Золотой бейдж «ждёт меня» админ получает по той же причине, по которой проходит сюда
     * (`isWaitingOn` спрашивает `assign`), — второе правило про админа, расходящееся с первым,
     * было бы хуже.
     */
    case 'operator':
      return actsAsServiceOperator(subject);
    /*
     * Сторона ИТ-службы — код её набора, а не виза (Р9). Право `serviceRequests.approveIt`, которым
     * она опознавалась прежде, было МЁРТВЫМ (ручки `PATCH /:id/it-approval` нет, коридора визы
     * нет), и Э9 убрал его из набора миграцией E. Опознавайся сторона правом — эта уборка погасила
     * бы «Системного администратора» молча, вместе с яркой меткой и письмом. Порядок был жёсткий и
     * сработал: сперва код (Э3), потом уборка права (Э9), и ни разу наоборот.
     *
     * `admin` — по той же причине и с тем же смыслом, что в `operator`: правом он проходил сюда
     * сам, кодов у него нет.
     */
    case 'it':
      return subject?.role === 'admin' || hasGrantCode(subject, OFFICE_EQUIPMENT_IT_GRANT);
    /*
     * Дизъюнкция, а не одно условие: сторону исполнителя держат двое — назначенный контрагент
     * целиком и поимённо назначенный сотрудник (инхаус-ремонт ИТ-службы). Проверить это по субъекту
     * нельзя ни в одном из двух случаев: назначение — свойство заявки, а не учётки.
     *
     * У СВОЕГО СОТРУДНИКА НАЗНАЧЕНИЕ РАБОТАЕТ В ПАРЕ С ПРАВОМ (план аудита исполнителей, Р1;
     * находка Н9). Строка `service_request_executors` историческая: по ней написана переписка и
     * разосланы задания, и снимать её при отзыве набора нельзя. Значит одно её наличие означало бы,
     * что переведённый сисадмин остаётся «Сервисным центром» в заявке, которая видна ему по базовой
     * области: пишет от имени исполнителя, попадает в адресаты реплики «Исполнителю» и в будущую
     * почту по ним. Право читается из БД на каждом запросе (`loadPrincipal`), поэтому отзыв гасит
     * сторону следующим же запросом, а не по истечении сессии (И1, И3).
     *
     * У подрядчика права не спрашивают, и это не забывчивость, а та же причина, что в
     * `isServiceExecutor`: поимённых строк у него не бывает, `serviceRequests.execute` в наборе
     * типа контрагента `service` нет и не появится, — потребуй мы его, сервисная компания перестала
     * бы быть стороной собственной заявки.
     *
     * Разложению аудитории на SQL (`addressedToMeSql`) добавленный сомножитель не мешает: он
     * спрашивается у СУБЪЕКТА, одинаково при всех пробах признаков, — а разложение требует
     * дизъюнкции по признакам ЗАЯВКИ. У субъекта без права ветка `isNamedExecutor` просто не войдёт
     * в дизъюнкцию, и это ровно то, чего мы добиваемся.
     */
    case 'service':
      return (
        facts.actsForAssignedService ||
        (facts.isNamedExecutor && can(subject, 'serviceRequests.execute'))
      );
  }
}

/**
 * Мои стороны в этой заявке: «я участник разговора?». Отсюда берутся право писать, состав поля
 * «Кому» и блёклая точка непрочитанного.
 *
 * `all` в ответе не бывает: участником «всех» быть нельзя, и попади оно сюда — блёклая точка
 * загорелась бы у каждого, кому видна заявка, то есть ровно у тех, кто разговора не ведёт.
 *
 * Пустой список — наблюдатель: заявка ему видна, реплики он читает, но ни писать, ни быть
 * помеченным чужой перепиской не должен (§3.11 — граница названа до выката, а не после).
 */
export function participantSidesOf(
  subject: AccessSubject | null | undefined,
  facts: ServiceChatFacts,
): ServiceChatSide[] {
  return SERVICE_CHAT_SIDES.filter(
    (side) => side !== 'all' && matchesServiceChatSide(side, subject, facts, false),
  );
}

/** Адресат реплики: сторона ИЛИ учётка — ровно как `CHECK ((side IS NULL) <> (user_id IS NULL))`. */
export type ServiceChatAddressee =
  | { readonly side: ServiceChatSide; readonly userId?: null }
  | { readonly side?: null; readonly userId: string };

/**
 * Who receives a LETTER for a chat message (ADR 0159 decision 9, changed by ADR 0227).
 *
 * **Only the addressees, not the whole visible feed.** The addressee exists for exactly this (ADR
 * 0141, §3.12: the mark drives highlighting and the letter); forwarding everything would turn the
 * contractor's mailbox into a mirror of the internal conversation. The cost is named: a contractor
 * without an account reads only letters, and a message addressed to "own people" never reaches
 * him; the composer's default («Всем участникам») and the line under «Кому» soften it.
 *
 * The table is a closed `Record`: a new side must answer whom it mails, or a message silently
 * stops reaching half of its readers.
 *
 * `requester` is the request author (ADR 0227, D1/D2): «Заявителю» and «Всем участникам» reach
 * him — including service ↔ contractor messages written to «Всем участникам», an accepted
 * consequence (D2). Only the author: colleagues of the customer side see «Заявителю» brightly in
 * the portal but get no letter. The service planner ignores this target; the requester planner
 * (`service-request-requester-mail.ts`) is the one that reads it.
 *
 * `operator` and `it` give one target — the service mailbox: the module has one mail channel, and
 * the two cannot be told apart by delivery. Promising otherwise would lie about the address.
 */
export type ChatMailTarget = 'office' | 'service' | 'requester';

const CHAT_MAIL_TARGETS: Record<ServiceChatSide, ChatMailTarget[]> = {
  all: ['office', 'service', 'requester'],
  customer: ['requester'],
  operator: ['office'],
  it: ['office'],
  service: ['service'],
};

/** What a message mails: the targets of its sides and the named addressees of the message. */
export interface ChatMailPlan {
  targets: ChatMailTarget[];
  /** Named addressees: only an assigned account gets the letter (the server checks it). */
  userIds: string[];
}

/**
 * The letter plan of a message's addressees. Imported executor notes (`origin='import'`) never
 * mail anyone, the author included: their time is approximate, they often have no author, and they
 * moved in as a batch.
 */
export function chatMailTargets(
  addressees: { sides: readonly ServiceChatSide[]; users: readonly string[] },
  origin: ServiceChatOrigin = 'chat',
): ChatMailPlan {
  if (origin === 'import') return { targets: [], userIds: [] };
  const targets = new Set<ChatMailTarget>();
  for (const side of addressees.sides) {
    for (const target of CHAT_MAIL_TARGETS[side]) targets.add(target);
  }
  return { targets: [...targets], userIds: [...addressees.users] };
}

/**
 * Адресована ли реплика этому человеку — вопрос яркой метки, счёта в бейдже и будущего письма
 * (§3.12). Спрашивается у каждого, кому видна заявка, а не только у участников: коллега по отделу
 * получает яркую метку на реплику «Заявителю», не становясь при этом участником разговора.
 *
 * Поимённый адресат сравнивается с id учётки, а не выводится из прав: он и заведён затем, чтобы
 * написать ОДНОМУ из двух назначенных инженеров, — вывести такое из субъекта нечем.
 */
export function audienceMatches(
  addressee: ServiceChatAddressee,
  subject: AccessSubject | null | undefined,
  facts: ServiceChatFacts,
): boolean {
  if (addressee.side) return matchesServiceChatSide(addressee.side, subject, facts, true);
  return addressee.userId === facts.userId;
}

/**
 * Можно ли отправить реплику прямо сейчас: страж ручки и условие кнопки отправки.
 *
 * Два сомножителя, и второй — не «статус вообще», а именно закрытость: `isServiceRequestClosed`
 * тот же, что запрещает ход и правку. Своим списком статусов здесь было бы два ответа на вопрос
 * «заявка ещё жива», и «Отложенная» рано или поздно попала бы в один список и не попала в другой —
 * а она как раз тот случай, когда написать особенно нужно: она стоит и ждёт объяснения.
 */
export function canWriteChat(
  subject: AccessSubject | null | undefined,
  facts: ServiceChatFacts,
  status: ServiceRequestStatus,
): boolean {
  return participantSidesOf(subject, facts).length > 0 && !isServiceRequestClosed(status);
}

/**
 * Отправка реплики. Текст обязателен и ограничен теми же 2000 символами, что и снятое примечание
 * исполнителя: длиннее — это уже документ, и его подшивают вложением.
 *
 * Адресаты приходят двумя списками — сторонами и учётками, — потому что двумя списками они и
 * хранятся (§3.3): сторона это слово словаря, поимённый адресат — ссылка на учётку с проверкой «он
 * назначен на эту заявку». Склеенный список строк пришлось бы разбирать угадыванием «uuid это или
 * сторона», и первый же контрагент с именем-uuid оказался бы стороной.
 */
export const sendServiceChatMessageSchema = z
  .object({
    body: z.string().trim().min(1, 'Напишите текст сообщения').max(2000),
    addressees: z.object({
      sides: z.array(serviceChatSideSchema).max(SERVICE_CHAT_SIDES.length).default([]),
      /**
       * Верхняя граница — защита от тела с тысячей идентификаторов, а не учётное правило:
       * назначенных исполнителей у заявки единицы, и каждого из них сервер всё равно сверяет со
       * строками `service_request_executors` (§3.3).
       */
      users: z.array(uuidSchema).max(50).default([]),
    }),
  })
  .superRefine((value, ctx) => {
    const { sides, users } = value.addressees;
    // Реплика без адресата не бывает: у ленты нет «просто сообщений» — на пометке держатся и
    // подсветка, и состав получателей будущего письма. Умолчание портала — «Всем участникам».
    if (sides.length === 0 && users.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Выберите хотя бы одного адресата',
        path: ['addressees'],
      });
      return;
    }
    // «Всем» и «ещё вот этому» — противоречие: `all` уже включает любого, а при подсчёте яркости
    // такая пара давала бы двойной учёт одной реплики. Портал гасит остальные пункты при выборе
    // «Всем участникам», но верить в это нельзя — проверка серверная.
    if (sides.includes('all') && (sides.length > 1 || users.length > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '«Всем участникам» не сочетается с другими адресатами',
        path: ['addressees', 'sides'],
      });
    }
  });
export type SendServiceChatMessageInput = z.infer<typeof sendServiceChatMessageSchema>;

/**
 * Подтверждение прочтения: курсор, а не отметка времени (§3.4). Клиент присылает его ПОСЛЕ
 * успешного показа ленты, поэтому номер здесь — «докуда человек действительно дочитал», а не
 * «когда открыл окно».
 *
 * Верхней границы в схеме нет и быть не может: `throughSeq` сверяется с `lastSeq` самой заявки, а
 * его знает только сервер. Клиент, приславший миллион, гасил бы весь будущий разговор — ответ на
 * это 422, и он приходит от ручки (§3.4, п. 4). Ноль разрешён: это «не прочитано ничего».
 */
export const markServiceChatReadSchema = z.object({
  throughSeq: z.number().int().min(0),
});
export type MarkServiceChatReadInput = z.infer<typeof markServiceChatReadSchema>;

/**
 * Страница ленты. Курсорная, а не по номеру страницы: лента только растёт, и смещение съезжало бы
 * на каждую пришедшую реплику. `beforeSeq` — подгрузка вверх (история), `afterSeq` —
 * инкрементальный опрос открытого окна; без обоих отдаются последние `limit` реплик.
 *
 * Границы `seq` начинаются с единицы (`CHECK seq > 0`), поэтому `min(1)`: ноль здесь означал бы
 * не «с начала», а ошибку клиента, и молча принятый он вернул бы пустую страницу вместо истории.
 */
export const serviceChatPageQuerySchema = z.object({
  beforeSeq: z.coerce.number().int().min(1).optional(),
  afterSeq: z.coerce.number().int().min(1).optional(),
  // Потолок — стоимость опроса: ленту спрашивает каждое открытое окно, и страница в тысячу реплик
  // умножалась бы на число клиентов. Умолчание — те же 50, что показывает окно при открытии.
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ServiceChatPageQuery = z.infer<typeof serviceChatPageQuerySchema>;

/**
 * Реплика в ленте. Автор пустой только у перенесённых (§3.9) — там его действительно не
 * восстановить; `authorName` при этом непустой всегда: имя показывает лента, а «—» вместо имени
 * решает портал, а не сервер.
 */
export interface ServiceChatMessageDto {
  id: string;
  /** Номер внутри заявки: им идёт пагинация, по нему же считается непрочитанное (§3.4). */
  seq: number;
  authorId: string | null;
  authorName: string;
  origin: ServiceChatOrigin;
  body: string;
  /** У перенесённых — приблизительное (§3.9), и портал обязан пометить это словами. */
  createdAt: string;
  addressees: {
    sides: ServiceChatSide[];
    /** Поимённые адресаты с именами: ярлык в ленте рисуется без второго запроса за учётками. */
    users: { id: string; fullName: string }[];
  };
}

/**
 * Страница ленты. `lastSeq` и `readThroughSeq` приходят вместе с содержимым, а не отдельной
 * ручкой: полосу «Новые» рисуют по ним же, и запрошенные порознь они разъехались бы ровно на те
 * реплики, что пришли между двумя запросами.
 */
export interface ServiceChatPageDto {
  items: ServiceChatMessageDto[];
  /** Есть ли что подгружать дальше в запрошенную сторону. */
  hasMore: boolean;
  lastSeq: number;
  readThroughSeq: number;
}

/**
 * Whom a letter for a message with these addressees will reach — the line under «Кому» (ADR 0159
 * decision 9, ADR 0227 E9).
 *
 * The portal calls this contract function with the server's answers and composes nothing itself:
 * a second rule next to the first would diverge silently — the very trouble the side rules live
 * in contracts to avoid.
 *
 * Two independent deliveries (D5): the service/contractor letters (`mailEnabled`) and the
 * requester letter (`requesterMailEnabled`). The author is never named to himself: his own message
 * never mails him (E3), and `participantSides` contains `customer` for the author only
 * (`participantSidesOf` passes `wideCustomer = false`), so the viewer-is-author fact is already in
 * the summary and needs no second rule here.
 *
 * Three answers, each true in its own case: who gets a letter; that the addressees exist but their
 * letters are switched off right now; that the message is addressed only to portal readers.
 */
export function chatMailNotice(
  plan: ChatMailPlan,
  chat: Pick<
    ServiceRequestChatSummaryDto,
    'mailEnabled' | 'requesterMailEnabled' | 'participantSides'
  >,
): string {
  const viewerIsAuthor = chat.participantSides.includes('customer');
  const service: string[] = [];
  if (plan.targets.includes('service')) service.push('сервисный центр');
  if (plan.targets.includes('office')) service.push('служба оргтехники');
  if (plan.userIds.length > 0) service.push('названные исполнители');
  const requester = plan.targets.includes('requester') && !viewerIsAuthor ? ['заявитель'] : [];
  const parts = [
    ...(chat.mailEnabled ? service : []),
    ...(chat.requesterMailEnabled ? requester : []),
  ];
  if (parts.length > 0) return `Получат письмом: ${parts.join(', ')}.`;
  if (service.length + requester.length > 0) {
    return 'Письма по этому сообщению сейчас не отправляются — его увидят только в портале.';
  }
  return 'Письма не будет: сообщение адресовано только тем, кто читает его в портале.';
}

/**
 * Блок `chat` карточки и строки списка — всё, что порталу нужно знать о переписке, не открывая её.
 * Считает сервер функциями выше; портал правил сторон не воспроизводит (§3.2).
 */
export interface ServiceRequestChatSummaryDto {
  /** Писать сейчас: участник и заявка не закрыта (`canWriteChat`). */
  canWrite: boolean;
  participantSides: ServiceChatSide[];
  total: number;
  /** Адресованные мне непрочитанные — яркая метка со счётом; её видят все, кому видна заявка. */
  unreadMine: number;
  /**
   * Есть ли чужие непрочитанные — блёклая точка без счёта, и только участнику. Булево, а не число,
   * намеренно: точка не считает, а число потребовало бы честного `COUNT` там, где ответ «да» даёт
   * первое же совпадение.
   */
  unreadOthers: boolean;
  lastSeq: number;
  readThroughSeq: number;
  /**
   * Уходят ли вообще письма по репликам этой заявки: событие включено рубильником, почта включена
   * и канал настроен. Считает сервер — портал этих трёх вещей не знает и знать не должен.
   *
   * Без него подпись «получат письмом: сервисный центр» врала бы каждому: выкат заводит событие
   * ВЫКЛЮЧЕННЫМ, и до включения писем нет ни одного.
   */
  mailEnabled: boolean;
  /**
   * Whether the request author gets letters for messages addressed «Заявителю» / «Всем
   * участникам» (ADR 0227, E9): the requester comment switch is on, mail is enabled and the
   * `repair` channel and the portal's reply address are configured. Additive and independent of
   * `mailEnabled` (D5): either can be off while the other goes. An old bundle ignores the field and
   * shows its old line for the deploy window.
   */
  requesterMailEnabled: boolean;
}
